/**
 * SecureEmbed Tier 2 Dashboard API Service
 *
 * API client for communicating with backend services
 * Task 1.3: Added settingsApi for per-user settings
 */

import axios, { AxiosInstance, AxiosError } from 'axios';
import { STORAGE_KEYS, API_CONFIG } from '../constants/config';
import { performLogout, getIsLoggingOut } from '@secureembed/shared-crm';
import { captureObservation, assertObservation, isScopeCancellation, requireConcreteEnvironment, assertScopedMutation } from '../scope/runtime';
import { isObservationRequest } from '../scope/requestIntent';

// API Configuration - reads from runtime config.js
const getApiBaseUrl = (): string => {
  // First try runtime config (set by Terraform in config.js)
  const config = (window as any).SECUREEMBED_CONFIG;
  if (config?.API_ENDPOINT) {
    return config.API_ENDPOINT + '/api';
  }
  if (config?.API_URL) {
    return config.API_URL + '/api';
  }
  // C3: Fallback to env variable - no hardcoded API Gateway URL
  const url = import.meta.env.VITE_API_BASE_URL || '';
  if (!url) {
    console.error('API endpoint not configured');
  }
  return url;
};

const API_TIMEOUT = API_CONFIG.TIMEOUT_MS;

// Create axios instance
const apiClient: AxiosInstance = axios.create({
  timeout: API_TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
});

/** Portal workspace reads share the authenticated dashboard client. */
export const portalsApi = {
  getEnvironments: async <T>(portalId: string): Promise<T> => {
    const response = await apiClient.get<T>(`/portals/${encodeURIComponent(portalId)}/environments`);
    return response.data;
  },
};

export type EnvironmentRegistryState = 'known' | 'empty' | 'unknown' | 'unreachable';

export interface EnvironmentFleetRow {
  environmentId: string;
  name: string;
  account: string | null;
  region: string;
  lifecycle: 'ready' | 'planned' | 'unknown';
  state: EnvironmentRegistryState;
}

export interface EnvironmentFleetResponse {
  data: EnvironmentFleetRow[] | null;
  status: EnvironmentRegistryState;
  source: string;
  observedAt?: string;
  message?: string;
}

/** Story #5483 (Launch W6) Stream 2/3 — E04-E11 per-environment projections.
 * Every response carries the same read_response envelope (status/source/
 * observedAt) as EnvironmentFleetResponse -- see Shared Contracts tab. */
export type ProjectionState = 'known' | 'empty' | 'unknown' | 'unreachable' | 'unavailable';

export interface EnvironmentOverviewAttribute {
  attribute: string;
  value: string | null;
  source: string;
  state: ProjectionState;
  detail?: string;
}
export interface EnvironmentOverviewResponse {
  data: EnvironmentOverviewAttribute[] | null;
  status: EnvironmentRegistryState;
  source: string;
  observedAt?: string;
  message?: string;
}

export interface EnvironmentServiceRow {
  serviceType: 'S3' | 'DynamoDB' | 'OpenSearch' | 'CloudFront';
  name: string;
  health: ProjectionState;
  configProvenance: string;
}
export interface EnvironmentServicesResponse {
  data: EnvironmentServiceRow[] | null;
  status: EnvironmentRegistryState;
  source: string;
  observedAt?: string;
  message?: string;
}

export interface EnvironmentServiceDetailResponse {
  data: Record<string, unknown> | null;
  status: EnvironmentRegistryState;
  source: string;
  observedAt?: string;
  message?: string;
}

export interface EnvironmentDataStoreRow {
  storeType: 'S3' | 'DynamoDB';
  name: string;
  classification: 'unknown';
  freshness: 'unknown';
  state: ProjectionState;
}
export interface EnvironmentDataResponse {
  data: EnvironmentDataStoreRow[] | null;
  status: EnvironmentRegistryState;
  source: string;
  observedAt?: string;
  message?: string;
}

/** Story #5483 (Launch W6) Stream 1.4 -- E03 Plan environment. */
export interface EnvironmentPlanDraft {
  planId: string;
  version: number;
  tier: 'DEV' | 'TEST' | 'UAT' | 'PROD' | 'DR';
  owner: string;
  account: string;
  region: string;
  template: string;
  portals: string[];
  savedAt: string;
}
export interface SavePlanDraftInput {
  planId?: string;
  tier: string;
  owner: string;
  account: string;
  region: string;
  template: string;
  portals: string[];
}
export interface SavePlanDraftResponse {
  draft?: EnvironmentPlanDraft;
  status: 'saved';
}
export interface SavePlanDraftError {
  error: string;
  field?: string;
}
export interface GetPlanDraftResponse {
  data: EnvironmentPlanDraft | null;
  status: EnvironmentRegistryState;
  source: string;
  observedAt?: string;
  message?: string;
}

/** Story #5483 (Launch W6) Stream 4.2/4.3 -- E12 lifecycle transitions. */
export interface LifecyclePreflightResponse {
  planHash: string;
  environmentId: string;
  transition: 'suspend' | 'wake';
  expiresAt: number;
  preview: string;
}
export interface LifecycleExecuteResponse {
  state: 'submitted' | 'denied' | 'unknown_hold';
  job_id?: string;
  reserved?: boolean;
  alreadyReserved?: boolean;
  command: {
    commandId: string;
    environmentId: string;
    transition: 'suspend' | 'wake';
    planHash: string;
    status: string;
    reservedAt?: number;
    state?: string;
    jobId?: string;
  } | null;
}
export interface LifecycleCommandResponse {
  command: LifecycleExecuteResponse['command'];
  status: EnvironmentRegistryState;
}

// Task #5527 (Story #5483 Stream 11) -- G5 UAT stand-up preview: a REAL
// terraform plan (never applied) captured once against the Dev spoke for a
// representative UAT-tier resource set, plus the written stand-up
// checklist. applied is always false -- there is no code path in this
// screen that can ever run terraform apply.
export interface UatStandupChecklistItem {
  pack: string;
  items: string[];
}
export interface UatStandupChecklist {
  gate: string;
  gate_decision: string;
  gate_default_scope: string;
  source: string;
  note: string;
  packs: UatStandupChecklistItem[];
}
export interface UatPlanResourceChange {
  address: string;
  actions: string[];
  type: string;
}
export interface UatPreviewData {
  terraformPlanText: string;
  planSummary: { resource_changes: UatPlanResourceChange[]; plan_summary: string };
  standupChecklist: UatStandupChecklist;
  previewConfigPath: string;
  targetAccount: string;
  applied: false;
}
export interface UatPreviewResponse {
  data: UatPreviewData | null;
  status: EnvironmentRegistryState;
  source: string;
  observedAt?: string;
  message?: string;
}

export const environmentsApi = {
  getFleet: async (): Promise<EnvironmentFleetResponse> => {
    const response = await apiClient.get<EnvironmentFleetResponse>('/environments');
    return response.data;
  },
  getOverview: async (environmentId: string): Promise<EnvironmentOverviewResponse> => {
    const response = await apiClient.get<EnvironmentOverviewResponse>('/environments', {
      params: { action: 'get-overview', environmentId },
    });
    return response.data;
  },
  getServices: async (environmentId: string): Promise<EnvironmentServicesResponse> => {
    const response = await apiClient.get<EnvironmentServicesResponse>('/environments', {
      params: { action: 'get-services', environmentId },
    });
    return response.data;
  },
  getServiceDetail: async (environmentId: string, serviceType: string, name: string): Promise<EnvironmentServiceDetailResponse> => {
    const response = await apiClient.get<EnvironmentServiceDetailResponse>('/environments', {
      params: { action: 'get-service-detail', environmentId, serviceType, name },
    });
    return response.data;
  },
  getData: async (environmentId: string): Promise<EnvironmentDataResponse> => {
    const response = await apiClient.get<EnvironmentDataResponse>('/environments', {
      params: { action: 'get-data', environmentId },
    });
    return response.data;
  },
  // E03 -- Save persists a draft plan row only; no provider call.
  savePlanDraft: async (input: SavePlanDraftInput): Promise<SavePlanDraftResponse> => {
    const response = await apiClient.post<SavePlanDraftResponse>('/environments', { action: 'save-plan-draft', ...input });
    return response.data;
  },
  getPlanDraft: async (planId: string): Promise<GetPlanDraftResponse> => {
    const response = await apiClient.get<GetPlanDraftResponse>('/environments', {
      params: { action: 'get-plan-draft', planId },
    });
    return response.data;
  },
  // E12 -- lifecycle transition preflight/execute/record-outcome/receipt.
  lifecyclePreflight: async (environmentId: string, transition: 'suspend' | 'wake'): Promise<LifecyclePreflightResponse> => {
    const response = await apiClient.post<LifecyclePreflightResponse>('/environments', { action: 'lifecycle-preflight', environmentId, transition });
    return response.data;
  },
  lifecycleExecute: async (environmentId: string, transition: 'suspend' | 'wake', planHash: string, idempotencyKey: string): Promise<LifecycleExecuteResponse> => {
    const response = await apiClient.post<LifecycleExecuteResponse>('/environments', { action: 'lifecycle-execute', environmentId, transition, planHash, idempotencyKey });
    return response.data;
  },
  lifecycleRecordOutcome: async (commandId: string, jobId: string): Promise<{ status: string }> => {
    const response = await apiClient.post('/environments', { action: 'lifecycle-record-outcome', commandId, jobId });
    return response.data;
  },
  lifecycleGetCommand: async (commandId: string): Promise<LifecycleCommandResponse> => {
    const response = await apiClient.get<LifecycleCommandResponse>('/environments', {
      params: { action: 'lifecycle-get-command', commandId },
    });
    return response.data;
  },
  // Task #5527 -- G5 UAT stand-up preview (read-only; never provisions).
  getUatPreview: async (): Promise<UatPreviewResponse> => {
    const response = await apiClient.get<UatPreviewResponse>('/environments', {
      params: { action: 'get-uat-preview' },
    });
    return response.data;
  },
};

// Story #5483 (Launch W6) Stream 3 -- E09 Environment Operations projection.
// Reuses the SAME /operations Lambda + ENV_JOBS_TABLE partitions that back
// opsWorkApi above (Stream 1, O01-O06) -- never a competing writer/reader,
// just an environment-filtered view of the same canonical jobs/holds data.
export interface EnvironmentOperationsResponse {
  reachable: boolean;
  environment: string;
  jobs: Array<Record<string, unknown>>;
  holds: Array<Record<string, unknown>>;
  alertsUnknownReason: string;
}
export const environmentOperationsApi = {
  getEnvironmentOperations: async (environmentId: string): Promise<EnvironmentOperationsResponse> => {
    const response = await apiClient.get<EnvironmentOperationsResponse>('/operations', {
      params: { action: 'get-environment-operations', environmentId },
    });
    return response.data;
  },
};

// Story #5483 (Launch W6) Stream 7 -- R01-R04 releases (catalogue, create,
// summary, ChangeSet). Every read carries the same read_response envelope
// (status/source/observedAt/schemaVersion/correlationId) as the environments
// projections above; every write is a WriteRequest/WriteResponse pair per
// the Shared Contracts tab (see lambda/dashboard/releases/handler.py).
export type ReleaseStage = 'draft' | 'frozen' | 'migrated_evidence' | string;

export interface ReleaseTarget {
  environmentId: string;
  accountId: string;
  region: string;
  portalId: string;
}

export interface ReleaseSource {
  ref?: string;
  kind: 'commit' | 'pull_request' | 'promotion_package';
  commit_sha?: string | null;
  pr_number?: number | null;
  pr_url?: string | null;
  resolved_at?: string;
}

export interface ReleaseArtifact {
  function_name: string;
  resolved_function_name?: string;
  code_sha256: string;
  resolved_at?: string;
}

export interface ReleaseChangeSetItem {
  type: string;
  name: string;
  sourceVersion?: string | null;
  targetVersion?: string | null;
  target?: string;
  compatibility?: string;
}

export interface ReleaseRecord {
  release_id: string;
  title: string;
  portal_id?: string;
  environment_id?: string;
  target?: ReleaseTarget;
  target_set?: unknown[];
  risk_tier?: 'low' | 'medium' | 'high' | null;
  change_reason?: string;
  stage: ReleaseStage;
  owner?: string;
  created_by?: string;
  created_at?: string;
  updated_at?: string;
  source?: ReleaseSource | null;
  artifact?: ReleaseArtifact | null;
  changeset?: ReleaseChangeSetItem[];
  plan_hash?: string;
  frozen?: boolean;
  frozen_at?: string | null;
  frozen_by?: string | null;
  origin?: 'promotion_package';
}

export interface ReleasesListResponse {
  data: ReleaseRecord[] | null;
  status: EnvironmentRegistryState | 'stale' | 'unauthorised';
  source: string;
  observedAt?: string;
  schemaVersion?: string;
  correlationId?: string;
  message?: string;
}

export interface ReleaseGetResponse {
  data: ReleaseRecord | null;
  status: EnvironmentRegistryState | 'stale' | 'unauthorised';
  source: string;
  observedAt?: string;
  message?: string;
}

export interface CreateReleasePayload {
  title: string;
  portalId: string;
  environmentId: string;
  sourceRef: string;
  artifactFunctionName: string;
  changeset?: ReleaseChangeSetItem[];
  targetSet?: unknown[];
}

export interface ReleaseWriteResponse {
  commandId: string;
  target: ReleaseTarget;
  planHash: string;
  stage: string;
  resumableRoute: string;
  verified: boolean;
  release: ReleaseRecord;
}

export interface ReleaseWriteError {
  error: string;
  reason?: string;
  current_version?: string;
  release?: ReleaseRecord;
}

export interface ReleaseListFilters {
  portal?: string;
  environment?: string;
  risk?: string;
  stage?: string;
  owner?: string;
  since?: string;
}

// R05 -- gates (Story #5483 Stream 8, Task #5515). Every category re-
// evaluates from stored evidence + "now" on each read -- never a cached
// verdict. 'unknown' means no evidence was ever recorded; 'fail' with
// stale=true means the evidence exists but is too old to trust.
export type GateState = 'pass' | 'fail' | 'unknown';
export type GateCategory =
  | 'tests' | 'security' | 'policy' | 'drift'
  | 'compatibility' | 'cost' | 'recovery' | 'documentation';

export interface GateCategoryResult {
  state: GateState;
  reason: string | null;
  stale: boolean;
  recorded_at?: string;
  source?: string;
}

export interface ReleaseGatesResult {
  release_id: string;
  plan_hash?: string;
  overall: GateState;
  categories: Record<GateCategory, GateCategoryResult>;
  evaluated_at: string;
}

export interface ReleaseGatesResponse {
  data: ReleaseGatesResult | null;
  status: EnvironmentRegistryState | 'stale' | 'unauthorised';
  source: string;
  observedAt?: string;
  message?: string;
}

// R06 -- approvals (Story #5483 Stream 8, Task #5516). decision #5284's
// single-operator rule: request and approve are two SEPARATE calls, each
// bound to the release's plan_hash at the time of the call.
export type ApprovalStatus = 'pending' | 'approved' | 'rejected' | 'revoked';

export interface ReleaseApprovalRequest {
  request_id: string;
  requested_by: string;
  requested_at: string;
  expires_at: number;
  plan_hash?: string;
  status: ApprovalStatus;
  resolved_at?: string;
  reason?: string;
}

export interface ReleaseApprovalsResponse {
  data: ReleaseApprovalRequest[] | null;
  status: EnvironmentRegistryState | 'stale' | 'unauthorised';
  source: string;
  observedAt?: string;
  message?: string;
}

export interface ReleaseApprovalReceipt {
  request_id: string;
  control_id: string;
  actor_subject: string;
  payload_digest: string;
  created_at: number;
  approved_at: number;
  status: 'approved';
  verification: { verified: boolean; reason?: string; result?: unknown };
}

export interface ReleaseApprovalError {
  error: string;
  reason?: string;
  sod_reason?: string;
}

// R07/R08 -- rollout + independent verification (Story #5483 Stream 9,
// Tasks #5517/#5518). One job record per release; verified can only ever
// be set by a DIFFERENT actor than started_by (verify_rollout on the
// backend refuses otherwise).
export type RolloutStatus = 'running' | 'stopped' | 'succeeded' | 'failed';

export interface ReleaseRolloutJob {
  job_id: string;
  status: RolloutStatus;
  target_environment_id: string;
  idempotency_key: string;
  started_by: string;
  started_at: string;
  updated_at: string;
  stop_requested: boolean;
  stop_reason?: string | null;
  stopped_by?: string;
  verified: boolean;
  verified_by?: string | null;
  verified_at?: string | null;
  verification_notes?: string | null;
}

export interface ReleaseRolloutResponse {
  data: ReleaseRolloutJob | null;
  status: EnvironmentRegistryState | 'stale' | 'unauthorised';
  source: string;
  observedAt?: string;
  message?: string;
}

// R10 -- history (Task #5520). coverage is ALWAYS 'partial' -- there is no
// event-sourced audit log behind this handler, only derived timestamps.
export interface ReleaseHistoryEvent {
  event_type: string;
  actor?: string | null;
  at: string;
  target_release_id?: string;
}

export interface ReleaseHistoryResult {
  release_id: string;
  events: ReleaseHistoryEvent[];
  coverage: 'partial';
  coverage_reason: string;
}

export interface ReleaseHistoryResponse {
  data: ReleaseHistoryResult | null;
  status: EnvironmentRegistryState | 'stale' | 'unauthorised';
  source: string;
  observedAt?: string;
  message?: string;
}

function genIdempotencyKey(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `local-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export const releasesApi = {
  listReleases: async (filters?: ReleaseListFilters): Promise<ReleasesListResponse> => {
    const response = await apiClient.get<ReleasesListResponse>('/releases', { params: filters });
    return response.data;
  },
  getRelease: async (releaseId: string): Promise<ReleaseGetResponse> => {
    const response = await apiClient.get<ReleaseGetResponse>('/releases', {
      params: { action: 'get', releaseId },
    });
    return response.data;
  },
  createRelease: async (
    payload: CreateReleasePayload,
    changeReason: string,
    riskTier: 'low' | 'medium' | 'high',
  ): Promise<ReleaseWriteResponse> => {
    const response = await apiClient.post<ReleaseWriteResponse>('/releases', {
      commandType: 'create_release',
      schemaVersion: '1',
      idempotencyKey: genIdempotencyKey(),
      changeReason,
      riskTier,
      payload,
    });
    return response.data;
  },
  updateDraft: async (
    releaseId: string,
    fields: Partial<Pick<CreateReleasePayload, 'title' | 'changeset' | 'targetSet'>>,
  ): Promise<ReleaseWriteResponse> => {
    const response = await apiClient.post<ReleaseWriteResponse>('/releases', {
      commandType: 'update_draft',
      payload: { releaseId, ...fields },
    });
    return response.data;
  },
  freezeChangeSet: async (releaseId: string): Promise<ReleaseWriteResponse> => {
    const response = await apiClient.post<ReleaseWriteResponse>('/releases', {
      commandType: 'freeze_changeset',
      payload: { releaseId },
    });
    return response.data;
  },
  getArtifactCatalogue: async <T = Record<string, unknown>>(kind: ArtifactCatalogueKind): Promise<ArtifactCatalogueResponse<T>> => {
    const response = await apiClient.get<ArtifactCatalogueResponse<T>>('/releases', {
      params: { action: 'artifacts', kind },
    });
    return response.data;
  },
  // R05 -- gates (Task #5515)
  getGates: async (releaseId: string): Promise<ReleaseGatesResponse> => {
    const response = await apiClient.get<ReleaseGatesResponse>('/releases', {
      params: { action: 'gates', releaseId },
    });
    return response.data;
  },
  recordGateEvidence: async (
    releaseId: string,
    category: GateCategory,
    status: 'pass' | 'fail',
    detail?: string,
  ): Promise<{ release_id: string; gates: ReleaseGatesResult }> => {
    const response = await apiClient.post<{ release_id: string; gates: ReleaseGatesResult }>('/releases', {
      commandType: 'record_gate_evidence',
      payload: { releaseId, category, status, detail },
    });
    return response.data;
  },
  // R06 -- approvals (Task #5516)
  getApprovals: async (releaseId: string): Promise<ReleaseApprovalsResponse> => {
    const response = await apiClient.get<ReleaseApprovalsResponse>('/releases', {
      params: { action: 'approvals', releaseId },
    });
    return response.data;
  },
  requestApproval: async (releaseId: string): Promise<{ request_id: string; payload_digest: string; expires_at: number; release_id: string }> => {
    const response = await apiClient.post('/releases', {
      commandType: 'request_approval',
      payload: { releaseId },
    });
    return response.data;
  },
  approveRelease: async (releaseId: string, requestId: string): Promise<ReleaseApprovalReceipt> => {
    const response = await apiClient.post<ReleaseApprovalReceipt>('/releases', {
      commandType: 'approve_release',
      payload: { releaseId, requestId },
    });
    return response.data;
  },
  revokeApproval: async (releaseId: string, requestId: string): Promise<{ status: string }> => {
    const response = await apiClient.post('/releases', {
      commandType: 'revoke_approval',
      payload: { releaseId, requestId },
    });
    return response.data;
  },
  // R07 -- rollout (Task #5517)
  getRollout: async (releaseId: string): Promise<ReleaseRolloutResponse> => {
    const response = await apiClient.get<ReleaseRolloutResponse>('/releases', {
      params: { action: 'rollout', releaseId },
    });
    return response.data;
  },
  startRollout: async (releaseId: string): Promise<{ release_id: string; rollout: ReleaseRolloutJob }> => {
    const response = await apiClient.post('/releases', {
      commandType: 'start_rollout',
      idempotencyKey: genIdempotencyKey(),
      payload: { releaseId },
    });
    return response.data;
  },
  stopRollout: async (releaseId: string, reason: string): Promise<{ release_id: string; rollout: ReleaseRolloutJob }> => {
    const response = await apiClient.post('/releases', {
      commandType: 'stop_rollout',
      payload: { releaseId, reason },
    });
    return response.data;
  },
  // R08 -- independent verification (Task #5518)
  getVerification: async (releaseId: string): Promise<ReleaseRolloutResponse> => {
    const response = await apiClient.get<ReleaseRolloutResponse>('/releases', {
      params: { action: 'verification', releaseId },
    });
    return response.data;
  },
  verifyRollout: async (releaseId: string, notes?: string): Promise<{ release_id: string; rollout: ReleaseRolloutJob }> => {
    const response = await apiClient.post('/releases', {
      commandType: 'verify_rollout',
      payload: { releaseId, notes },
    });
    return response.data;
  },
  // R09 -- rollback (Task #5519). Always creates a NEW release record.
  createRollback: async (releaseId: string, changeReason: string): Promise<ReleaseWriteResponse> => {
    const response = await apiClient.post<ReleaseWriteResponse>('/releases', {
      commandType: 'create_rollback',
      idempotencyKey: genIdempotencyKey(),
      changeReason,
      payload: { releaseId },
    });
    return response.data;
  },
  // R10 -- history (Task #5520)
  getHistory: async (releaseId: string): Promise<ReleaseHistoryResponse> => {
    const response = await apiClient.get<ReleaseHistoryResponse>('/releases', {
      params: { action: 'history', releaseId },
    });
    return response.data;
  },
};

// Story #5483 (Launch W6) Stream 10 -- R11-R14 read-only artifact catalogues
// (Configuration baselines, Data snapshots, Run profiles, Advanced
// provenance). Every kind reshapes the SAME real release records R01-R04
// already write (lambda/dashboard/releases/handler.py list_artifact_catalogue)
// -- there is no separate table or Lambda behind these four screens.
export type ArtifactCatalogueKind = 'config' | 'data' | 'profiles' | 'provenance';

export interface ConfigBaselineRow {
  baseline_id: string;
  artifact_type?: string | null;
  artifact_name?: string | null;
  portal_id?: string | null;
  environment_id?: string | null;
  digest?: string | null;
  effective_at?: string | null;
  state?: string | null;
  release_id: string;
}

export interface DataSnapshotRow {
  snapshot_id: string;
  dataset_scope?: string | null;
  digest?: string | null;
  state?: string | null;
  release_id: string;
}

export interface RunProfileRow {
  profile_id: string;
  risk_tier?: string | null;
  environment_id?: string | null;
  portal_ids: string[];
  release_count: number;
  last_release_id?: string | null;
  last_verified_at?: string;
}

export interface ProvenanceRow {
  artifact_name?: string | null;
  source_ref?: string | null;
  digest?: string | null;
  sbom_toolchain?: string | null;
  state: 'verified' | 'incomplete' | 'mapped_read_only' | string;
  release_id: string;
}

export interface ArtifactCatalogueResponse<T = Record<string, unknown>> {
  data: T[] | null;
  status: EnvironmentRegistryState | 'stale' | 'unauthorised';
  source: string;
  observedAt?: string;
  schemaVersion?: string;
  correlationId?: string;
  message?: string;
}

// Scope tickets bind read completion to the identity/scope that issued it.
const observationRequests = new WeakMap<object, { ticket: ReturnType<typeof captureObservation>; cleanup: () => void }>();
function finishObservation(config: object | undefined): void {
  if (!config) return;
  const request = observationRequests.get(config);
  if (request) { request.cleanup(); observationRequests.delete(config); assertObservation(request.ticket); }
}
// Request interceptor for auth and dynamic base URL
apiClient.interceptors.request.use(
  (config) => {
    const observation = isObservationRequest(config.method || 'get', config.url || '', config.data);
    if (!observation && !['head', 'options'].includes((config.method || 'get').toLowerCase())) assertScopedMutation(config.url || '');
    if (observation && config.params?.action !== 'scope-options') {
      const ticket = captureObservation(); const controller = new AbortController();
      const abort = () => controller.abort(); const previous = config.signal;
      ticket.signal.addEventListener('abort', abort, { once: true });
      previous?.addEventListener?.('abort', abort, { once: true });
      if (ticket.signal.aborted || previous?.aborted) abort();
      config.signal = controller.signal;
      observationRequests.set(config, { ticket, cleanup: () => { ticket.signal.removeEventListener('abort', abort); previous?.removeEventListener?.('abort', abort); } });
    }
    // Set baseURL dynamically on each request (config.js may load after module init)
    config.baseURL = getApiBaseUrl();

    const token = localStorage.getItem(STORAGE_KEYS.ID_TOKEN) || localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // The hub's API Gateway Cognito authorizer only accepts the ID token
    // above, but My Account > Security/Sessions calls Cognito's own
    // GetUser/ListDevices/etc, which only ever accept the real AccessToken
    // -- a different JWT (Task #5558/Bug #5615, confirmed live). Send it
    // separately, and ONLY on the two routes that need it: every other API
    // Gateway resource's CORS preflight (Access-Control-Allow-Headers) does
    // not list this header, so sending it on every request would fail
    // preflight everywhere else (found live via a real CORS-checking test
    // server, SearchCommand.test.tsx, not assumed).
    if (config.url?.includes('/my-account/security') || config.url?.includes('/my-account/sessions')) {
      const accessToken = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
      if (accessToken) {
        config.headers['X-Cognito-Access-Token'] = accessToken;
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => { finishObservation(response.config); return response; },
  (error: AxiosError) => {
    finishObservation(error.config);
    if (!getIsLoggingOut() && error.response?.status === 401) {
      performLogout({
        storageKeys: [
          STORAGE_KEYS.AUTH_TOKEN,
          STORAGE_KEYS.ID_TOKEN,
          STORAGE_KEYS.REFRESH_TOKEN,
          STORAGE_KEYS.USER,
        ],
      });
    }
    return Promise.reject(error);
  }
);

// Types
export interface AnalyticsResponse {
  success: boolean;
  data: {
    tenant_id: string;
    time_range: string;
    period: {
      start: string;
      end: string;
    };
    summary?: {
      total_insights: number;
      by_type: Record<string, number>;
      unique_source_embeddings: number;
      daily_breakdown: Record<string, Record<string, number>>;
    };
    trends?: {
      granularity: string;
      data_points: number;
      trends: Array<{
        timestamp: string;
        total: number;
        by_type: Record<string, number>;
      }>;
    };
    performance?: {
      insights_processed: number;
      algorithms_used: Record<string, number>;
      processing_time?: {
        avg_ms: number;
        min_ms: number;
        max_ms: number;
      };
      confidence?: {
        avg: number;
        min: number;
        max: number;
      };
    };
  };
}

export interface InsightResponse {
  success: boolean;
  data: {
    insights: Array<{
      insight_id: string;
      insight_type: string;
      created_at: string;
      source_embedding_id?: string;
      provenance?: {
        algorithm?: string;
        confidence?: number;
      };
    }>;
    count: number;
    next_token?: string;
  };
}

export interface ExportResponse {
  success: boolean;
  data: {
    export_id?: string;
    download_url?: string;
    record_count: number;
    format: string;
    expires_in_seconds?: number;
    content?: string;
    export_timestamp?: string;
    message?: string;
  };
}

export interface ProvenanceResponse {
  success: boolean;
  data: {
    source_embedding_id: string;
    derived_insights_count: number;
    insights: Array<{
      insight_id: string;
      insight_type: string;
      created_at: string;
      provenance: Record<string, any>;
    }>;
  };
}

// Analytics API
export const analyticsApi = {
  /**
   * Get analytics data for dashboard
   */
  getAnalytics: async (
    metric: 'summary' | 'trends' | 'performance' | 'all' = 'all',
    timeRange: string = '7d',
    granularity: string = 'day'
  ): Promise<AnalyticsResponse> => {
    const response = await apiClient.get<AnalyticsResponse>('/analytics', {
      params: { metric, time_range: timeRange, granularity },
    });
    return response.data;
  },
};

// Insights API
export const insightsApi = {
  /**
   * List insights with optional filters
   */
  listInsights: async (params?: {
    insight_type?: string;
    limit?: number;
    next_token?: string;
    start_date?: string;
    end_date?: string;
  }): Promise<InsightResponse> => {
    const response = await apiClient.get<InsightResponse>('/insights', { params });
    return response.data;
  },

  /**
   * Get a specific insight by ID
   */
  getInsight: async (insightId: string): Promise<any> => {
    const response = await apiClient.get(`/insights/${insightId}`);
    return response.data;
  },

  /**
   * Create a new insight
   */
  createInsight: async (data: {
    insight_type: string;
    insight_data: Record<string, any>;
    source_embedding_id?: string;
    source_embeddings?: string[];
    provenance_metadata?: Record<string, any>;
  }): Promise<any> => {
    const response = await apiClient.post('/insights', data);
    return response.data;
  },
};

// Provenance API
export const provenanceApi = {
  /**
   * Get provenance chain for an embedding
   */
  getProvenanceChain: async (
    embeddingId: string,
    depth: number = 10,
    includeRaw: boolean = false
  ): Promise<ProvenanceResponse> => {
    const response = await apiClient.get<ProvenanceResponse>(`/provenance/${embeddingId}`, {
      params: { depth, include_raw: includeRaw },
    });
    return response.data;
  },
};

// Export API
export const exportApi = {
  /**
   * Generate data export
   */
  createExport: async (params: {
    format: 'json' | 'csv';
    start_date?: string;
    end_date?: string;
    insight_type?: string;
    max_records?: number;
  }): Promise<ExportResponse> => {
    const response = await apiClient.post<ExportResponse>('/export', params);
    return response.data;
  },
};

// Search API (Tier 1)
export const searchApi = {
  /**
   * Search for similar embeddings
   */
  searchSimilar: async (params: {
    query: string;
    top_k?: number;
    filters?: Record<string, any>;
  }): Promise<any> => {
    const response = await apiClient.post('/search/similar', params);
    return response.data;
  },

  /**
   * Get metadata for embeddings
   */
  getMetadata: async (params: {
    embedding_ids?: string[];
    region?: string;
    date_from?: string;
    date_to?: string;
  }): Promise<any> => {
    const response = await apiClient.get('/metadata', { params });
    return response.data;
  },
};

// Aggregate API
export const aggregateApi = {
  /**
   * Aggregate insights across embeddings
   */
  aggregate: async (params: {
    source_embeddings: string[];
    aggregation_type: 'statistical' | 'count_by_type' | 'timeline' | 'merge';
    insight_type_filter?: string;
    aggregation_params?: Record<string, any>;
  }): Promise<any> => {
    const response = await apiClient.post('/aggregate', params);
    return response.data;
  },
};

// Backup API
export const backupApi = {
  listBackups: async (params?: Record<string, any>): Promise<any> => {
    const response = await apiClient.post('/backup', { action: 'list-backups', ...params });
    return response.data;
  },
  listVaults: async (): Promise<any> => {
    const response = await apiClient.post('/backup', { action: 'list-vaults' });
    return response.data;
  },
  createBackup: async (params: { resource_arn: string; vault_name?: string; move_to_cold_after_days?: number; delete_after_days?: number }): Promise<any> => {
    const response = await apiClient.post('/backup', { action: 'create-backup', ...params });
    return response.data;
  },
  restoreBackup: async (params: { recovery_point_arn: string; vault_name?: string; metadata?: Record<string, string> }, approvalToken?: string): Promise<any> => {
    const response = await apiClient.post('/backup', { action: 'restore-backup', ...params },
      { headers: approvalToken ? { 'X-Approval-Token': approvalToken } : undefined });
    return response.data;
  },
  getBackupPlan: async (): Promise<any> => {
    const response = await apiClient.post('/backup', { action: 'get-backup-plan' });
    return response.data;
  },
  createBackupPlan: async (params: { plan_name: string; schedule?: string; retention_days?: number; cold_storage_days?: number; vault_name?: string; resource_arns?: string[]; account?: string; env?: string }): Promise<any> => {
    const response = await apiClient.post('/backup', { action: 'create-backup-plan', ...params });
    return response.data;
  },
  // Bug fix (Story #4487 — Bug 2): one-time on-demand path — starts jobs immediately,
  // no stray recurring plan, returns jobs_started for proper UI feedback.
  createBackupOndemand: async (params: { resource_arns: string[]; retention_days?: number; cold_storage_days?: number; vault_name?: string; account?: string; env?: string }): Promise<any> => {
    const response = await apiClient.post('/backup', { action: 'create-backup-ondemand', ...params });
    return response.data;
  },
  estimateCosts: async (params: { storage_gb: number; retention_days: number; cold_storage_days?: number; resource_types?: string[] }): Promise<any> => {
    const response = await apiClient.post('/backup', { action: 'estimate-costs', ...params });
    return response.data;
  },
  listResources: async (params?: Record<string, any>): Promise<any> => {
    const response = await apiClient.post('/backup', { action: 'list-resources', ...params });
    return response.data;
  },
  restoreSelective: async (params: { recovery_point_arns: string[]; vault_name?: string; restore_target?: string }, approvalToken?: string): Promise<any> => {
    const response = await apiClient.post('/backup', { action: 'restore-selective', ...params },
      { headers: approvalToken ? { 'X-Approval-Token': approvalToken } : undefined });
    return response.data;
  },
  listSchedules: async (): Promise<any> => {
    const response = await apiClient.post('/backup', { action: 'list-schedules' });
    return response.data;
  },
  updateSchedule: async (params: { plan_id: string; plan_name?: string; schedule_expression?: string; retention_days?: number; vault_name?: string }): Promise<any> => {
    const response = await apiClient.post('/backup', { action: 'update-schedule', ...params });
    return response.data;
  },
  deleteSchedule: async (params: { plan_id: string }, approvalToken?: string): Promise<any> => {
    const response = await apiClient.post('/backup', { action: 'delete-schedule', ...params },
      { headers: approvalToken ? { 'X-Approval-Token': approvalToken } : undefined });
    return response.data;
  },
  runSchedule: async (params: { plan_id: string }, approvalToken?: string): Promise<any> => {
    const response = await apiClient.post('/backup', { action: 'run-schedule', ...params },
      { headers: approvalToken ? { 'X-Approval-Token': approvalToken } : undefined });
    return response.data;
  },
  // O17 (Story #5650): sign an exported evidence pack's chain hash with the
  // hub's ML-DSA-65 KMS key, so a recipient can verify the pack offline
  // against the returned public key instead of trusting the channel.
  signEvidencePack: async (chainSha256: string): Promise<{
    signed: boolean; chain_sha256: string; algorithm: string; key_arn: string;
    key_spec: string; signature_b64: string; public_key_b64: string; signed_at: string;
  }> => {
    const response = await apiClient.post('/backup', {
      action: 'sign-evidence-pack', chain_sha256: chainSha256,
    });
    // The backup Lambda wraps every action in {success, data}. Siblings here
    // return the envelope and let each caller unwrap it; this one declares a
    // precise return type, so it unwraps here instead -- returning the envelope
    // under that type would have made `sig.signed` undefined at runtime while
    // typechecking clean, and the pack would have silently stayed unsigned.
    // Caught by invoking the real Lambda, not by any test.
    return response.data?.data ?? response.data;
  },
  listBackupContents: async (params?: { recovery_point_arn?: string; vault_name?: string; plan_id?: string }): Promise<any> => {
    const response = await apiClient.post('/backup', { action: 'list-backup-contents', ...params });
    return response.data;
  },
  listJobs: async (params?: { plan_id?: string; status?: string; resource_type?: string }): Promise<any> => {
    const response = await apiClient.post('/backup', { action: 'list-jobs', ...params });
    return response.data;
  },
  // Story #4482: Backup Catalogue + Vaults & DR tabs
  listCatalogue: async (params?: { account?: string; env?: string; tier?: string; kind?: string; search?: string; limit?: number }): Promise<any> => {
    const response = await apiClient.post('/backup', { action: 'list-catalogue', ...params });
    return response.data;
  },
  listVaultsStatus: async (params?: { include_dr?: boolean }): Promise<any> => {
    const response = await apiClient.post('/backup', { action: 'list-vaults-status', ...params });
    return response.data;
  },
  deleteRecoveryPoint: async (params: { vault_name: string; recovery_point_arn: string }, approvalToken?: string): Promise<any> => {
    const response = await apiClient.post('/backup', { action: 'delete-recovery-point', ...params },
      { headers: approvalToken ? { 'X-Approval-Token': approvalToken } : undefined });
    return response.data;
  },
  // O14 (Story #5653): read-only provider preflight for a governed restore
  // plan -- source/scope/destination in, a real impact + interlocks preview
  // out. No approval token: this makes no provider mutation.
  restorePreflight: async (params: {
    recovery_point_arn: string;
    scope: 'whole' | 'selected';
    selected_resource_arns?: string[];
    destination: 'original' | 'alternate';
    vault_name?: string;
  }): Promise<any> => {
    const response = await apiClient.post('/backup', { action: 'restore-preflight', ...params });
    return response.data;
  },
  // O16 (Story #5653): rehearsal record store. Both writers require the
  // decision #5284 approval token (see recoveryApproval.ts), same as
  // restore/schedule-run/delete.
  startRehearsal: async (params: {
    source_recovery_point_arn: string;
    target_description: string;
    success_criteria: string;
    owner: string;
  }, approvalToken?: string): Promise<any> => {
    const response = await apiClient.post('/backup', { action: 'start-rehearsal', ...params },
      { headers: approvalToken ? { 'X-Approval-Token': approvalToken } : undefined });
    return response.data;
  },
  closeRehearsal: async (params: {
    record_id: string;
    measured_rto_minutes: number;
    measured_rpo_minutes: number;
    runbook_accurate: boolean;
    runbook_discrepancies?: string[];
    access_ready: boolean;
    cleanup_verified: boolean;
    cleanup_evidence?: string;
  }, approvalToken?: string): Promise<any> => {
    const response = await apiClient.post('/backup', { action: 'close-rehearsal', ...params },
      { headers: approvalToken ? { 'X-Approval-Token': approvalToken } : undefined });
    return response.data;
  },
  listRehearsals: async (): Promise<any> => {
    const response = await apiClient.post('/backup', { action: 'list-rehearsals' });
    return response.data;
  },
  // O17 (Story #5653): the real evidence store -- written only by
  // closeRehearsal above.
  listEvidence: async (): Promise<any> => {
    const response = await apiClient.post('/backup', { action: 'list-evidence' });
    return response.data;
  },
};

// Operations Work API — Story #5352 Stream 1 (O01-O06). Backs
// dashboard/src/pages/operations/*.tsx via GET /operations?action=...
// (GET+action-param, not backup's POST+action-body shape — the operations
// Lambda dispatch is read-only this wave, see lambda/dashboard/operations/
// handler.py). NEVER add a method named `operationsApi` here — that
// identifier is already live above for Environment-lifecycle deploy/destroy.
export const opsWorkApi = {
  getCommandCentre: async (): Promise<any> => {
    const response = await apiClient.get('/operations', { params: { action: 'get-command-centre' } });
    return response.data;
  },
  getAttention: async (): Promise<any> => {
    const response = await apiClient.get('/operations', { params: { action: 'get-attention' } });
    return response.data;
  },
  getJobs: async (): Promise<any> => {
    const response = await apiClient.get('/operations', { params: { action: 'get-jobs' } });
    return response.data;
  },
  getJob: async (jobId: string): Promise<any> => {
    const response = await apiClient.get('/operations', { params: { action: 'get-job', job_id: jobId } });
    return response.data;
  },
  getApprovals: async (): Promise<any> => {
    const response = await apiClient.get('/operations', { params: { action: 'get-approvals' } });
    return response.data;
  },
  getSchedules: async (): Promise<any> => {
    const response = await apiClient.get('/operations', { params: { action: 'get-schedules' } });
    return response.data;
  },
  getIncidents: async (): Promise<any> => {
    const response = await apiClient.get('/operations', { params: { action: 'get-incidents' } });
    return response.data;
  },

  // --- O02 Attention writers (Story #5651) ---
  assignAttentionItem: async (itemId: string, assignee: string): Promise<any> => {
    const response = await apiClient.post('/operations', { action: 'assign-attention-item', item_id: itemId, assignee });
    return response.data;
  },
  acknowledgeAttentionItem: async (itemId: string): Promise<any> => {
    const response = await apiClient.post('/operations', { action: 'acknowledge-attention-item', item_id: itemId });
    return response.data;
  },
  snoozeAttentionItem: async (itemId: string, snoozeMinutes: number): Promise<any> => {
    const response = await apiClient.post('/operations',
      { action: 'snooze-attention-item', item_id: itemId, snooze_minutes: snoozeMinutes });
    return response.data;
  },

  // --- O06 Incident writers (Story #5651) ---
  declareIncident: async (title: string, severity: 'low' | 'medium' | 'high' | 'critical', summary: string): Promise<any> => {
    const response = await apiClient.post('/operations', { action: 'declare-incident', title, severity, summary });
    return response.data;
  },
  addIncidentEvent: async (incidentId: string, note: string): Promise<any> => {
    const response = await apiClient.post('/operations', { action: 'add-incident-event', incident_id: incidentId, note });
    return response.data;
  },
  resolveIncident: async (incidentId: string, resolutionNote: string): Promise<any> => {
    const response = await apiClient.post('/operations',
      { action: 'resolve-incident', incident_id: incidentId, resolution_note: resolutionNote });
    return response.data;
  },

  // --- O01 Typed command / governed preflight (Story #5651) ---
  preflightCommand: async (commandText: string, environmentId: string): Promise<any> => {
    const response = await apiClient.post('/operations', { action: 'preflight-command', command_text: commandText, environmentId: requireConcreteEnvironment(environmentId) });
    return response.data;
  },
  executeCommand: async (command: string, params: Record<string, unknown>, environmentId: string, targetDigest: string): Promise<any> => {
    const response = await apiClient.post('/operations', { action: 'execute-command', command, params, environmentId: requireConcreteEnvironment(environmentId), target_digest: targetDigest });
    return response.data;
  },

  // --- O04 Approval-plan decisions (Story #5651). approvalToken is the
  // request_id from the SEPARATE identityApi.requestApproval/approveRequest
  // round-trip (see dashboard/src/pages/operationsApproval.ts) -- sent as
  // X-Approval-Token, same convention as every other governed write in this
  // file (identityApi, securityApi, recoveryApproval.ts). ---
  approvePlan: async (planId: string, approvalToken: string, environmentId: string): Promise<any> => {
    const response = await apiClient.post('/operations', { action: 'approve-plan', plan_id: planId, environmentId: requireConcreteEnvironment(environmentId) },
      { headers: { 'X-Approval-Token': approvalToken } });
    return response.data;
  },
  rejectPlan: async (planId: string, reason: string): Promise<any> => {
    const response = await apiClient.post('/operations', { action: 'reject-plan', plan_id: planId, reason });
    return response.data;
  },
  revokePlan: async (planId: string): Promise<any> => {
    const response = await apiClient.post('/operations', { action: 'revoke-plan', plan_id: planId });
    return response.data;
  },

  // --- O05 Schedule writers (Story #5651) ---
  createSchedule: async (name: string, scheduleExpression: string, targetArn: string): Promise<any> => {
    const response = await apiClient.post('/operations',
      { action: 'create-schedule', name, schedule_expression: scheduleExpression, target_arn: targetArn });
    return response.data;
  },
  pauseSchedule: async (name: string): Promise<any> => {
    const response = await apiClient.post('/operations', { action: 'pause-schedule', name });
    return response.data;
  },
  resumeSchedule: async (name: string): Promise<any> => {
    const response = await apiClient.post('/operations', { action: 'resume-schedule', name });
    return response.data;
  },
  editSchedule: async (name: string, scheduleExpression: string): Promise<any> => {
    const response = await apiClient.post('/operations',
      { action: 'edit-schedule', name, schedule_expression: scheduleExpression });
    return response.data;
  },
};

// Data Flows API — Story #5352 Stream 2 (O07-O11). Backs
// dashboard/src/pages/operations/data-flows/*.tsx via GET /data-flows?action=...
// (same GET+action-param shape as opsWorkApi above, since every action in
// lambda/dashboard/data-flows/handler.py is a read or a provably
// side-effect-free operation — see that file's module docstring). NEVER
// reuse the `operationsApi` name (env lifecycle deploy/destroy, below) or
// `opsWorkApi` (Stream 1's O01-O06 — a different Lambda/table) for this.
export const dataFlowsApi = {
  getOverview: async (): Promise<any> => {
    const response = await apiClient.get('/data-flows', { params: { action: 'get-overview' } });
    return response.data;
  },
  getJobs: async (): Promise<any> => {
    const response = await apiClient.get('/data-flows', { params: { action: 'get-jobs' } });
    return response.data;
  },
  getJob: async (jobId: string): Promise<any> => {
    const response = await apiClient.get('/data-flows', { params: { action: 'get-job', job_id: jobId } });
    return response.data;
  },
  listSources: async (database?: string): Promise<any> => {
    const response = await apiClient.get('/data-flows', { params: { action: 'list-sources', ...(database ? { database } : {}) } });
    return response.data;
  },
  registerSourcePreview: async (database: string, sourceId: string): Promise<any> => {
    const response = await apiClient.get('/data-flows', { params: { action: 'register-source-preview', database, source_id: sourceId } });
    return response.data;
  },
  testConnection: async (database: string, sourceId: string): Promise<any> => {
    const response = await apiClient.get('/data-flows', { params: { action: 'test-connection', database, source_id: sourceId } });
    return response.data;
  },
  compareSchema: async (database: string): Promise<any> => {
    const response = await apiClient.get('/data-flows', { params: { action: 'compare-schema', database } });
    return response.data;
  },
  getSchedules: async (): Promise<any> => {
    const response = await apiClient.get('/data-flows', { params: { action: 'get-schedules' } });
    return response.data;
  },
  previewSchedule: async (expression: string): Promise<any> => {
    const response = await apiClient.get('/data-flows', { params: { action: 'preview-schedule', expression } });
    return response.data;
  },
  getActivity: async (): Promise<any> => {
    const response = await apiClient.get('/data-flows', { params: { action: 'get-activity' } });
    return response.data;
  },
  // Story #5652 (Restore Lane C) — real writers, POST /data-flows {action, ...}.
  retryJob: async (jobId: string): Promise<any> => {
    const response = await apiClient.post('/data-flows', { action: 'retry-job', job_id: jobId });
    return response.data;
  },
  quarantineJob: async (jobId: string, reason: string): Promise<any> => {
    const response = await apiClient.post('/data-flows', { action: 'quarantine-job', job_id: jobId, reason });
    return response.data;
  },
  unquarantineJob: async (jobId: string, reason?: string): Promise<any> => {
    const response = await apiClient.post('/data-flows', { action: 'unquarantine-job', job_id: jobId, reason });
    return response.data;
  },
  createSchedule: async (flowId: string, scheduleExpression: string): Promise<any> => {
    const response = await apiClient.post('/data-flows', { action: 'create-schedule', flow_id: flowId, schedule_expression: scheduleExpression });
    return response.data;
  },
  pauseSchedule: async (ruleName: string): Promise<any> => {
    const response = await apiClient.post('/data-flows', { action: 'pause-schedule', rule_name: ruleName });
    return response.data;
  },
  resumeSchedule: async (ruleName: string): Promise<any> => {
    const response = await apiClient.post('/data-flows', { action: 'resume-schedule', rule_name: ruleName });
    return response.data;
  },
  editSchedule: async (ruleName: string, scheduleExpression: string): Promise<any> => {
    const response = await apiClient.post('/data-flows', { action: 'edit-schedule', rule_name: ruleName, schedule_expression: scheduleExpression });
    return response.data;
  },
  previewBackfill: async (params: { database: string; source_id: string; start: string; end: string }): Promise<any> => {
    const response = await apiClient.get('/data-flows', { params: { action: 'preview-backfill', ...params } });
    return response.data;
  },
  createBackfillPlan: async (params: { database: string; source_id: string; start: string; end: string; preview_digest: string }): Promise<any> => {
    const response = await apiClient.post('/data-flows', { action: 'create-backfill-plan', ...params });
    return response.data;
  },
  listBackfillPlans: async (): Promise<any> => {
    const response = await apiClient.get('/data-flows', { params: { action: 'list-backfill-plans' } });
    return response.data;
  },
};

export default apiClient;

// Operations API (Portal-driven deploy/destroy/status)
export const operationsApi = {
  /**
   * Get overall status (all environments)
   */
  getStatus: async (): Promise<any> => {
    try {
      const response = await apiClient.get('/status');
      const data = response.data;
      // Check for HTML error response
      if (typeof data === 'string' && (data.includes('<!DOCTYPE') || data.includes('<html'))) {
        return { error: 'API not deployed', environments: {} };
      }
      return data;
    } catch (err: any) {
      if (isScopeCancellation(err)) throw err;
      console.warn('Status API error:', err?.message);
      return { error: err?.message || 'Failed to fetch status', environments: {} };
    }
  },

  /**
   * Trigger deployment from the portal
   */
  deploy: async (params: { env: string; layer: string; power_action?: boolean }): Promise<any> => {
    // Backend expects 'environment' not 'env'
    const response = await apiClient.post('/deploy', {
      environment: params.env,
      layer: params.layer,
      power_action: params.power_action,
    });
    return response.data;
  },

  /**
   * Trigger destroy from the portal
   */
  destroy: async (params: { env: string; layer: string; confirmation?: string; power_action?: boolean }): Promise<any> => {
    // Backend expects 'environment' not 'env'
    const response = await apiClient.post('/destroy', {
      environment: params.env,
      layer: params.layer,
      confirmation: params.confirmation,
      power_action: params.power_action,
    });
    return response.data;
  },

  /**
   * Stop an active deployment/destroy operation
   */
  stop: async (params: { env: string; build_id?: string }): Promise<any> => {
    // Backend expects 'environment' not 'env'
    const response = await apiClient.post('/deploy/stop', { environment: params.env, build_id: params.build_id });
    return response.data;
  },
};

// Power API (start/stop environment resources)
export const powerApi = {
  getState: async (params: { env: string }): Promise<any> => {
    const response = await apiClient.get('/power', { params: { environment: params.env } });
    return response.data;
  },
  setPower: async (params: { env: string; action: 'start' | 'stop'; eks_desired?: number; confirm?: boolean }): Promise<any> => {
    const response = await apiClient.post('/power', {
      environment: params.env,
      action: params.action,
      eks_desired: params.eks_desired,
      confirm: params.confirm,
    });
    return response.data;
  },
};

// Demo Environment API (Environment Control Plane — US #4440)
// Portal-driven demo/test environment + demo-data ops. Routes resolve under
// /api/demo-env/{action}; the backend dispatches on the trailing segment.
// Rich per-baseline metadata (manifest v2, Story #4729). `baselines` (names
// only) is retained for back-compat; `baseline_details` is what the Baselines
// card renders — server-sorted pinned-first then newest-first.
export interface BaselineDetail {
  name: string;
  captured_at?: string | null;
  captured_by?: string | null;
  description?: string;
  origin?: 'portal-save' | 'cli-export' | 'auto-pre-promote' | 'scenario' | string;
  protected?: boolean;
  env?: string;
  account?: string;
  app_version?: { git_sha?: string; recorded_at?: number } | null;
  tables?: number;
  items?: number;
  /**
   * Per-table item counts from the baseline's own manifest (Story #5868).
   * op_save_dataset has always written these; the summary that builds
   * baseline_details used to collapse them to a total and drop the detail,
   * which left the Data & Baselines screen able to compare only grand totals.
   */
  table_items?: Record<string, number>;
  size_bytes?: number;
  objects?: number;
}

export interface DemoEnvStatus {
  env: string;
  account?: string;
  opensearch?: {
    running: boolean;
    processing?: boolean;
    endpoint?: string | null;
    // Story #4940 single-shared-domain model: set when this env runs no domain
    // of its own and consumes another env's shared domain over VPC peering.
    shared?: { domain: string; endpoint?: string; health?: string | null; healthy: boolean };
  };
  nat?: { count: number; running: boolean };
  data?: Record<string, number | null>;
  snapshots?: string[];
  baselines?: string[];
  baseline_details?: BaselineDetail[];
  version?: string;
  error?: string;
}

// Result payload of a COMPLETED reinstate job (jobStatus().result). The backend
// verifies against live AWS after the Terraform apply — the UI renders these
// observed flags, never a bare "done" nobody checked.
export interface ReinstateResult {
  verified?: boolean;
  opensearch_running?: boolean;
  nat_running?: boolean;
  baseline?: string | null;
}

// Release-Promotion Control Plane (extends the Environment Control Plane).
// One promotion job moves work UP the release ladder (dev→test→uat→prod) from
// any lower source to any higher target, per-portal, in four modes:
//   1 Design only · 2 Design+Backend · 3 Design+Backend+Data · 4 Data only.
// Mode 4 is the absorbed "Replicate from DEV" (generalised to any source).
export type PromoteMode = 1 | 2 | 3 | 4;

export interface PromoteOptions {
  source: string;
  target: string;
  portal: string;
  mode: PromoteMode;
  autoSpinUp?: boolean;
  saveBaselineFirst?: boolean;
  // For a recovery [Re-run from data stage] — resume at promote-data, skipping
  // the already-captured baseline step.
  resumeStage?: string;
  // Type-to-confirm value (target name; 'deploy-prod' for prod).
  confirmation?: string;
  // W3: per-promotion gate waivers, keyed by gate stage name
  // ('gate-version' | 'gate-sechub' | 'gate-drift'); value is the justification,
  // recorded on the stage + audit trail.
  gateOverrides?: Record<string, string>;
  // W3: pin a frozen Golden Config sha (a Demo Profile run sets this).
  pinGitSha?: string;
}

// One stage of a multi-stage promote job, aggregated into a live checklist.
export interface PromoteStage {
  name: string;
  status: 'pending' | 'running' | 'complete' | 'failed' | 'blocked' | 'skipped' | 'cancelled';
  build_id?: string;
  message?: string;
  // W3: gate stages carry a decision detail (pass/blocked, reason, note, override,
  // as_of, critical_high, drifted, …) for the UI to render inline.
  detail?: Record<string, any>;
}

export interface PromotionJob {
  id?: string;
  job?: string;
  op?: string;
  mode?: PromoteMode;
  source?: string;
  target?: string;
  portal?: string;
  status?: 'running' | 'complete' | 'failed' | 'blocked' | 'cancelled';
  stage?: string;
  stages?: PromoteStage[];
  build_id?: string;
  started?: number;
  git_sha?: string;
  manifest_missing?: boolean;
  auto_baseline?: string;
  // Non-blocking readiness warnings attached at preflight (e.g. the target's
  // staged CodeBuild source zip is stale/missing) — rendered amber, never fatal.
  warnings?: string[];
  error?: string;
  message?: string;
  // W3: when a gate blocks, `blocked` carries {gate, reason, …}.
  blocked?: Record<string, any> & { gate?: string; reason?: string };
  cancel_requested?: boolean;
  result?: Record<string, any> & { auto_baseline?: string };
}

// R2: cached drift verdict — the scheduled compute-drift compares the target's
// live fingerprint (api-handler CodeSha256 + frontend bundle ETag) against the
// deployed-manifest recorded at the last promotion. drifted null = no baseline
// yet (honest unknown, never guessed).
export interface DriftVerdict {
  drifted: boolean | null;
  changes?: number;
  details?: Array<{ component: string; recorded: string; live: string | null }>;
  baseline_git_sha?: string | null;
  baseline_recorded_at?: number;
  as_of?: string;
  basis?: string;
  note?: string;
}

export interface DriftResponse {
  env: string;
  portal: string;
  verdict: DriftVerdict | null;
  error?: string;
}

// Promotion Packages — a promotable "unit of work" (a story tracked by the P0
// registry) that is on the source env but not yet on the target.
export interface PromotionUnit {
  story: string;
  title?: string;
  sha: string;
  on_envs: string[];
  at?: string;
  order?: number;
  promo_status?: string;
}
export interface PackageResolution {
  pin_sha: string | null;
  pin_story?: string;
  selected: string[];
  dependencies: string[];
  included: Array<{ story: string; title?: string; sha: string; order: number }>;
  error?: string | null;
}
export interface PromotionUnitsResponse {
  source: string;
  target: string;
  portal: string;
  promotable: PromotionUnit[];
  already_on_target: PromotionUnit[];
  total: number;
  preview?: PackageResolution;
  error?: string;
}
export interface DependencyDetail {
  session_id: string;
  label: string;
  stories: string[];
  shared_files: string[];  // the exact source files that couple the two sessions
  shared_count: number;    // total shared (shared_files is capped for display)
}
export interface SessionBatch {
  session_id: string;
  label: string;
  lead: string;
  at: string;
  order: number;
  stories: string[];
  commit_count: number;
  file_count: number;
  files_truncated?: boolean;
  sha: string;
  depends_on: string[];   // earlier sessions this one shares source files with
  depends_on_detail?: DependencyDetail[];  // WHY each dependency is coupled (shared files)
  on_target?: boolean;    // already deployed up to the selected target env
}
export interface SessionsResponse {
  portal: string;
  source: string;
  target: string;
  sessions: SessionBatch[];
  baseline?: { at: string; sha: string };  // last-known deploy to the target (on-target cutoff)
  error?: string;
}
export interface AdoWorkItem {
  id: number;
  title: string | null;   // null when the item couldn't be read (missing / no access)
  type: string;
  state: string;
  tasks: AdoWorkItem[];    // child work items (usually Tasks)
}
export interface AdoWorkItemsResponse {
  items: Record<string, AdoWorkItem>;  // keyed by story id (string)
  error?: string;
}
export interface PackageHistoryRow {
  target: string;
  pin_sha: string;
  selected: string[];
  dependencies: string[];
  included: string[];
  job?: string;
  actor?: string;
  at: string;
}

// W3: a Golden Config is a frozen, hash-pinned build captured from a source env.
export interface GoldenConfig {
  name: string;
  portal?: string;
  git_sha?: string | null;
  source_env?: string;
  pinned?: boolean;
  description?: string;
  created_by?: string;
  created?: number | string;
  updated?: number | string;
}

// W3: a Demo Profile binds a Golden Config + Data Set + target for one-click run.
export interface DemoProfile {
  name: string;
  portal?: string;
  golden_config?: string;
  data_set?: string;
  target?: string;
  mode?: number | string;
  created_by?: string;
  updated?: number | string;
}

// Per-(target[,portal]) readiness as returned by op_targets — the single
// render-time source the UI reads for all show/hide, disabled and badge state.
export interface TargetReadiness {
  ready: 'ready' | 'blocked';
  reason?: string;
  deployable: boolean;
  first_time?: boolean;
  portals?: Record<string, TargetReadiness>;
}

export interface TargetsMatrix {
  source?: string;
  // Per-target readiness; each target may carry a per-portal sub-matrix when
  // `portal` was omitted from the request.
  targets?: Record<string, TargetReadiness>;
  error?: string;
}

// ---------------------------------------------------------------------------
// Environment lifecycle jobs + demo-hold (W2 — Environments hub / detail).
// Every lifecycle/data operation (reset, snapshot, save, restore, teardown,
// stand-up, promote) runs as a *tracked* job. `lock` names the single active
// mutating job for the env (so two operators cannot mutate at once); `hold` is
// an admin demo-hold that blocks destructive/data ops during a live demo.
// ---------------------------------------------------------------------------
export interface EnvJobStep {
  name: string;
  status: string;
}

export interface EnvJob {
  job_id: string;
  op: string;
  name?: string;
  actor?: string;
  status: 'running' | 'complete' | 'failed' | 'pending' | 'blocked' | string;
  steps?: EnvJobStep[];
  /**
   * Epoch SECONDS — and the live API sends them as STRINGS, not numbers.
   *
   * Measured 2026-09-06 by invoking the real hub Lambda
   * (secureembed-management-dashboard-demo-env, /demo-env/env-jobs?env=test):
   *   {"started": "1787476645", "heartbeat": "1787476646", "finished": "1787476646"}
   * DynamoDB Number attributes come back as strings through this handler.
   *
   * These were typed `number`. Nothing is broken today — the one place that
   * reads them multiplies by 1000, and JS coerces a numeric string for `*`, so
   * the date renders correctly (verified, not assumed). But the type was
   * asserting something false, and the next person to reach for `.toFixed()`,
   * `Math.max()` or a `>` comparison on these would get a silent wrong answer
   * rather than a compile error. Typing the truth costs nothing and removes
   * that trap; isStalled() in LifecycleTiers already does its own numeric
   * coercion for exactly this reason.
   */
  started?: number | string;
  heartbeat?: number | string;
  finished?: number | string;
}

export interface EnvLock {
  owner_job?: string;
}

export interface EnvHold {
  held_by?: string;
  reason?: string;
  expiry?: number;
}

export interface EnvJobsResponse {
  env: string;
  jobs: EnvJob[];
  lock: EnvLock | null;
  hold: EnvHold | null;
  error?: string;
}

export interface EnvHoldResponse {
  env: string;
  hold: EnvHold | null;
  error?: string;
}

// ---------------------------------------------------------------------------
// Story #5650 — the demo domain screens restored (O18-O22).
// ---------------------------------------------------------------------------

// O20 — single-operator approval machinery (decision #5284), same request/
// approve shape as every other approval-gated control in this codebase.
export interface HoldApprovalRequest {
  request_id: string;
  payload_digest: string;
  expires_at: number;
}

export interface HoldApprovalReceipt {
  request_id: string;
  status: string;
  approved_at?: number;
  verification?: { verified: boolean; reason?: string; result?: unknown };
}

// O18 — a registered demo scenario catalogue entry. The catalogue is the set of
// rendered scenario files in the AU vault under env-control/scenarios/.
export interface ScenarioCatalogueEntry {
  name: string;
  portal: string;
  environment: string;
  approved_baseline?: string;
  expected_counts?: Record<string, unknown>;
  git_sha?: string;
  reset_profile?: string;
  users?: Record<string, string>;
  verification?: { status: string };
  created_by?: string;
  updated?: number;
}

// O19 — one readiness check's real verdict. Never fabricated: a check the
// server cannot perform reports UNKNOWN with `reason` explaining why.
export interface DemoReadinessCheck {
  status: 'PASS' | 'FAIL' | 'UNKNOWN';
  reason: string;
}

export interface DemoReadinessVerdict {
  env: string;
  portal: string;
  scenario: string | null;
  verdict: 'PASS' | 'FAIL' | 'INCOMPLETE' | 'UNKNOWN';
  checks: Record<string, DemoReadinessCheck>;
  hold?: EnvHold | null;
  error?: string;
}

// O21 — read-only preview of what a scenario-reset would change.
export interface ResetPreview {
  scenario: string;
  env: string;
  blocked: boolean;
  block_reason: string | null;
  persona_error: string | null;
  would_write: Record<string, number>;
  would_delete_fixed_keys: Record<string, number>;
  would_delete_gsi_cascade_estimate: Record<string, number>;
  invariants_to_check: number;
  hold: EnvHold | null;
}

// O22 — one entry in the append-only evidence chain. Backed by the demo-env
// audit trail in secureembed-management-audit-logs, which has recorded every
// demo-env mutation since July.
export interface EvidenceEntry {
  entry_id: string;
  env: string;
  kind: 'scenario_readiness' | 'safety_point' | 'hold' | 'reset_preview' | 'reset' | 'post_demo';
  actor: string;
  recorded_at: number;
  [field: string]: unknown;
}

export const demoEnvApi = {
  getStatus: async (params: { env?: string } = {}): Promise<DemoEnvStatus> => {
    const env = requireConcreteEnvironment(params.env);
    try {
      const response = await apiClient.get('/demo-env/status', { params: { env } });
      return safeApiResponse<DemoEnvStatus>(response.data, { env, error: 'API not deployed' });
    } catch (err: any) {
      if (isScopeCancellation(err)) throw err;
      // A per-env HTTP failure must degrade to an honest `error` status: the
      // fleet view fetches every env in one Promise.all, so a thrown 500 for
      // one spoke would blank ALL cards — and an undefined status renders as
      // "torn down", which is a lie for an always-on env.
      const message =
        err?.response?.data?.error || err?.message || 'status unavailable';
      return { env, error: String(message) };
    }
  },
  resetData: async (params: { env?: string; name?: string; portal?: string }): Promise<any> => {
    const response = await apiClient.post('/demo-env/reset-data', {
      env: requireConcreteEnvironment(params.env),
      name: params.name,
      portal: params.portal,
    });
    return response.data;
  },
  // T1 cost teardown — destroys the OpenSearch domain + NAT gateway (Bug #4851).
  // Asynchronous: returns { job, build_id } immediately and the caller polls
  // getJobStatus. The job reaches 'complete' only after AWS confirms both
  // resources are actually gone; a green build alone is not enough.
  //
  // This replaced powerApi.setPower({action:'stop'}), which the button used to
  // call. That handler manages EKS node groups and Aurora clusters, neither of
  // which this platform runs, so it reported success while destroying nothing
  // and TEST billed at full rate for a month.
  teardown: async (params: { env?: string; tier?: 'cost' | 'data' }): Promise<any> => {
    const response = await apiClient.post('/demo-env/teardown', {
      env: requireConcreteEnvironment(params.env),
      tier: params.tier || 'cost',
    });
    return response.data;
  },
  // Reinstate a torn-down environment — the way back from a T1/T2 teardown.
  // Terraform rebuilds the OpenSearch domain + NAT gateway in the spoke, then,
  // when `baseline` is named, restores that data set (records + search index
  // together). Omit `baseline` for infrastructure only (existing data is left
  // as-is; safe to run even when everything is already up — it re-verifies).
  // Asynchronous: returns { job } and the caller polls jobStatus — with a LONG
  // cap (the OpenSearch rebuild alone runs ~20–40 min), not the 5-min default.
  reinstate: async (params: { env?: string; baseline?: string }): Promise<{ job?: string; error?: string }> => {
    const body: Record<string, any> = { env: requireConcreteEnvironment(params.env) };
    if (params.baseline) body.baseline = params.baseline;
    const response = await apiClient.post('/demo-env/reinstate', body);
    return response.data;
  },
  // Apply a scenario file (fence-checked fixture replace) — narrower than
  // resetData: scoped to the scenario's own rows, legal on dev as well as
  // test (SCENARIO_ENV_ALLOWLIST, T5b). force overrides a demo-hold held by
  // a DIFFERENT caller (holder-only relaxation, D7); the same holder never
  // needs it. Synchronous — the response carries the write/delete counts.
  scenarioReset: async (params: { env?: string; scenario?: string; force?: boolean }): Promise<any> => {
    const response = await apiClient.post('/demo-env/scenario-reset', {
      env: requireConcreteEnvironment(params.env),
      scenario: params.scenario,
      force: params.force,
    });
    return response.data;
  },
  snapshot: async (params: { env?: string; name?: string }): Promise<any> => {
    const response = await apiClient.post('/demo-env/snapshot', {
      env: requireConcreteEnvironment(params.env),
      name: params.name,
    });
    return response.data;
  },
  restore: async (params: { env?: string; name?: string }): Promise<any> => {
    const response = await apiClient.post('/demo-env/restore', {
      env: requireConcreteEnvironment(params.env),
      name: params.name,
    });
    return response.data;
  },
  saveDataset: async (params: { env?: string; name?: string; description?: string }): Promise<any> => {
    const response = await apiClient.post('/demo-env/save-dataset', {
      env: requireConcreteEnvironment(params.env),
      name: params.name,
      description: params.description,
    });
    return response.data;
  },

  // Metadata-only edit: description and/or the pinned (delete-protected) flag.
  updateBaseline: async (params: {
    env?: string;
    name: string;
    description?: string;
    protected?: boolean;
  }): Promise<any> => {
    const body: Record<string, any> = { env: requireConcreteEnvironment(params.env), name: params.name };
    if (params.description !== undefined) body.description = params.description;
    if (params.protected !== undefined) body.protected = params.protected;
    const response = await apiClient.post('/demo-env/update-baseline', body);
    return response.data;
  },
  // Atomically restore BOTH layers of a baseline (DynamoDB records + OpenSearch
  // index) under one name, so the two stores can never drift.
  restoreBaseline: async (params: { env?: string; name?: string }): Promise<any> => {
    const response = await apiClient.post('/demo-env/restore-baseline', {
      env: requireConcreteEnvironment(params.env),
      name: params.name,
    });
    return response.data;
  },
  // Poll the state of an async Save/Restore/Rename job (running | complete | failed)
  // so the UI can show a live progress indicator instead of manual Refresh.
  jobStatus: async (params: { env?: string; id: string }): Promise<any> => {
    const response = await apiClient.get('/demo-env/job-status', {
      params: { env: requireConcreteEnvironment(params.env), id: params.id },
    });
    return response.data;
  },
  // Delete BOTH halves of a baseline (records export + OpenSearch snapshot). Synchronous.
  deleteBaseline: async (params: { env?: string; name: string }): Promise<any> => {
    const response = await apiClient.post('/demo-env/delete-baseline', {
      env: requireConcreteEnvironment(params.env),
      name: params.name,
    });
    return response.data;
  },
  // Rename a baseline (clone snapshot + copy records + delete old). Async (returns a job).
  renameBaseline: async (params: { env?: string; name: string; new: string }): Promise<any> => {
    const response = await apiClient.post('/demo-env/rename-baseline', {
      env: requireConcreteEnvironment(params.env),
      name: params.name,
      new: params.new,
    });
    return response.data;
  },
  // Start a promotion job (Release-Promotion Control Plane). Async — returns 202 {job}.
  // Moves work UP the ladder from `source` to `target`, per portal, in one of four modes.
  // The backend reads snake_case options, so translate here (a prior camelCase body
  // silently fell back to backend defaults — gate overrides MUST reach the backend).
  promote: async (params: PromoteOptions): Promise<PromotionJob> => {
    const body: Record<string, any> = {
      source: params.source, target: params.target,
      portal: params.portal, mode: params.mode,
    };
    if (params.autoSpinUp !== undefined) body.auto_spin_up = params.autoSpinUp;
    if (params.saveBaselineFirst !== undefined) body.save_baseline_first = params.saveBaselineFirst;
    if (params.resumeStage) body.resume_stage = params.resumeStage;
    if (params.confirmation) body.confirmation = params.confirmation;
    if (params.gateOverrides) body.gate_overrides = params.gateOverrides;
    if (params.pinGitSha) body.pin_git_sha = params.pinGitSha;
    const response = await apiClient.post('/demo-env/promote', body);
    return response.data;
  },
  // W3: cancel a running promotion — honoured at the next stage boundary (an
  // in-flight build or data slice finishes first). Admin.
  promotionCancel: async (params: { target: string; id: string }): Promise<PromotionJob> => {
    const response = await apiClient.post('/demo-env/promotion-cancel', params);
    return response.data;
  },
  // W3/R3: roll a promotion back. Default = re-apply the pre-promotion data
  // baseline. includeBuild=true ALSO re-applies the prior deployed-manifest sha
  // as a pinned promotion (build + data revert in one ordered job; build-only
  // when the promotion captured no data baseline). Admin; same guard-rails as
  // any destructive op (never prod/uat, demo-hold blocks).
  promotionRollback: async (params: { target: string; id: string; portal?: string; includeBuild?: boolean }): Promise<PromotionJob> => {
    const { includeBuild, ...rest } = params;
    const response = await apiClient.post('/demo-env/promotion-rollback',
      includeBuild ? { ...rest, include_build: true } : rest);
    return response.data;
  },
  // R2: cached drift verdict for the env-card badge / Overview banner.
  getDrift: async (env: string, portal = 'innovateflow'): Promise<DriftResponse> => {
    const response = await apiClient.get('/demo-env/drift', { params: { env, portal } });
    return safeApiResponse<DriftResponse>(response.data, { env, portal, verdict: null });
  },
  // R2: admin "Recompute drift" — synchronous fingerprint + verdict refresh.
  computeDrift: async (env: string, portal = 'innovateflow'): Promise<DriftResponse> => {
    const response = await apiClient.post('/demo-env/compute-drift', { env, portal });
    return response.data;
  },
  // Poll a multi-stage promotion job (running | complete | failed | blocked).
  // Reuses the extended job-vault schema (stages[], auto_baseline, build_id, …).
  promotionStatus: async (params: { target: string; id: string }): Promise<PromotionJob> => {
    const response = await apiClient.get('/demo-env/promotion-status', { params });
    return response.data;
  },
  // Promotion Packages: the work-items on `source` not yet on `target`, plus a
  // dependency-sweep preview. Read-only; any authenticated caller.
  listPromotionUnits: async (
    source = 'dev', target = 'test', portal = 'innovateflow',
  ): Promise<PromotionUnitsResponse> => {
    const response = await apiClient.get('/demo-env/promotion-units', { params: { source, target, portal } });
    return safeApiResponse<PromotionUnitsResponse>(response.data, {
      source, target, portal, promotable: [], already_on_target: [], total: 0,
    });
  },
  // Promotion Packages by dev SESSION (the logical model): each batch is a session, with
  // a real file-overlap dependency graph between sessions.
  listSessionBatches: async (portal = 'innovateflow', target = 'test', source = 'dev'): Promise<SessionsResponse> => {
    const response = await apiClient.get('/demo-env/promotion-sessions', { params: { portal, target, source } });
    return safeApiResponse<SessionsResponse>(response.data, { portal, source, target, sessions: [] });
  },
  // ADO story titles + their child tasks, for the Promotion Packages tree. Read-only.
  adoWorkItems: async (ids: string[]): Promise<AdoWorkItemsResponse> => {
    if (!ids.length) return { items: {} };
    const response = await apiClient.get('/demo-env/ado-workitems', { params: { ids: ids.join(',') } });
    return safeApiResponse<AdoWorkItemsResponse>(response.data, { items: {} });
  },
  // Promote a SELECTED set of story ids as one package. The backend resolves the
  // selection to a single pin sha (sweeping in earlier un-promoted units as
  // dependencies) and drives the same staged promotion engine as promote(). Admin.
  promotePackage: async (params: {
    source?: string; target: string; portal?: string; storyIds?: string[]; sessionIds?: string[];
    mode?: PromoteMode; confirmation?: string; gateOverrides?: Record<string, string>;
  }): Promise<PromotionJob & { package?: PackageResolution }> => {
    const body: Record<string, any> = {
      source: params.source ?? 'dev', target: params.target,
      portal: params.portal ?? 'innovateflow', mode: params.mode ?? 4,
    };
    if (params.storyIds) body.story_ids = params.storyIds;
    if (params.sessionIds) body.session_ids = params.sessionIds;
    if (params.confirmation) body.confirmation = params.confirmation;
    if (params.gateOverrides) body.gate_overrides = params.gateOverrides;
    const response = await apiClient.post('/demo-env/promote-package', body);
    return response.data;
  },
  // Append-only history of package promotions (for the history panel + rollback picker).
  listPackageHistory: async (
    portal = 'innovateflow', target?: string,
  ): Promise<{ portal: string; target: string; history: PackageHistoryRow[] }> => {
    const response = await apiClient.get('/demo-env/promotion-package-history', { params: { portal, ...(target ? { target } : {}) } });
    return safeApiResponse(response.data, { portal, target: target ?? 'all', history: [] });
  },
  // Render-time readiness/deployability matrix for the UI. ONE call (portal omitted)
  // returns the full per-target/per-portal matrix so the UI never probes N×M itself.
  targets: async (params: { source: string; portal?: string }): Promise<TargetsMatrix> => {
    const response = await apiClient.get('/demo-env/targets', { params });
    return response.data;
  },
  // Environment activity: the tracked lifecycle/data jobs for an env, plus the
  // current lock (single active mutating job) and demo-hold, in one call.
  getEnvJobs: async (env: string | null): Promise<EnvJobsResponse> => {
    env = requireConcreteEnvironment(env);
    const response = await apiClient.get('/demo-env/env-jobs', { params: { env } });
    return safeApiResponse<EnvJobsResponse>(response.data, {
      env,
      jobs: [],
      lock: null,
      hold: null,
      error: 'API not deployed',
    });
  },
  // Current demo-hold for an env (blocks resets/destructive ops during a live demo).
  getHold: async (env: string | null): Promise<EnvHoldResponse> => {
    env = requireConcreteEnvironment(env);
    const response = await apiClient.get('/demo-env/env-hold', { params: { env } });
    return safeApiResponse<EnvHoldResponse>(response.data, { env, hold: null });
  },
  // Set a demo-hold (admin): a reason + TTL in seconds.
  // O20 (decision #5284): acquiring a hold requires a VERIFIED single-operator
  // approval receipt. approvalId is the request_id from requestHoldApproval,
  // already run through approveHoldRequest. The server re-verifies it against
  // this exact {env, reason, ttlSec}; a missing or stale approval is refused
  // with 428, never silently ignored.
  setHold: async (env: string, reason: string, ttlSec: number, approvalId?: string): Promise<EnvHoldResponse> => {
    env = requireConcreteEnvironment(env);
    const response = await apiClient.post('/demo-env/env-hold', {
      env, reason, ttl_sec: ttlSec, approval_id: approvalId,
    });
    return response.data;
  },
  // O20 step 1: request a single-operator approval bound to the exact
  // {env, reason, ttl_sec} payload. Returns a request_id -- never itself usable
  // as an approval; approveHoldRequest must be called next, separately.
  requestHoldApproval: async (env: string, reason: string, ttlSec: number): Promise<HoldApprovalRequest> => {
    const response = await apiClient.post('/demo-env/env-hold/request-approval', {
      env, reason, ttl_sec: ttlSec,
    });
    return response.data;
  },
  // O20 step 2: approve the SAME request_id + exact payload, with a fresh
  // step-up auth timestamp (decision #5284 mechanism 2 -- a replayed or cached
  // session cannot approve). Returns the approval receipt.
  approveHoldRequest: async (
    requestId: string, env: string, reason: string, ttlSec: number, freshAuthTime?: number,
  ): Promise<HoldApprovalReceipt> => {
    const response = await apiClient.post('/demo-env/env-hold/approve', {
      request_id: requestId, env, reason, ttl_sec: ttlSec,
      fresh_auth_time: freshAuthTime ?? Math.floor(Date.now() / 1000),
    });
    return response.data;
  },

  // O18 -- the registered demo scenario catalogue. Binds a scenario file name
  // to its portal/environment, approved baseline, expected counts, pinned
  // build and personas. Backed by the vault objects under
  // env-control/scenarios/, which handler.py already read one-at-a-time.
  listScenarios: async (portal = 'innovateflow'): Promise<{ portal: string; scenarios: ScenarioCatalogueEntry[] }> => {
    const response = await apiClient.get('/demo-env/demo-scenarios', { params: { portal } });
    return safeApiResponse(response.data, { portal, scenarios: [] });
  },
  registerScenario: async (params: {
    name: string; portal?: string; environment?: string; approved_baseline?: string;
    expected_counts?: Record<string, unknown>; git_sha?: string; reset_profile?: string;
    users?: Record<string, string>;
  }): Promise<{ scenario: ScenarioCatalogueEntry }> => {
    const response = await apiClient.post('/demo-env/demo-scenarios', {
      portal: 'innovateflow', environment: 'test', ...params,
    });
    return response.data;
  },
  deleteScenario: async (name: string, portal = 'innovateflow'): Promise<{ deleted: string }> => {
    const response = await apiClient.delete('/demo-env/demo-scenarios', { params: { name, portal } });
    return response.data;
  },

  // O19 -- a real readiness verdict (PASS/FAIL/INCOMPLETE), never fabricated:
  // any check the server cannot actually perform comes back UNKNOWN with its
  // reason, so an operator can tell "not ready" from "not checked".
  getReadiness: async (env: string, scenario?: string, portal = 'innovateflow'): Promise<DemoReadinessVerdict> => {
    const response = await apiClient.get('/demo-env/demo-readiness', { params: { env, scenario, portal } });
    return safeApiResponse<DemoReadinessVerdict>(response.data, {
      env, portal, scenario: scenario ?? null, verdict: 'UNKNOWN', checks: {},
    });
  },

  // O21 -- read-only preview of what a scenario-reset would change. Executes
  // nothing; this is the read model whose absence withdrew the screen.
  getResetPreview: async (env: string, scenario: string): Promise<ResetPreview> => {
    const response = await apiClient.get('/demo-env/reset-preview', { params: { env, scenario } });
    return response.data;
  },

  // O22 -- the append-only evidence chain for an environment.
  listEvidence: async (env: string, limit = 50): Promise<{ env: string; evidence: EvidenceEntry[] }> => {
    const response = await apiClient.get('/demo-env/demo-evidence', { params: { env, limit } });
    return safeApiResponse(response.data, { env, evidence: [] });
  },
  // O22 -- append an operator's post-demo note to the same chain, so the record
  // of what a demo actually showed sits beside the machine-recorded actions.
  postDemoProof: async (params: { env: string; note?: string; scenario?: string }): Promise<{ evidence: EvidenceEntry }> => {
    const response = await apiClient.post('/demo-env/demo-evidence/post-demo-proof', params);
    return response.data;
  },
  // Clear the demo-hold (admin). DELETE carries the env in the body.
  clearHold: async (env: string): Promise<EnvHoldResponse> => {
    env = requireConcreteEnvironment(env);
    const response = await apiClient.delete('/demo-env/env-hold', { data: { env } });
    return response.data;
  },
  // Back-compat alias for the absorbed "Replicate from DEV" card — maps to mode 4.
  pullFromDev: async (): Promise<PromotionJob> => {
    return demoEnvApi.promote({ source: 'dev', target: 'test', portal: 'innovateflow', mode: 4 });
  },

  // W3 Releases registry — Golden Configs (frozen, hash-pinned builds).
  listGoldenConfigs: async (portal = 'innovateflow'): Promise<{ portal: string; golden_configs: GoldenConfig[] }> => {
    const response = await apiClient.get('/demo-env/golden-configs', { params: { portal } });
    return safeApiResponse(response.data, { portal, golden_configs: [] });
  },
  captureGoldenConfig: async (params: { name: string; portal?: string; source?: string; description?: string; git_sha?: string }): Promise<{ golden_config: GoldenConfig }> => {
    const response = await apiClient.post('/demo-env/golden-configs', {
      portal: 'innovateflow', source: 'dev', ...params,
    });
    return response.data;
  },
  deleteGoldenConfig: async (name: string, portal = 'innovateflow'): Promise<any> => {
    const response = await apiClient.delete('/demo-env/golden-configs', { params: { name, portal } });
    return response.data;
  },

  // W3 Releases registry — Demo Profiles (golden config + data set + target).
  listDemoProfiles: async (portal = 'innovateflow'): Promise<{ portal: string; demo_profiles: DemoProfile[] }> => {
    const response = await apiClient.get('/demo-env/demo-profiles', { params: { portal } });
    return safeApiResponse(response.data, { portal, demo_profiles: [] });
  },
  saveDemoProfile: async (params: { name: string; golden_config: string; target?: string; data_set?: string; mode?: number; portal?: string }): Promise<{ demo_profile: DemoProfile }> => {
    const response = await apiClient.post('/demo-env/demo-profiles', {
      portal: 'innovateflow', target: 'test', mode: 3, ...params,
    });
    return response.data;
  },
  deleteDemoProfile: async (name: string, portal = 'innovateflow'): Promise<any> => {
    const response = await apiClient.delete('/demo-env/demo-profiles', { params: { name, portal } });
    return response.data;
  },
  // One-click: stand the target up on the profile's pinned build + seed its data.
  runDemoProfile: async (name: string, portal = 'innovateflow'): Promise<PromotionJob> => {
    const response = await apiClient.post('/demo-env/demo-profiles/run', { name, portal });
    return response.data;
  },
};

// Resize API (scale OpenSearch/Aurora/EKS)
export const resizeApi = {
  getConfig: async (params: { env: string }): Promise<any> => {
    const response = await apiClient.get('/resize', { params: { environment: params.env } });
    return response.data;
  },
  resize: async (params: { env: string; service: 'opensearch' | 'aurora' | 'eks'; config: Record<string, any> }): Promise<any> => {
    const response = await apiClient.post('/resize', {
      environment: params.env,
      service: params.service,
      config: params.config,
    });
    return response.data;
  },
};

// Ingestion API (Portal control — multi-datastore scheduling)
export const ingestionApi = {
  getStatus: async (params: { env: string; include_failed?: boolean }): Promise<any> => {
    const response = await apiClient.get('/ingestion', { params });
    return response.data;
  },
  run: async (params: { env: string; datastore?: string; options?: Record<string, any> }): Promise<any> => {
    const response = await apiClient.post('/ingestion', { ...params, action: 'run' });
    return response.data;
  },
  schedule: async (params: { env: string; schedule: string }): Promise<any> => {
    const response = await apiClient.post('/ingestion', { ...params, action: 'schedule' });
    return response.data;
  },
  stop: async (params: { env: string }): Promise<any> => {
    const response = await apiClient.post('/ingestion', { ...params, action: 'stop' });
    return response.data;
  },
  getDetails: async (params: { env: string; executionName: string }): Promise<any> => {
    const response = await apiClient.post('/ingestion', { ...params, action: 'details' });
    return response.data;
  },
  deleteData: async (params: { env: string; executionName?: string; executionNames?: string[] }): Promise<any> => {
    const response = await apiClient.post('/ingestion', { ...params, action: 'delete' });
    return response.data;
  },
  getLogs: async (params: {
    env: string;
    executionName?: string;
    executionArn?: string;
    nextToken?: string;
    limit?: number;
  }): Promise<any> => {
    const response = await apiClient.post('/ingestion', { ...params, action: 'logs' });
    return response.data;
  },
  updateSchedule: async (params: {
    env: string;
    enabled: boolean;
    scheduleExpression: string;
    region?: string;
    dataTypes?: string[];
    updatedOnly?: boolean;
    startDate?: string;
    endDate?: string;
    endDateCurrent?: boolean;
  }): Promise<any> => {
    const response = await apiClient.post('/ingestion', {
      ...params,
      action: 'updateSchedule'
    });
    return response.data;
  },
  // Old per-datastore schedule methods removed — use jobsApi.scheduleJob instead
  // Activity monitoring endpoints
  getActivity: async (params: { env: string }): Promise<any> => {
    const response = await apiClient.post('/ingestion', {
      action: 'activity',
      environment: params.env,
    });
    return response.data;
  },
  getActivityHistory: async (params: { env: string }): Promise<any> => {
    const response = await apiClient.post('/ingestion', {
      action: 'activity-history',
      environment: params.env,
    });
    return response.data;
  },
  getActivityResources: async (params: { env: string }): Promise<any> => {
    const response = await apiClient.post('/ingestion', {
      action: 'activity-resources',
      environment: params.env,
    });
    return response.data;
  },
  getJobsList: async (params: { env: string; limit?: number }): Promise<any> => {
    const response = await apiClient.post('/ingestion', {
      action: 'jobs-list',
      environment: params.env,
      limit: params.limit || 20,
    });
    return response.data;
  },
  getJobsProgress: async (params: { env: string; job_id: string }): Promise<any> => {
    const response = await apiClient.post('/ingestion', {
      action: 'jobs-progress',
      environment: params.env,
      job_id: params.job_id,
    });
    return response.data;
  },
  startJobProfile: async (params: { env: string; profile: string }): Promise<any> => {
    const response = await apiClient.post('/ingestion', {
      action: 'jobs-start',
      environment: params.env,
      profile: params.profile,
    });
    return response.data;
  },
  getSourcesForDatabase: async (database: string): Promise<{ database: string; sources: any[]; total: number }> => {
    const response = await apiClient.post('/ingestion', {
      action: 'sources-for-database',
      database,
    });
    return response.data;
  },
  getCollaboratorStats: async (params: { env: string }): Promise<any> => {
    const response = await apiClient.post('/ingestion', {
      action: 'collaborator-stats',
      environment: params.env,
    });
    return response.data;
  },
  getResourceBreakdown: async (params: { env: string }): Promise<any> => {
    const response = await apiClient.post('/ingestion', {
      action: 'activity-resources-breakdown',
      environment: params.env,
    });
    return response.data;
  },
};

// Ingestion Jobs API (dedicated Jobs Lambda — CRUD + lifecycle)
export interface IngestionJobConfig {
  target_database: string;
  target_type?: string;
  total_target_count?: number;
  sources?: Record<string, any>[];
  mode?: 'incremental' | 'bulk';
  focus_areas?: string[];
  cleanup_raw_data?: boolean;
  embedding_config?: { model_id?: string; dimensions?: number };
  metadata_fields?: string[];
  job_name?: string;
  schedule_expression?: string;
  schedule_enabled?: boolean;
  topic_proportions?: Record<string, number>;
  source_proportions?: Record<string, number>;
  status?: string;
}

export interface IngestionJob {
  job_id: string;
  target_database: string;
  target_type: string;
  status: 'PENDING' | 'RUNNING' | 'PAUSED' | 'COMPLETED' | 'FAILED' | 'CANCELLED' | 'SCHEDULED' | 'ON_HOLD' | 'DRAFT';
  mode: 'incremental' | 'bulk';
  total_target_count: number;
  progress_pct: number;
  sources: Record<string, any>[];
  created_at: string;
  updated_at: string;
  created_by?: string;
  job_name?: string;
  schedule_expression?: string;
  schedule_enabled?: boolean;
  topic_proportions?: Record<string, number>;
  source_proportions?: Record<string, number>;
  run_history?: RunRecord[];
}

export interface RunRecord {
  run_id: string;
  start_time: string;
  end_time: string;
  status: string;
  records_processed: number;
  records_failed: number;
  errors: string[];
  per_source_stats: Record<string, any>;
}

export const jobsApi = {
  createJob: async (config: IngestionJobConfig): Promise<IngestionJob> => {
    const response = await apiClient.post('/ingestion/jobs', config);
    return response.data;
  },
  getJob: async (jobId: string): Promise<IngestionJob> => {
    const response = await apiClient.get(`/ingestion/jobs/${jobId}`);
    return response.data;
  },
  listJobs: async (params?: { status?: string; database?: string }): Promise<{ jobs: IngestionJob[] }> => {
    const response = await apiClient.get('/ingestion/jobs', { params });
    return response.data;
  },
  updateJob: async (jobId: string, updates: Record<string, any>): Promise<IngestionJob> => {
    const response = await apiClient.put(`/ingestion/jobs/${jobId}`, updates);
    return response.data;
  },
  startJob: async (jobId: string): Promise<IngestionJob> => {
    const response = await apiClient.post(`/ingestion/jobs/${jobId}/start`);
    return response.data;
  },
  pauseJob: async (jobId: string): Promise<IngestionJob> => {
    const response = await apiClient.put(`/ingestion/jobs/${jobId}`, { action: 'pause' });
    return response.data;
  },
  cancelJob: async (jobId: string): Promise<IngestionJob> => {
    const response = await apiClient.put(`/ingestion/jobs/${jobId}`, { action: 'cancel' });
    return response.data;
  },
  resumeJob: async (jobId: string): Promise<IngestionJob> => {
    const response = await apiClient.put(`/ingestion/jobs/${jobId}`, { action: 'resume' });
    return response.data;
  },
  getJobProfiles: async (): Promise<any> => {
    const response = await apiClient.get('/ingestion/jobs', { params: { profiles: true } });
    return response.data;
  },
  scheduleJob: async (jobId: string, scheduleExpression: string): Promise<any> => {
    const response = await apiClient.post(`/ingestion/jobs/${jobId}/schedule`, { schedule_expression: scheduleExpression });
    return response.data;
  },
  holdJob: async (jobId: string): Promise<any> => {
    const response = await apiClient.post(`/ingestion/jobs/${jobId}/hold`);
    return response.data;
  },
  restartJob: async (jobId: string): Promise<any> => {
    const response = await apiClient.post(`/ingestion/jobs/${jobId}/restart`);
    return response.data;
  },
  deleteJob: async (jobId: string): Promise<any> => {
    const response = await apiClient.delete(`/ingestion/jobs/${jobId}`);
    return response.data;
  },
  getJobHistory: async (jobId: string, params?: { limit?: number; offset?: number }): Promise<any> => {
    const response = await apiClient.get(`/ingestion/jobs/${jobId}/history`, { params });
    return response.data;
  },
};

// Organizations API
export const organizationsApi = {
  list: async (): Promise<any> => {
    const response = await apiClient.get('/organizations');
    return response.data;
  },
  create: async (data: { name: string; description?: string }): Promise<any> => {
    const response = await apiClient.post('/organizations', data);
    return response.data;
  },
  get: async (id: string): Promise<any> => {
    const response = await apiClient.get(`/organizations/${id}`);
    return response.data;
  },
  update: async (id: string, data: { name?: string; description?: string; status?: string }): Promise<any> => {
    const response = await apiClient.put(`/organizations/${id}`, data);
    return response.data;
  },
  delete: async (id: string): Promise<any> => {
    const response = await apiClient.delete(`/organizations/${id}`);
    return response.data;
  },
};

// User Access API
export const userAccessApi = {
  get: async (userId: string): Promise<any> => {
    const response = await apiClient.get(`/users/${userId}/access`);
    return response.data;
  },
  update: async (userId: string, data: {
    organization_id?: string;
    datasets?: string[];
    admin?: boolean;
    analysis_tier?: string;
    can_save_views?: boolean;
    can_share_views?: boolean;
  }): Promise<any> => {
    const response = await apiClient.put(`/users/${userId}/access`, data);
    return response.data;
  },
};

// Multi-Pool User Management API
// `env` selects which AWS account a Cognito pool call runs against —
// 'management' (default, the Hub's own admin pool) or 'dev'/'test'
// (cross-account into the portal-user spokes, ADO #4583). listPools()
// already aggregates all three and tags each pool with its env; callers
// pass that same env back on every subsequent call for that pool.
export const userPoolApi = {
  listPools: async (): Promise<any> => {
    const response = await apiClient.get('/users/pools');
    return strictApiResponse(response.data, 'pools');
  },
  listGroups: async (poolId: string, env?: string): Promise<any> => {
    const response = await apiClient.get('/users/groups', {
      params: { pool_id: poolId, ...(env ? { env } : {}) },
    });
    return safeApiResponse(response.data, { groups: [] });
  },
  listUsers: async (poolId?: string, env?: string): Promise<any> => {
    const params: Record<string, string> = {};
    if (poolId) params.pool_id = poolId;
    if (env) params.env = env;
    const response = await apiClient.get('/users', {
      params: Object.keys(params).length ? params : undefined,
    });
    return safeApiResponse(response.data, { users: [] });
  },
  createUser: async (data: { email: string; group: string; send_email: boolean; pool_id?: string; env?: string }): Promise<any> => {
    const response = await apiClient.post('/users', data);
    return response.data;
  },
  updateUser: async (username: string, data: { group?: string; enabled?: boolean; email?: string; pool_id?: string; tier?: string; env?: string }): Promise<any> => {
    const response = await apiClient.put(`/users/${encodeURIComponent(username)}`, data);
    return response.data;
  },
  deleteUser: async (username: string, poolId?: string, env?: string): Promise<any> => {
    const params: Record<string, string> = {};
    if (poolId) params.pool_id = poolId;
    if (env) params.env = env;
    const response = await apiClient.delete(`/users/${encodeURIComponent(username)}`, {
      params: Object.keys(params).length ? params : undefined,
    });
    return response.data;
  },
  resetPassword: async (username: string, data: { send_email: boolean; pool_id?: string; env?: string }): Promise<any> => {
    const response = await apiClient.post(`/users/${encodeURIComponent(username)}/reset-password`, data);
    return response.data;
  },
  resendInvite: async (username: string, poolId?: string, env?: string): Promise<any> => {
    const response = await apiClient.post(`/users/${encodeURIComponent(username)}/resend-invite`, {
      pool_id: poolId,
      env,
    });
    return response.data;
  },
};

// Identity Lambda API (Story #5353 Stream 2-5, screens A02-A05). Calls the
// NEW lambda/dashboard/identity/ Lambda, coexisting with userPoolApi above
// (see ADR-019). Approval-gated writes (CTL-*-04 etc) take an optional
// approvalToken which is sent as X-Approval-Token, never in the body.
const APPROVAL_HEADER = 'X-Approval-Token';

export const identityApi = {
  // --- A02 Users ---
  createUser: async (data: { subject_id?: string; email: string; group: string }, approvalToken?: string): Promise<any> => {
    const response = await apiClient.post('/administration/identity/users', data, {
      headers: approvalToken ? { [APPROVAL_HEADER]: approvalToken } : undefined,
    });
    return response.data;
  },
  resetPassword: async (subjectId: string): Promise<any> => {
    const response = await apiClient.post(
      `/administration/identity/users/${encodeURIComponent(subjectId)}/reset-password`, {});
    return response.data;
  },
  suspendUser: async (subjectId: string, approvalToken?: string): Promise<any> => {
    const response = await apiClient.post(
      `/administration/identity/users/${encodeURIComponent(subjectId)}/suspend`, {},
      { headers: approvalToken ? { [APPROVAL_HEADER]: approvalToken } : undefined });
    return response.data;
  },
  reEnableUser: async (subjectId: string): Promise<any> => {
    const response = await apiClient.post(
      `/administration/identity/users/${encodeURIComponent(subjectId)}/re-enable`, {});
    return response.data;
  },

  // --- A03 Invitations ---
  listInvitations: async (): Promise<any> => {
    const response = await apiClient.get('/administration/identity/invitations');
    return safeApiResponse(response.data, { invitations: [] });
  },
  resendInvitation: async (subjectId: string): Promise<any> => {
    const response = await apiClient.post(
      `/administration/identity/invitations/${encodeURIComponent(subjectId)}/resend`, {});
    return response.data;
  },
  supersedeInvitation: async (subjectId: string): Promise<any> => {
    const response = await apiClient.post(
      `/administration/identity/invitations/${encodeURIComponent(subjectId)}/supersede`, {});
    return response.data;
  },

  // --- A04 Access ---
  explainAccess: async (subjectId: string): Promise<any> => {
    const response = await apiClient.get(
      `/administration/identity/access/${encodeURIComponent(subjectId)}/explain`);
    return response.data;
  },
  previewAccess: async (subjectId: string, proposed: Record<string, unknown>): Promise<any> => {
    const response = await apiClient.post(
      `/administration/identity/access/${encodeURIComponent(subjectId)}/preview`, { proposed });
    return response.data;
  },
  applyAccess: async (subjectId: string, proposed: Record<string, unknown>, approvalToken: string): Promise<any> => {
    const response = await apiClient.post(
      `/administration/identity/access/${encodeURIComponent(subjectId)}/apply`, { proposed },
      { headers: { [APPROVAL_HEADER]: approvalToken } });
    return response.data;
  },

  // --- A05 Reviews ---
  createReview: async (data: {
    population: string[]; scope: string; policy: string; evidence_cutoff: string; reviewer: string;
  }, approvalToken: string): Promise<any> => {
    const response = await apiClient.post('/administration/identity/reviews', data,
      { headers: { [APPROVAL_HEADER]: approvalToken } });
    return response.data;
  },
  getReview: async (reviewId: string): Promise<any> => {
    const response = await apiClient.get(`/administration/identity/reviews/${encodeURIComponent(reviewId)}`);
    return response.data;
  },
  decideReview: async (reviewId: string, data: {
    subject_id: string; capability: string; decision: 'attest' | 'revoke' | 'investigate'; reason: string;
  }): Promise<any> => {
    const response = await apiClient.post(
      `/administration/identity/reviews/${encodeURIComponent(reviewId)}/decide`, data);
    return response.data;
  },
  reassignReviewer: async (reviewId: string, reviewer: string): Promise<any> => {
    const response = await apiClient.post(
      `/administration/identity/reviews/${encodeURIComponent(reviewId)}/reassign`, { reviewer });
    return response.data;
  },
  closeReview: async (reviewId: string, approvalToken: string): Promise<any> => {
    const response = await apiClient.post(
      `/administration/identity/reviews/${encodeURIComponent(reviewId)}/close`, {},
      { headers: { [APPROVAL_HEADER]: approvalToken } });
    return response.data;
  },

  // --- P18-P21 Portal Access projections (read-only, no writer) ---
  getPortalAccessUsers: async (portal: string, env: string = 'dev'): Promise<any> => {
    const response = await apiClient.get(`/portal-access/${encodeURIComponent(portal)}/users`, { params: { env } });
    return response.data;
  },
  getPortalAccessOrganisations: async (portal: string, env: string = 'dev'): Promise<any> => {
    const response = await apiClient.get(`/portal-access/${encodeURIComponent(portal)}/organisations`,
      { params: { env } });
    return response.data;
  },
  getPortalAccessInvitations: async (portal: string, env: string = 'dev'): Promise<any> => {
    const response = await apiClient.get(`/portal-access/${encodeURIComponent(portal)}/invitations`,
      { params: { env } });
    return response.data;
  },
  getPortalAccessServiceIdentities: async (portal: string, env: string = 'dev'): Promise<any> => {
    const response = await apiClient.get(`/portal-access/${encodeURIComponent(portal)}/service-identities`,
      { params: { env } });
    return response.data;
  },

  // --- Single-operator approvals (Story #5353 Stream 15, decision #5284) ---
  // Two SEPARATE calls, never one: request an approval bound to the exact
  // control_id + payload, then (as a genuinely separate action) approve it.
  // The resulting request_id is sent as X-Approval-Token on the actual
  // governed-write call (see APPROVAL_HEADER above).
  requestApproval: async (controlId: string, payload: Record<string, unknown>): Promise<any> => {
    const response = await apiClient.post('/administration/identity/approvals/request',
      { control_id: controlId, payload });
    return response.data;
  },
  approveRequest: async (requestId: string, controlId: string, payload: Record<string, unknown>): Promise<any> => {
    const response = await apiClient.post(`/administration/identity/approvals/${encodeURIComponent(requestId)}/approve`,
      { control_id: controlId, payload });
    return response.data;
  },
  revokeApprovalRequest: async (requestId: string): Promise<any> => {
    const response = await apiClient.post(`/administration/identity/approvals/${encodeURIComponent(requestId)}/revoke`, {});
    return response.data;
  },

  // --- A06 Service Identities ---
  createServiceIdentity: async (data: {
    name: string; owner: string; purpose: string; capability: string; rotation_days: number;
  }): Promise<any> => {
    const response = await apiClient.post('/administration/identity/service-identities/create', data);
    return response.data;
  },
  testServiceIdentityAccess: async (name: string): Promise<any> => {
    const response = await apiClient.post('/administration/identity/service-identities/test-effective-access',
      { name });
    return response.data;
  },
  planServiceIdentityRotation: async (name: string, rotationDays: number): Promise<any> => {
    const response = await apiClient.post('/administration/identity/service-identities/plan-rotation',
      { name, rotation_days: rotationDays });
    return response.data;
  },
  previewServiceIdentityDisableImpact: async (name: string): Promise<any> => {
    const response = await apiClient.post(
      '/administration/identity/service-identities/preview-disable-impact', { name });
    return response.data;
  },
  disableRevokeServiceIdentity: async (name: string, confirmedMigration: boolean): Promise<any> => {
    const response = await apiClient.post('/administration/identity/service-identities/disable-revoke',
      { name, confirmed_migration: confirmedMigration });
    return response.data;
  },

  // --- A07 Providers ---
  testProviders: async (poolName?: string): Promise<any> => {
    const response = await apiClient.post('/administration/identity/providers/test',
      poolName ? { pool_name: poolName } : {});
    return response.data;
  },
  compareProviderRegistry: async (): Promise<any> => {
    const response = await apiClient.post('/administration/identity/providers/compare', {});
    return response.data;
  },
  proposeProviderMapping: async (poolName: string, proposedChanges: Record<string, unknown>): Promise<any> => {
    const response = await apiClient.post('/administration/identity/providers/propose-mapping',
      { pool_name: poolName, proposed_changes: proposedChanges });
    return response.data;
  },
  reconcileProvider: async (poolName: string): Promise<any> => {
    const response = await apiClient.post('/administration/identity/providers/reconcile', { pool_name: poolName });
    return response.data;
  },

  // --- M01 My Account Profile ---
  getProfile: async (): Promise<any> => {
    const response = await apiClient.get('/my-account/profile');
    return response.data;
  },
  editProfile: async (proposed: Record<string, unknown>): Promise<any> => {
    const response = await apiClient.post('/my-account/profile/edit', { proposed });
    return response.data;
  },
  saveProfile: async (proposed: Record<string, unknown>): Promise<any> => {
    const response = await apiClient.post('/my-account/profile/save', { proposed });
    return response.data;
  },
  cancelProfileEdit: async (): Promise<any> => {
    const response = await apiClient.post('/my-account/profile/cancel', {});
    return response.data;
  },
  openOrganisationAdmin: async (): Promise<any> => {
    const response = await apiClient.post('/my-account/profile/organisation-admin', {});
    return response.data;
  },

  // --- M02 My Account Security ---
  getSecurity: async (): Promise<any> => {
    const response = await apiClient.get('/my-account/security');
    return response.data;
  },
  changePassword: async (currentPassword: string, newPassword: string): Promise<any> => {
    const response = await apiClient.post('/my-account/security/change-password',
      { current_password: currentPassword, new_password: newPassword });
    return response.data;
  },
  mfaEnrolStart: async (): Promise<any> => {
    const response = await apiClient.post('/my-account/security/mfa/enrol-start', {});
    return response.data;
  },
  mfaEnrolVerify: async (userCode: string, deviceName?: string): Promise<any> => {
    const response = await apiClient.post('/my-account/security/mfa/enrol-verify',
      { user_code: userCode, device_name: deviceName });
    return response.data;
  },
  mfaRemove: async (): Promise<any> => {
    const response = await apiClient.post('/my-account/security/mfa/remove', {});
    return response.data;
  },
  securityHelp: async (): Promise<any> => {
    const response = await apiClient.post('/my-account/security/help', {});
    return response.data;
  },

  // --- M03 My Account Sessions ---
  getSessions: async (): Promise<any> => {
    const response = await apiClient.get('/my-account/sessions');
    return response.data;
  },
  revokeSession: async (deviceKey: string): Promise<any> => {
    const response = await apiClient.post('/my-account/sessions/revoke', { device_key: deviceKey });
    return response.data;
  },
  signOutAllSessions: async (): Promise<any> => {
    const response = await apiClient.post('/my-account/sessions/sign-out-all', {});
    return response.data;
  },
  refreshSessions: async (): Promise<any> => {
    const response = await apiClient.post('/my-account/sessions/refresh', {});
    return response.data;
  },
  sessionSecurityEvent: async (): Promise<any> => {
    const response = await apiClient.post('/my-account/sessions/security-event', {});
    return response.data;
  },

  // --- M05 My Account Notifications ---
  getNotificationSettings: async (): Promise<any> => {
    const response = await apiClient.get('/my-account/notifications');
    return response.data;
  },
  toggleNotificationSubscription: async (eventType: string, enabled: boolean): Promise<any> => {
    const response = await apiClient.post('/my-account/notifications/subscription',
      { event_type: eventType, enabled });
    return response.data;
  },
  verifyChannelStart: async (channel: 'email' | 'sms'): Promise<any> => {
    const response = await apiClient.post('/my-account/notifications/verify-start', { channel });
    return response.data;
  },
  verifyChannelConfirm: async (channel: 'email' | 'sms', code: string): Promise<any> => {
    const response = await apiClient.post('/my-account/notifications/verify-confirm', { channel, code });
    return response.data;
  },
  setQuietHours: async (start: string, end: string): Promise<any> => {
    const response = await apiClient.post('/my-account/notifications/quiet-hours', { start, end });
    return response.data;
  },
  openAlertPolicy: async (): Promise<any> => {
    const response = await apiClient.post('/my-account/notifications/alert-policy', {});
    return response.data;
  },

  // --- M06 My Account Effective Access ---
  explainMyAccess: async (): Promise<any> => {
    const response = await apiClient.get('/my-account/effective-access');
    return response.data;
  },
  openPortalFromAccess: async (): Promise<any> => {
    const response = await apiClient.post('/my-account/open-portal', {});
    return response.data;
  },
  requestAccessChange: async (): Promise<any> => {
    const response = await apiClient.post('/my-account/request-change', {});
    return response.data;
  },
  exportMyAccess: async (): Promise<any> => {
    const response = await apiClient.post('/my-account/export', {});
    return response.data;
  },

  // --- M04 My Account Preferences ---
  getPreferences: async (): Promise<any> => {
    const response = await apiClient.get('/my-account/preferences');
    return response.data;
  },
  previewPreferences: async (proposed: Record<string, unknown>): Promise<any> => {
    const response = await apiClient.post('/my-account/preferences/preview', { proposed });
    return response.data;
  },
  savePreferences: async (proposed: Record<string, unknown>): Promise<any> => {
    const response = await apiClient.post('/my-account/preferences/save', { proposed });
    return response.data;
  },
  resetPreferences: async (): Promise<any> => {
    const response = await apiClient.post('/my-account/preferences/reset', {});
    return response.data;
  },
  chooseDefaultView: async (view: string): Promise<any> => {
    const response = await apiClient.post('/my-account/preferences/default-view', { view });
    return response.data;
  },
};

// User Settings Types
export interface UserSettings {
  displayName?: string;
  email?: string;
  theme?: 'purple' | 'dark' | 'light' | 'contrast' | 'green';
  timezone?: string;
  dateFormat?: string;
  defaultTimeRange?: string;
  exportFormat?: 'json' | 'csv';
  notifications?: {
    anomalyAlerts?: boolean;
    dailyReports?: boolean;
    weeklySummaries?: boolean;
    systemAlerts?: boolean;
  };
}

export interface SettingsResponse {
  success: boolean;
  data: UserSettings;
  message?: string;
}

// Environment Colors (Task 1.1)
export const ENV_COLORS = {
  dev: { primary: '#2563eb', bg: '#dbeafe', text: '#1d4ed8' },
  test: { primary: '#f97316', bg: '#ffedd5', text: '#c2410c' },
  uat: { primary: '#7c3aed', bg: '#ede9fe', text: '#6d28d9' },
  prod: { primary: '#16a34a', bg: '#dcfce7', text: '#15803d' },
  shared: { primary: '#0891b2', bg: '#cffafe', text: '#0e7490' },
} as const;

// Health/Metrics Types (Task 1.1)
export interface ComponentHealth {
  status: 'healthy' | 'warning' | 'critical' | 'no_data' | 'not_found' | 'stopped' | 'error' | 'unknown';
  latency_p99?: number;
  error_rate?: number;
  invocations?: number;
  duration_avg?: number;
  cpu?: number;
  memory?: number;
  connections?: number;
  deployed?: boolean;
  [key: string]: any;
}

export interface HealthResponse {
  version: string;
  environment: string;
  /**
   * The AWS account the health checks actually read (Bug #5876 follow-up).
   *
   * The health Lambda assumes ONE role (MONITOR_ROLE_ARN) into a single spoke,
   * so the `environment` parameter changes only which resource NAMES it looks
   * for — never which account it looks in. When this does not match the
   * selected environment's account, every "unknown" means "we never looked in
   * the right account", not "we looked and could not tell". Callers must not
   * present the reading as that environment's.
   */
  monitored_account?: string;
  overall_status: 'healthy' | 'warning' | 'critical' | 'unknown';
  components: {
    api_gateway?: ComponentHealth;
    lambda?: ComponentHealth;
    opensearch?: ComponentHealth;
    step_functions?: ComponentHealth;
    aurora?: ComponentHealth;
    eks?: ComponentHealth;
    dynamodb?: ComponentHealth;
    cognito?: ComponentHealth;
    cloudfront?: ComponentHealth;
    s3?: ComponentHealth;
  };
  timestamp: string;
}

export interface AlertItem {
  id: string;
  name: string;
  severity: 'critical' | 'warning' | 'info' | 'resolved';
  state: string;
  message: string;
  metric?: string;
  namespace?: string;
  threshold?: number;
  timestamp: string;
  state_reason?: string;
}

export interface AlertsResponse {
  version: string;
  environment: string;
  alerts: AlertItem[];
  /**
   * The AWS account the alarms were actually read from (Bug #5876 follow-up).
   *
   * Critical for honesty here: an empty `alerts` array renders as "no alerts",
   * i.e. all clear. Measured 2026-09-06, asking for environment=test searched
   * the DEV account for `secureembed-test-*` alarms and returned zero, while
   * the Test account genuinely held 14 — including
   * secureembed-test-tier1a-api-errors. A firing alarm would have been
   * invisible. When this does not match the environment's account, an empty
   * list means NOT MONITORED, never all-clear.
   */
  monitored_account?: string;
  summary: {
    critical: number;
    warning: number;
    info: number;
    resolved: number;
    total_active: number;
  };
  timestamp: string;
  /**
   * True only on getAlerts' own fallback path (the real request failed and
   * this is a synthesized empty response, not a genuine zero from the
   * source). Optional and absent on every real response, so existing
   * callers that don't check it are unaffected — but a caller that DOES
   * check it can tell "the estate has no alerts" apart from "we could not
   * ask" instead of rendering both as the same clean "no alerts" (DoD-07).
   */
  sourceUnreachable?: boolean;
}

export interface LogEntry {
  timestamp: string;
  message: string;
  level?: 'info' | 'warn' | 'error';
  source?: string;
}

export interface LogsResponse {
  version: string;
  environment: string;
  logs: LogEntry[];
  next_token?: string;
  timestamp: string;
}

// GET /logs/{buildId} — CodeBuild build status + log tail (per-env routed).
export interface BuildLogsResponse {
  id: string;
  status: string;
  start_time?: string | null;
  end_time?: string | null;
  current_phase?: string;
  phases?: Array<{
    name?: string;
    status?: string;
    start_time?: string | null;
    end_time?: string | null;
    duration?: number;
  }>;
  logs?: Array<{ timestamp?: string; message: string; level?: string; action?: string }>;
  next_token?: string;
  log_error?: string;
  log_message?: string;
  version?: string;
}

// Helper to safely handle API responses that might be HTML error pages
const safeApiResponse = <T>(data: any, fallback: T): T => {
  if (!data) return fallback;
  if (typeof data === 'string') {
    if (data.trim().startsWith('<!DOCTYPE') || data.trim().startsWith('<html')) {
      console.warn('API returned HTML instead of JSON - environment may not be deployed');
      return fallback;
    }
    try {
      return JSON.parse(data);
    } catch {
      return fallback;
    }
  }
  return data;
};

// Strict version that throws on error responses instead of returning fallback
const strictApiResponse = <T>(data: any, label: string): T => {
  if (!data) {
    throw new Error(`No response from ${label} API`);
  }
  if (typeof data === 'string') {
    if (data.trim().startsWith('<!DOCTYPE') || data.trim().startsWith('<html')) {
      throw new Error(`${label} API returned HTML — environment may not be deployed`);
    }
    try {
      const parsed = JSON.parse(data);
      if (parsed.error) {
        throw new Error(parsed.error);
      }
      return parsed;
    } catch (e) {
      if (isScopeCancellation(e)) throw e;
      if (e instanceof SyntaxError) {
        throw new Error(`${label} API returned invalid JSON`);
      }
      throw e;
    }
  }
  if (data.error) {
    throw new Error(data.error);
  }
  return data;
};

// Metrics API (Task 1.1) - Combines health, alerts, logs
export const metricsApi = {
  /**
   * Get health metrics for an environment
   */
  getHealth: async (
    environment: string | null | undefined = undefined,
    component?: string,
    options?: { duration?: string; includeSeries?: boolean; signal?: AbortSignal }
  ): Promise<HealthResponse> => {
    environment = requireConcreteEnvironment(environment);
    const fallback: HealthResponse = {
      version: '14.4.0',
      environment,
      overall_status: 'unknown',
      components: {},
      timestamp: new Date().toISOString()
    };
    try {
      const params: Record<string, string> = { environment };
      if (component) params.component = component;
      if (options?.duration) params.duration = options.duration;
      if (options?.includeSeries) params.include_series = 'true';
      const response = await apiClient.get<HealthResponse>('/health', { params, signal: options?.signal });
      return safeApiResponse(response.data, fallback);
    } catch (err: any) {
      if (isScopeCancellation(err)) throw err;
      // Return fallback on error instead of throwing
      console.warn('Health API error:', err?.message);
      return fallback;
    }
  },

  /**
   * Get alerts for an environment
   */
  getAlerts: async (environment: string | null | undefined = undefined, action: string = 'list'): Promise<AlertsResponse> => {
    environment = requireConcreteEnvironment(environment);
    const fallback: AlertsResponse = {
      version: '14.4.0',
      environment,
      alerts: [],
      summary: { critical: 0, warning: 0, info: 0, resolved: 0, total_active: 0 },
      timestamp: new Date().toISOString(),
      sourceUnreachable: true,
    };
    try {
      const response = await apiClient.get<AlertsResponse>('/alerts', {
        params: { environment, action },
      });
      // safeApiResponse falls back on an HTML/unparseable body too (a 200
      // that isn't real data) — that is exactly as "we could not really
      // read this" as a thrown request error, so it gets the same honest
      // flag rather than being indistinguishable from a genuine zero.
      return safeApiResponse(response.data, fallback);
    } catch (err: any) {
      if (isScopeCancellation(err)) throw err;
      console.warn('Alerts API error:', err?.message);
      return fallback;
    }
  },

  /**
   * Get alert summary counts
   */
  getAlertSummary: async (environment: string | null | undefined = undefined): Promise<AlertsResponse> => {
    const response = await apiClient.get<AlertsResponse>('/alerts', {
      params: { environment, action: 'summary' },
    });
    return response.data;
  },

  /**
   * Acknowledge an alert
   */
  acknowledgeAlert: async (alertId: string, user?: string): Promise<{ success: boolean; message?: string }> => {
    const response = await apiClient.post('/alerts', {
      action: 'acknowledge',
      alert_id: alertId,
      user,
    });
    return response.data;
  },

  /**
   * Get CloudWatch logs for an environment
   */
  getLogs: async (environment: string | null | undefined = undefined, logGroup?: string, limit: number = 50): Promise<LogsResponse> => {
    environment = requireConcreteEnvironment(environment);
    const response = await apiClient.get<LogsResponse>('/logs', {
      params: { environment, log_group: logGroup, limit },
    });
    return response.data;
  },

  /**
   * Get a CodeBuild build's status + log tail (GET /logs/{buildId}).
   * `environment` routes the read to that spoke's account — promotion builds
   * run in the TARGET spoke's own terraform-executor, which the default
   * (dev-pinned) monitor role cannot see.
   */
  getBuildLogs: async (
    buildId: string,
    environment?: string,
    limit: number = 100,
  ): Promise<BuildLogsResponse> => {
    const response = await apiClient.get<BuildLogsResponse>(
      `/logs/${encodeURIComponent(buildId)}`,
      { params: { ...(environment ? { environment } : {}), limit } },
    );
    return response.data;
  },

  /**
   * Get audit trail chain health across all portals
   */
  getAuditHealth: async (): Promise<any> => {
    try {
      const response = await apiClient.get('/audit/health');
      return response.data;
    } catch {
      return {
        portals: [],
        total_events: 0,
      };
    }
  },
};

// Scholarly Research API
export const scholarApi = {
  /**
   * Search scholarly papers
   */
  search: async (params: {
    query: string;
    sources?: string[];
    limit?: number;
    offset?: number;
    filters?: Record<string, any>;
  }): Promise<any> => {
    const response = await apiClient.post('/scholar/search', params);
    return response.data;
  },

  /**
   * Get a specific paper by ID
   */
  getPaper: async (paperId: string): Promise<any> => {
    const response = await apiClient.get(`/scholar/papers/${paperId}`);
    return response.data;
  },

  /**
   * Get faceted search results for scholarly data
   */
  faceted: async (params: {
    field: string;
    query?: string;
    source?: string;
  }): Promise<any> => {
    const response = await apiClient.get('/scholar/facets', { params });
    return response.data;
  },

  /**
   * Get paper recommendations based on a paper ID
   */
  recommend: async (paperId: string, limit?: number): Promise<any> => {
    const response = await apiClient.get(`/scholar/recommend/${paperId}`, {
      params: { limit: limit || 10 },
    });
    return response.data;
  },

  /**
   * Get paper recommendations based on a text query
   */
  recommendFromQuery: async (query: string, limit?: number): Promise<any> => {
    const response = await apiClient.post('/scholar/recommend', {
      query,
      limit: limit || 10,
    });
    return response.data;
  },

  /**
   * Get scholarly analytics (citation trends, publication counts, etc.)
   */
  analytics: async (params?: {
    time_range?: string;
    source?: string;
    metric?: string;
  }): Promise<any> => {
    const response = await apiClient.get('/scholar/analytics', { params });
    return response.data;
  },

  /**
   * Export scholarly data
   */
  exportData: async (params: {
    format: 'json' | 'csv' | 'bibtex';
    query?: string;
    paper_ids?: string[];
    max_records?: number;
  }): Promise<any> => {
    const response = await apiClient.post('/scholar/export', params);
    return response.data;
  },

  /**
   * Get or manage scholarly alerts
   */
  alerts: async (params?: {
    action?: 'list' | 'create' | 'delete';
    alert_id?: string;
    query?: string;
    frequency?: 'daily' | 'weekly';
  }): Promise<any> => {
    if (params?.action === 'create') {
      const response = await apiClient.post('/scholar/alerts', params);
      return response.data;
    } else if (params?.action === 'delete' && params?.alert_id) {
      const response = await apiClient.delete(`/scholar/alerts/${params.alert_id}`);
      return response.data;
    }
    const response = await apiClient.get('/scholar/alerts', { params });
    return response.data;
  },
};

// Datastore Infrastructure - live query from ingestion Lambda
export interface DatastoreInfo {
  records: number;
  dataSize: string;
  rawFiles: number;
  rawSize: string;
  timeSpan: string;
  description: string;
  lastUpdated: string | null;
  schedule: string;
  status: 'active' | 'pending' | 'inactive';
  index: string;
}

export interface OpenSearchStorage {
  total_gb: number;
  used_gb: number | null;
  free_gb: number | null;
  percent_used: number | null;
}

export const datastoreApi = {
  getStats: async (): Promise<{
    success: boolean;
    data: {
      record_counts: Record<string, number>;
      datasource_details: Record<string, DatastoreInfo>;
      total_records: number;
      total_datasources: number;
      opensearch_storage?: OpenSearchStorage;
    };
  }> => {
    const response = await apiClient.post('/ingestion', { action: 'datastores' });
    return response.data;
  },
};

// ---------------------------------------------------------------------------
// Support Tickets API (Management Hub — Story #4613, Stream 9 / #4637)
//
// Read-mostly cross-portal support view. Reads return the HUB-SAFE projection
// (public-only: internal notes/attachments never appear). The write surface is
// EXACTLY status/priority PATCH (conditional on version), a public text reply,
// and Retry sync now — matching the hub Lambda's least-privilege boundary.
// ---------------------------------------------------------------------------
export interface SupportEvent {
  seq: number;
  event_type: string;
  actor: string;
  visibility: string;
  created_at: string;
  body?: Record<string, unknown>;
}

export interface SupportAttachment {
  attachment_id: string;
  filename?: string;
  content_type?: string;
  size_bytes?: number;
  visibility?: string;
  scan_status?: string;
  created_at?: string;
  is_video?: boolean;
}

export interface SupportTicket {
  ticket_id: string;
  ref?: string;
  subject?: string;
  status?: string;
  priority?: string;
  portal?: string;
  request_type?: string;
  user_id?: string;
  assignee?: string | null;
  created_at?: string;
  updated_at?: string;
  age_seconds?: number | null;
  sync_status?: string;
  ado_id?: string;
  ado_url?: string;
  duplicate?: boolean;
  duplicate_of?: string;
  escalated?: boolean;
  last_escalated_at?: string;
  security_review?: boolean;
  version?: number;
  events?: SupportEvent[];
  attachments?: SupportAttachment[];
  failed_syncs?: { outbox_id: string; op?: string; last_error?: string }[];
  /** C07 (Story #5484 Stream 5): set once this ticket is escalated to an
   *  Operations incident — one ticket links exactly one incident, never a
   *  second/duplicate link. */
  linked_incident_ref?: string | null;
  escalated_to_operations_at?: string | null;
}

export interface SupportStats {
  open: number;
  unassigned: number;
  median_first_response_hours: number | null;
  aging_over_7d: number;
  resolved_30d: number;
  sync_failures: number;
}

export interface SupportListResponse {
  tickets: SupportTicket[];
  stats: SupportStats;
  source?: { account: string; environment: string; portals: string[] };
}

export const supportApi = {
  /** Hub queue + live tiles (all numbers from real queue-index queries). */
  list: async (): Promise<SupportListResponse> => {
    const response = await apiClient.get<SupportListResponse>('/support/tickets');
    return response.data;
  },
  /** Hub-originated audit ticket (Admins only) — records an admin action such
   *  as a credential email so it is visible in the Support queue. */
  createTicket: async (data: {
    subject: string;
    description: string;
    user_id?: string;
    portal?: string;
    request_type?: string;
  }): Promise<{ ticket_id: string; ref: string; status: string }> => {
    const response = await apiClient.post('/support/tickets', data);
    return response.data;
  },
  /** Hub-safe ticket detail (public-only thread + attachments). */
  get: async (ticketId: string): Promise<SupportTicket> => {
    const response = await apiClient.get<SupportTicket>(`/support/tickets/${ticketId}`);
    return response.data;
  },
  /** Public text reply (text-only — no internal notes / uploads from the hub). */
  reply: async (ticketId: string, message: string): Promise<{ ok: boolean }> => {
    const response = await apiClient.post(`/support/tickets/${ticketId}/reply`, { message });
    return response.data;
  },
  /** status/priority PATCH, conditional on the loaded version (optimistic lock). */
  patch: async (
    ticketId: string,
    changes: { status?: string; priority?: string; version: number },
  ): Promise<{ ok: boolean; status?: string; priority?: string; version: number }> => {
    const response = await apiClient.patch(`/support/tickets/${ticketId}`, changes);
    return response.data;
  },
  /** Retry sync now — reset a FAILED outbox row to pending. */
  retrySync: async (ticketId: string, outboxId: string): Promise<{ ok: boolean }> => {
    const response = await apiClient.post(`/support/tickets/${ticketId}/retry-sync`, {
      outbox_id: outboxId,
    });
    return response.data;
  },
  /** C07 (Story #5484 Stream 5): link this ticket to ONE existing Operations
   *  incident-chronology reference — never creates a second link on the same
   *  ticket (idempotent on the same ref, 409 on a different one). This does
   *  NOT declare a new incident; Operations has no writable incident record
   *  type yet (see dashboard/src/pages/operations/Incidents.tsx). */
  escalateToOperations: async (
    ticketId: string,
    incidentRef: string,
  ): Promise<{ ok: boolean; linked_incident_ref: string; escalated_to_operations_at: string; already_linked: boolean }> => {
    const response = await apiClient.post(`/support/tickets/${ticketId}/escalate-to-operations`, {
      incident_ref: incidentRef,
    });
    return response.data;
  },
};

// --------------------------------------------------------------------------
// C08 — Customers > Access projection (Story #5484 Stream 5, Task #5544)
// --------------------------------------------------------------------------
export interface AccessProjectionRow {
  subject: string;
  subject_id?: string;
  kind: 'human' | 'service';
  portal: string;
  environment: string;
  role: string;
  source: string;
  expiry?: string | null;
  access_state: string;
  open_in_identity_path: string;
}

export interface AccessProjectionResponse {
  data: AccessProjectionRow[];
  scope: { organisationId: string; organisationName?: string | null; portal: string };
  source: string;
  observedAt: string;
  status: 'known' | 'empty' | 'unknown' | 'unreachable' | 'stale' | 'unauthorised';
  completeness: number;
  confidence: number;
  scope_notes?: string[];
}

export const customersAccessApi = {
  /** Read-only effective-access projection for one customer organisation.
   *  Every "change access" affordance deep-links to Administration/Identity
   *  via each row's open_in_identity_path — this client never grants/revokes
   *  anything itself. */
  getOrganisationAccess: async (organisationId: string): Promise<AccessProjectionResponse> => {
    const response = await apiClient.get<AccessProjectionResponse>(
      '/customers/access-projection',
      { params: { organisationId } },
    );
    return response.data;
  },
};

// User Settings API (Task 1.3)
export const settingsApi = {
  /**
   * Get current user's settings
   */
  getSettings: async (): Promise<SettingsResponse> => {
    const response = await apiClient.get<SettingsResponse>('/settings');
    return response.data;
  },

  /**
   * Update user settings
   */
  updateSettings: async (settings: Partial<UserSettings>): Promise<SettingsResponse> => {
    const response = await apiClient.put<SettingsResponse>('/settings', settings);
    return response.data;
  },

  /**
   * Get available themes
   */
  getThemes: async (): Promise<{ success: boolean; data: { themes: string[] } }> => {
    const response = await apiClient.get('/settings/themes');
    return response.data;
  },
};

// Browse API Types
export interface BrowseParams {
  query?: string;
  page: number;
  pageSize: number;
  sortField?: string;
  sortOrder?: 'asc' | 'desc';
  filters?: Record<string, string[]>;
  facetFields?: string[];
  searchAfter?: any[];
}

export interface BrowseField {
  name: string;
  type: string;
  keyword: boolean;
  sortable: boolean;
  large: boolean;
}

export interface FacetBucket {
  value: string;
  count: number;
}

export interface BrowseResult {
  results: Record<string, any>[];
  total: number;
  searchAfter?: any[];
  facets?: Record<string, FacetBucket[]>;
}

// Partner API client (separate base URL for partner service)
const getPartnerApiUrl = (): string => {
  const config = (window as any).SECUREEMBED_CONFIG;
  if (config?.PARTNER_API_ENDPOINT) {
    return config.PARTNER_API_ENDPOINT;
  }
  // Fall back to main API
  return getApiBaseUrl();
};

const partnerApiClient: AxiosInstance = axios.create({
  timeout: API_TIMEOUT,
  headers: { 'Content-Type': 'application/json' },
});

partnerApiClient.interceptors.request.use((config) => {
  config.baseURL = getPartnerApiUrl();
  const token = localStorage.getItem(STORAGE_KEYS.ID_TOKEN) || localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Story #5346 (Launch W4) Stream 4b -- typed shape for the P23/P24/A08
// cost-truth screens. costsApi.getCosts keeps its existing `any` return
export interface BillingFx {
  pair: 'AUD/USD'; usd_per_aud: string; rate_date: string; source: string; retrieved_at: string; conversion?: string;
}
export interface BillingSourceState {
  status: 'known' | 'stale' | 'unavailable'; complete: boolean; estimated?: boolean;
  retrieved_at?: string; coverage_end_exclusive?: string; collection_started_at?: string; published_at?: string;
  age_seconds?: number; generation?: string; last_attempt?: unknown; account_coverage?: unknown;
  coverage_kind?: string; organization_total_complete?: boolean;
  unmapped_organization_cost?: { status: string; amount: number | null }; budget_headroom?: number | null; shared_attribution?: unknown;
}
export interface BillingForecast {
  forecast_total: number | null; forecast_unit: 'AUD'; status: 'known' | 'stale' | 'unavailable'; reason?: string;
  amount?: string; currency?: string; start?: string; end?: string; retrieved_at?: string; metric?: string; fx?: BillingFx;
}
export interface CostsOverviewResponse {
  version: string; action?: string; period: string; environment: string; account_ids: string[];
  start_date: string; end_date: string; total_cost: number; daily_average: number;
  currency: 'AUD'; basis: 'unblended'; source_metric: 'UnblendedCost'; native_totals: Record<string, string>;
  tax_treatment?: string; fx: BillingFx; fxRate?: number; fxRateSource?: string; confidence: number | null;
  daily_costs: Array<{ date: string; cost: number }>; service_costs: Array<{ service: string; cost: number }>;
  account_service_costs?: Array<{account_id: string; service: string; cost: number}>;
  forecast: BillingForecast; source_state: BillingSourceState; timestamp: string;
  error?: string; unprovisioned?: boolean; message?: string;
}
export interface CostsDrilldownResponse extends CostsOverviewResponse {
  date?: string; service?: string; services?: Array<{service: string; cost: number}>; total?: number;
}
function billingEnvironment(environment?: string): string {
  if (!environment?.trim()) throw new Error('Select an explicit registered billing scope');
  return environment;
}
function checkedBilling(data: CostsOverviewResponse): CostsOverviewResponse {
  if (data?.currency !== 'AUD' || data?.basis !== 'unblended' || !data?.source_state?.complete
    || !['known', 'stale'].includes(data.source_state.status) || !Number.isFinite(data.total_cost)
    || !Number.isFinite(data.daily_average) || !Array.isArray(data.daily_costs) || !Array.isArray(data.service_costs)
    || data.daily_costs.some(row => !Number.isFinite(row.cost)) || data.service_costs.some(row => !Number.isFinite(row.cost))
    || !data.forecast || !data.fx || !Array.isArray(data.account_ids)
    || (data.account_service_costs!==undefined&&(!Array.isArray(data.account_service_costs)||data.account_service_costs.some(row=>!row||typeof row.account_id!=='string'||!data.account_ids.includes(row.account_id)||typeof row.service!=='string'||!Number.isFinite(row.cost))))) {
    throw new Error(data?.error || 'Complete cached billing observation unavailable');
  }
  return data;
}
function billingDates(params: Record<string,string>, period: string, startDate?: string, endDate?: string): void {
  if (!!startDate !== !!endDate) throw new Error('Both custom date boundaries are required');
  if (startDate && endDate) { params.start_date = startDate; params.end_date = endDate; }
  else params.period = period;
}
/** Cached billing reads only. All is the configured registered account union. */
export const costsApi = {
  getCosts: async (period = '30d', startDate?: string, endDate?: string, environment?: string, signal?: AbortSignal): Promise<CostsOverviewResponse> => {
    const params: Record<string,string> = {environment: billingEnvironment(environment)};
    billingDates(params, period, startDate, endDate);
    const response = await apiClient.get<CostsOverviewResponse>('/costs', {params, signal});
    return checkedBilling(response.data);
  },
  getDailyServices: async (date: string, environment?: string, signal?: AbortSignal): Promise<CostsDrilldownResponse> => {
    const params = {action:'daily_services', date, environment:billingEnvironment(environment)};
    const response = await apiClient.get<CostsDrilldownResponse>('/costs', {params, signal});
    checkedBilling(response.data); return response.data;
  },
  getServiceTrend: async (service: string, period = '30d', environment?: string, startDate?: string, endDate?: string, signal?: AbortSignal): Promise<CostsDrilldownResponse> => {
    const params: Record<string,string> = {action:'service_trend', service, environment:billingEnvironment(environment)};
    billingDates(params, period, startDate, endDate);
    const response = await apiClient.get<CostsDrilldownResponse>('/costs', {params, signal});
    checkedBilling(response.data); return response.data;
  },
};

// Datastores not yet ingested (browse disabled)
const EMPTY_BROWSE_DATASTORES: string[] = [];

// Browse API - unified record browsing via ingestion Lambda → OpenSearch
export const browseApi = {
  /**
   * Browse records in a datastore with pagination, sorting, filtering
   */
  browse: async (datastore: string, params: BrowseParams): Promise<BrowseResult> => {
    // Empty datastores return immediately
    if (EMPTY_BROWSE_DATASTORES.includes(datastore)) {
      return { results: [], total: 0 };
    }

    // Route through ingestion Lambda which queries OpenSearch directly
    const body: Record<string, any> = {
      action: 'browse',
      datastore,
      query: params.query || '',
      page: params.page,
      pageSize: params.pageSize,
      sortField: params.sortField,
      sortOrder: params.sortOrder,
    };
    if (params.searchAfter) {
      body.searchAfter = params.searchAfter;
    }
    if (params.filters && Object.keys(params.filters).length > 0) {
      body.filters = params.filters;
    }
    if (params.facetFields && params.facetFields.length > 0) {
      body.facetFields = params.facetFields;
    }
    const response = await apiClient.post('/ingestion', body);

    const data = response.data;
    return {
      results: data?.results || [],
      total: data?.total || 0,
      searchAfter: data?.searchAfter || undefined,
      facets: data?.facets || undefined,
    };
  },

  getFields: async (datastore: string): Promise<BrowseField[]> => {
    if (EMPTY_BROWSE_DATASTORES.includes(datastore)) {
      return [];
    }
    const response = await apiClient.post('/ingestion', {
      action: 'browse-fields',
      datastore,
    });
    return response.data?.fields || [];
  },

  /**
   * Get a single record's full metadata
   */
  getRecord: async (_datastore: string, id: string): Promise<Record<string, any> | null> => {
    try {
      const response = await apiClient.get('/metadata', {
        params: { embedding_ids: id },
      });
      const data = response.data;
      const results = data?.data?.results || data?.results || [];
      return results[0] || null;
    } catch {
      return null;
    }
  },
};

// Energy Organisation Ingestion API (Layer 07j)
export const energyOrgApi = {
  getStatus: async (params: { env: string }): Promise<any> => {
    const response = await apiClient.post('/ingestion', { action: 'energy-org-status', environment: params.env });
    return response.data;
  },
  run: async (params: { env: string; source: string }): Promise<any> => {
    const response = await apiClient.post('/ingestion', { action: 'energy-org-run', environment: params.env, source: params.source });
    return response.data;
  },
  schedule: async (params: { env: string; schedule: string }): Promise<any> => {
    const response = await apiClient.post('/ingestion', { action: 'energy-org-schedule', environment: params.env, schedule: params.schedule });
    return response.data;
  },
  stop: async (params: { env: string }): Promise<any> => {
    const response = await apiClient.post('/ingestion', { action: 'energy-org-stop', environment: params.env });
    return response.data;
  },
  search: async (params: { query: string; limit?: number; filters?: Record<string, any> }): Promise<any> => {
    const response = await apiClient.post('/ingestion', { action: 'energy-org-search', ...params });
    return response.data;
  },
};

// Developer VMs API (lifecycle management)
export const developerVmApi = {
  list: async (params: { env: string }): Promise<any> => {
    const response = await apiClient.get('/developer-vms', { params: { environment: params.env } });
    return response.data?.data || response.data;
  },
  start: async (params: { env: string; vm_id: string }): Promise<any> => {
    const response = await apiClient.post('/developer-vms', { action: 'start', environment: params.env, vm_id: params.vm_id });
    return response.data;
  },
  stop: async (params: { env: string; vm_id: string; confirm: boolean }): Promise<any> => {
    const response = await apiClient.post('/developer-vms', { action: 'stop', environment: params.env, vm_id: params.vm_id, confirm: params.confirm });
    return response.data;
  },
  reboot: async (params: { env: string; vm_id: string }): Promise<any> => {
    const response = await apiClient.post('/developer-vms', { action: 'reboot', environment: params.env, vm_id: params.vm_id });
    return response.data;
  },
  resize: async (params: { env: string; vm_id: string; instance_type?: string; volume_size?: number }): Promise<any> => {
    const response = await apiClient.post('/developer-vms', { action: 'resize', environment: params.env, vm_id: params.vm_id, instance_type: params.instance_type, volume_size: params.volume_size });
    return response.data;
  },
  reset: async (params: { env: string; vm_id: string; double_confirm: boolean }): Promise<any> => {
    const response = await apiClient.post('/developer-vms', { action: 'reset', environment: params.env, vm_id: params.vm_id, double_confirm: params.double_confirm });
    return response.data;
  },
  create: async (params: { env: string; developer_name: string; email: string; instance_type: string; volume_size: number; auto_shutdown_time: string; request_id: string; approval_id: string }): Promise<any> => {
    const response = await apiClient.post('/developer-vms', { action: 'create', environment: params.env, developer_name: params.developer_name, email: params.email, instance_type: params.instance_type, volume_size: params.volume_size, auto_shutdown_time: params.auto_shutdown_time, request_id: params.request_id, approval_id: params.approval_id });
    return response.data;
  },
  destroy: async (params: { env: string; vm_id: string; type_confirm: string; developer_name: string }): Promise<any> => {
    const response = await apiClient.post('/developer-vms', { action: 'destroy', environment: params.env, vm_id: params.vm_id, type_confirm: params.type_confirm, developer_name: params.developer_name });
    return response.data;
  },
  status: async (params: { env: string; vm_id: string }): Promise<any> => {
    const response = await apiClient.post('/developer-vms', { action: 'status', environment: params.env, vm_id: params.vm_id });
    return response.data;
  },
};

// =============================================================================
// Developer Workspaces (A21) -- Story #5484 (Launch W7) Stream 10
//
// The real create call above now REQUIRES request_id + approval_id from
// an approved workspace request (decision #5284) -- these four actions
// are the governed front door that produces them. handler.py's own
// handle_create returns 428 without them.
// =============================================================================

export interface WorkspaceRequest {
  request_id: string;
  developer_name: string;
  email: string;
  purpose: string;
  repository: string;
  branch: string;
  account: string;
  region: string;
  access_level: 'read-only' | 'standard' | 'elevated';
  instance_type: string;
  budget_usd: number;
  expiry: string;
  plan_digest: string;
  status: 'pending-approval' | 'approved' | 'provisioned';
  created_at: string;
  created_by: string;
  approved_approval_id?: string;
  build_id?: string;
}

export const developerWorkspacesApi = {
  previewRequest: async (body: Omit<WorkspaceRequest, 'request_id' | 'plan_digest' | 'status' | 'created_at' | 'created_by'>): Promise<WorkspaceRequest> => {
    const response = await apiClient.post('/developer-vms', { action: 'preview_workspace_request', ...body });
    return response.data?.data || response.data;
  },
  requestApproval: async (requestId: string): Promise<{ request_id: string; payload_digest: string; expires_at: number }> => {
    const response = await apiClient.post('/developer-vms', { action: 'request_workspace_approval', request_id: requestId });
    return response.data?.data || response.data;
  },
  approve: async (requestId: string, approvalId: string, freshAuthTime?: number): Promise<any> => {
    const response = await apiClient.post('/developer-vms', { action: 'approve_workspace_request', request_id: requestId, approval_id: approvalId, fresh_auth_time: freshAuthTime });
    return response.data?.data || response.data;
  },
  revoke: async (approvalId: string): Promise<any> => {
    const response = await apiClient.post('/developer-vms', { action: 'revoke_workspace_approval', approval_id: approvalId });
    return response.data?.data || response.data;
  },
};

// =============================================================================
// =============================================================================

export interface PortalSettings {
  portal_id: string;
  portal_name: string;
  ai_model_document: string;
  ai_model_folder: string;
  ai_model_project: string;
  subscription_tiers: string;
  trial_duration_days: number;
  trial_credit_usd: number;
  stripe_secret_key_ssm: string;
  stripe_publishable_key_ssm: string;
  stripe_webhook_secret_ssm: string;
  updated_at?: string;
  updated_by?: string;
}

// =============================================================================
// Model Configuration Admin API (Story #5164 / Story #5010 Phase 4)
// =============================================================================

export interface ModelConfigRole {
  role: string;
  model_id: string | null;
  wire_format: string | null;
  version: number | null;
  source: 'portal_role_assignment' | 'platform_role_default' | 'compile_time_default' | null;
  freshness?: string | null;
  confidence?: number | null;
  is_embedding: boolean;
  is_override: boolean;
  residency_compliant: boolean | null;
  /** Real call-site count for this role on this portal (from the same
   * registry get_candidates trusts). Null for the platform-default scope,
   * which has no call sites of its own. */
  site_count?: number | null;
  rate_usd_per_unit?: { input_per_million?: number; output_per_million?: number } | null;
  measured_monthly_cost_usd?: ModelConfigMeasuredCost | null;
  /** Real per-call-site detail for the [i] popup -- file/line/function/owning
   * Lambda, read from the same generated registry as knowledge/ai-roles/*.md
   * (scripts/scan-bedrock-callsites.py). One source, two renderings. */
  call_sites?: ModelConfigCallSite[];
}

export interface ModelConfigCallSite {
  file: string;
  line: number;
  function: string | null;
  owning_lambda: string;
  wire_format: string;
}

/** Story #5346 (design Section 3.3.11). status is "measured" only once the
 * real rollup clears the honesty floor (>=20 calls, >=14 coverage days) --
 * below that it is "insufficient_data" and monthly_cost_usd is null, never
 * a number extrapolated from too thin a sample. call_count/coverage_days
 * are always returned even when insufficient, so the UI can show exactly
 * how much real usage exists ("3 calls in 6 days") rather than nothing. */
export interface ModelConfigMeasuredCost {
  status: 'measured' | 'insufficient_data';
  window_days: number;
  coverage_days: number;
  call_count: number;
  monthly_cost_usd: number | null;
  rate_source: string;
  computed_at: string | null;
}

/** Candidate-side version of the same figure -- basis distinguishes a
 * candidate with its own real rollup history ("own_history", e.g. a prior
 * trial) from one that has never been assigned to this role
 * ("projected_from_current_traffic": the current model's real call volume,
 * re-priced at the candidate's rate -- an assumption, stated as such). */
export interface ModelConfigCandidateMeasuredCost extends ModelConfigMeasuredCost {
  basis: 'own_history' | 'projected_from_current_traffic' | 'insufficient_data';
}

export interface ModelConfigDryRunStatus {
  role: string;
  candidate_model_id: string;
  status: 'available' | 'not_run_for_candidate';
  reason?: string;
  job_id?: string;
  total_docs?: number;
  avg_tokens_per_doc?: number;
  projected_cost_usd?: number;
  affected_indices_by_portal?: Record<string, string[]>;
  computed_at?: string;
}

export interface ModelConfigDryRunDisabled {
  error: 'dryrun_trigger_disabled';
  reason: string;
}

export interface ModelConfigResolvedView {
  portal: string;
  roles: ModelConfigRole[];
  override_count: number;
}

export interface ModelConfigWriteResult {
  scope: string;
  target: string;
  model_id: string;
  version: number;
  full_target: string;
}

export interface ModelConfigStaleWriteError {
  error: 'stale_write';
  reason: string;
  current: Record<string, unknown> | null;
}

export interface ModelConfigGuardRejection {
  error: 'guard_rejection' | 'embedding_role_locked';
  reasons?: string[];
  reason?: string;
}

export interface ModelConfigHistoryEntry {
  scope_target: string;
  version: number;
  scope: string;
  target: string;
  model_id: string;
  wire_format: string;
  written_at: string;
  written_by: string;
  previous_version: number | null;
}

export interface ModelConfigCandidate {
  model_id: string;
  compliant: boolean;
  reasons: string[];
  families: string[];
  /** 'AU' if the model has an au. inference profile (or is an AU-pinned
   * embedding model), 'offshore' otherwise. Offshore candidates are
   * non-compliant unless the caller opts in via include_offshore. */
  residency: 'AU' | 'offshore';
  verified_by: string | null;
  rate_usd_per_unit: { input_per_million?: number; output_per_million?: number } | null;
  measured_monthly_cost_usd?: ModelConfigCandidateMeasuredCost | null;
}

export interface ModelConfigCandidatesResponse {
  role: string;
  required_wire_formats: string[];
  anomalous_wire_formats: string[];
  candidates: ModelConfigCandidate[];
}

export interface ModelConfigComparisonRow {
  model_id: string;
  compliant: boolean;
  region: string | null;
  policy_compatible: boolean;
  policy_reasons: string[];
  cost_usd_per_million: { input_per_million?: number; output_per_million?: number } | null;
  measured_monthly_cost_usd?: ModelConfigCandidateMeasuredCost | null;
  quality: number | null;
  latency_ms: number | null;
  measurement_status: string;
  context_tokens: number | null;
  verified_by: string | null;
}

export interface ModelConfigCompareResponse {
  role: string;
  required_wire_formats: string[];
  anomalous_wire_formats: string[];
  comparison: ModelConfigComparisonRow[];
}

export interface ModelConfigEvaluationResult {
  role: string;
  model_id: string;
  latency_ms: number;
  raw_response: unknown;
  measurement_status: string;
}

export interface ModelConfigEvaluationDisabled {
  error: 'billable_eval_disabled';
  reason: string;
}

// Story #6280 Stream 7 -- the platform-level Datastores view. One row per
// (datastore_id, vector_field) the live table actually has (Stream 2's
// GET /model-config/datastores), never a placeholder: readers/writers,
// residency and lock state all come back from the same generated registry
// the admin API's own reindex validation uses.
export interface ModelConfigDatastoreField {
  vector_field: string;
  model_id: string | null;
  analysis_type: string | null;
  bedrock_dimensions: number | null;
  stored_dimensions: number | null;
  normalize: boolean | null;
  concrete_index: string | null;
  state: string;
  last_reindex_at: string | null;
  residency_compliant: boolean | null;
  readers: string[] | null;
  writers: string[] | null;
  reader_portals: string[];
  writer_portals: string[];
  locked: boolean;
  lock_reason: string | null;
}

export interface ModelConfigActiveJob {
  job_id: string;
  status: string;
}

export interface ModelConfigDatastoreRow {
  datastore_id: string;
  fields: ModelConfigDatastoreField[];
  active_job: ModelConfigActiveJob | null;
}

export interface ModelConfigDatastoresResponse {
  datastores: ModelConfigDatastoreRow[];
  count: number;
}

export interface ModelConfigReindexResult {
  datastore_id: string;
  job_id: string;
  status: string;
  candidate_model_id: string;
  model_tag: string;
  candidate_rows: Array<{ field: string; scope: string; target: string }>;
  fields_meta: Record<string, {
    bedrock_dimensions: number; stored_dimensions: number; normalize: boolean | null; analysis_type: string | null;
  }>;
  source_concrete_index: string | null;
}

export interface ModelConfigReindexRejection {
  error: 'model_locked' | 'unknown_model' | 'unknown_datastore' | 'incompatible_model_family' | 'live_row_missing';
  reason: string;
}

export const modelConfigApi = {
  getResolved: async (portalId: string): Promise<ModelConfigResolvedView> => {
    const response = await apiClient.get('/model-config', { params: { portal: portalId } });
    return response.data;
  },
  getCandidates: async (
    role: string, includeOffshore = false, currentModelId?: string | null,
  ): Promise<ModelConfigCandidatesResponse> => {
    const response = await apiClient.get('/model-config/candidates', {
      params: {
        role, include_offshore: includeOffshore || undefined,
        current_model_id: currentModelId || undefined,
      },
    });
    return response.data;
  },
  compareCandidates: async (
    role: string, includeOffshore = false, currentModelId?: string | null,
  ): Promise<ModelConfigCompareResponse> => {
    const response = await apiClient.get('/model-config/candidates', {
      params: {
        role, action: 'compare_candidates', include_offshore: includeOffshore || undefined,
        current_model_id: currentModelId || undefined,
      },
    });
    return response.data;
  },
  runEvaluation: async (role: string, modelId: string): Promise<ModelConfigEvaluationResult> => {
    const response = await apiClient.post('/model-config/evaluate', { role, model_id: modelId });
    return response.data;
  },
  putPlatformRole: async (
    role: string,
    modelId: string,
    expectedVersion: number | null,
    wireFormat?: string,
  ): Promise<ModelConfigWriteResult> => {
    const response = await apiClient.put(`/model-config/platform/role/${role}`, {
      model_id: modelId, expected_version: expectedVersion, wire_format: wireFormat,
    });
    return response.data;
  },
  putPortalRole: async (
    portalId: string,
    role: string,
    modelId: string,
    expectedVersion: number | null,
    wireFormat?: string,
  ): Promise<ModelConfigWriteResult> => {
    const response = await apiClient.put(`/model-config/portal/${portalId}/role/${role}`, {
      model_id: modelId, expected_version: expectedVersion, wire_format: wireFormat,
    });
    return response.data;
  },
  putSite: async (
    siteId: string,
    modelId: string,
    expectedVersion: number | null,
    wireFormat?: string,
  ): Promise<ModelConfigWriteResult> => {
    const response = await apiClient.put(`/model-config/site/${siteId}`, {
      model_id: modelId, expected_version: expectedVersion, wire_format: wireFormat,
    });
    return response.data;
  },
  getHistory: async (fullTarget: string): Promise<{ full_target: string; history: ModelConfigHistoryEntry[] }> => {
    const response = await apiClient.get(`/model-config/history/${encodeURIComponent(fullTarget)}`);
    return response.data;
  },
  // Story #5346 Slice B -- embedding-role reprocessing cost, read from and
  // triggered against the existing Phase 7 dry-run job.
  getDryRunStatus: async (role: string, candidateModelId: string): Promise<ModelConfigDryRunStatus> => {
    const response = await apiClient.get('/model-config/dry-run', {
      params: { role, candidate_model_id: candidateModelId },
    });
    return response.data;
  },
  triggerDryRun: async (role: string, candidateModelId: string): Promise<{
    role: string; candidate_model_id: string; invoke_status_code: number; result: Record<string, unknown>;
  }> => {
    const response = await apiClient.post('/model-config/dry-run', { role, candidate_model_id: candidateModelId });
    return response.data;
  },
  // Story #6280 Stream 7 -- platform-level Datastores view.
  getDatastores: async (): Promise<ModelConfigDatastoresResponse> => {
    const response = await apiClient.get('/model-config/datastores');
    return response.data;
  },
  proposeReindex: async (
    datastoreId: string, candidateModelId: string,
  ): Promise<ModelConfigReindexResult> => {
    const response = await apiClient.post(`/model-config/datastores/${datastoreId}/reindex`, {
      candidate_model_id: candidateModelId,
    });
    return response.data;
  },
  // Task #6289 (Rob, 2026-09-16) / confirmed missing live by the Task #6296
  // CoWork run, 2026-09-20 -- Cancel a QUEUED/RUNNING job before it goes
  // live, and Revert a field's LIVE binding back to its previous value.
  // Both call routes the backend already had (handler.py cancel_datastore_reindex
  // / revert_datastore_binding) with no server-side change beyond exposing
  // active_job on getDatastores() above.
  cancelReindex: async (datastoreId: string, jobId: string): Promise<{
    datastore_id: string; job_id: string; status: string; already_cancelled?: boolean;
  }> => {
    const response = await apiClient.post(`/model-config/datastores/${datastoreId}/reindex/${jobId}/cancel`);
    return response.data;
  },
  revertBinding: async (datastoreId: string, vectorField: string): Promise<{
    datastore_id: string; vector_field: string; model_id: string; concrete_index: string; reverted: true;
  }> => {
    const response = await apiClient.post(`/model-config/datastores/${datastoreId}/${vectorField}/revert`);
    return response.data;
  },
};

// =============================================================================
// Portal Config Draft API (Story #5346 Stream 3a/3b — P07)
// =============================================================================

export interface PortalConfigDraftEntry {
  scope: string;
  version: number;
  portal_id: string;
  environment: string;
  author: string;
  reason: string;
  saved_at: string;
  display_name?: string;
  description?: string;
  support_email?: string;
  timezone?: string;
  feature_flags?: Record<string, boolean>;
}

export interface PortalConfigDraftGetResponse {
  portal_id: string;
  environment: string;
  latest: PortalConfigDraftEntry | null;
  history: PortalConfigDraftEntry[];
}

export interface PortalConfigDraftValidateResponse {
  portal_id: string;
  environment: string;
  validated_against_version: number | null;
  valid: boolean;
  issues: Array<{ field: string; reason: string }>;
}

export interface PortalConfigDraftChangesetResponse {
  portal_id: string;
  environment: string;
  version: number;
  changeset_id: string;
  status: string;
  created_by: string;
}

// =============================================================================
// Allocation API (Story #5346 Stream 5a/5b — P25/P26)
// =============================================================================

export interface AllocationClassifyResponse {
  rule_version: string;
  categories: { direct: number; 'model-metered': number; shared: number; untagged: number };
  per_service: Array<{ service: string; cost: number; category: string }>;
  confidence: number;
  total_cost: number;
  source?: string;
  freshness?: string;
  ai_roles: {
    model_metered_total: number;
    roles: Array<{ role: string; cost: number | null; status: string }>;
    note: string;
    source?: string;
    freshness?: string;
    confidence?: number;
  };
  version: string;
}

export interface AllocationRuleChangeset {
  changeset: {
    rule_set: string;
    version: number;
    base_version: string;
    before: Record<string, string>;
    after: Record<string, string>;
    status: string;
    created_by: string;
    created_at: string;
  };
}

export interface AllocationRuleVersionsResponse {
  current_version: string;
  drafts: AllocationRuleChangeset['changeset'][];
}

export const allocationApi = {
  classify: async (serviceCosts: Array<{ service: string; cost: number }>, signal?: AbortSignal): Promise<AllocationClassifyResponse> => {
    const response = await apiClient.post('/allocation/classify', { service_costs: serviceCosts }, {signal});
    return response.data;
  },
  createRuleChangeset: async (proposedRules: Record<string, string>): Promise<AllocationRuleChangeset> => {
    const response = await apiClient.post('/allocation/rule-changeset', { proposed_rules: proposedRules });
    return response.data;
  },
  getRuleVersions: async (): Promise<AllocationRuleVersionsResponse> => {
    const response = await apiClient.get('/allocation/rule-versions');
    return response.data;
  },
};

// =============================================================================
// Investigations API (Story #5346 Stream 6a/6c — P27)
// =============================================================================

export interface Investigation {
  investigation_id: string;
  trigger: string;
  scope: Record<string, unknown>;
  filter_window: { start_date: string; end_date: string };
  status: 'DRAFT' | 'PENDING' | 'SCHEDULED' | 'RUNNING' | 'PAUSED' | 'ON_HOLD' | 'COMPLETED' | 'FAILED' | 'CANCELLED';
  conclusion: 'Confirmed' | 'Contributory' | 'Unrelated' | 'Unresolved' | null;
  evidence_note?: string;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export const investigationsApi = {
  create: async (trigger: string, scope: Record<string, unknown>, filterWindow: { start_date: string; end_date: string }): Promise<{ investigation: Investigation }> => {
    const response = await apiClient.post('/investigations/create', { trigger, scope, filter_window: filterWindow });
    return response.data;
  },
  list: async (): Promise<{ investigations: Investigation[]; source: string; freshness: string; confidence: number }> => {
    const response = await apiClient.get('/investigations');
    return response.data;
  },
  get: async (id: string): Promise<{ investigation: Investigation }> => {
    const response = await apiClient.get(`/investigations/${id}`);
    return response.data;
  },
  decide: async (id: string, conclusion: string, evidenceNote: string): Promise<{ investigation: Investigation }> => {
    const response = await apiClient.post(`/investigations/${id}/decision`, { conclusion, evidence_note: evidenceNote });
    return response.data;
  },
};

// =============================================================================
// Budgets API (Story #6611 -- A09, the real AWS Budgets in the payer account)
// =============================================================================

/** The six AWS accounts. Five hold a monthly budget; the payer has none (the screen says so). `scopeKey` is set for
 *  the three that are registered environments in the global scope; the other
 *  three are organisation accounts that the scope selector does not list. */
export const BUDGET_ENVIRONMENTS = [
  { key: 'dev', label: 'Dev', accountId: '541341196861', scopeKey: 'dev' },
  { key: 'test', label: 'Test', accountId: '126458880516', scopeKey: 'test' },
  { key: 'management', label: 'Shared-Services (hub)', accountId: '801945369224', scopeKey: 'management' },
  { key: 'payer', label: 'Payer (org master)', accountId: '768693789269', scopeKey: null },
  { key: 'audit', label: 'Audit', accountId: '447580525949', scopeKey: null },
  { key: 'log-archive', label: 'Log Archive', accountId: '009853298259', scopeKey: null },
] as const;

export type BudgetEnvironmentKey = typeof BUDGET_ENVIRONMENTS[number]['key'];

export interface BudgetMoney { usd: number; aud: number }

export type BudgetActionState = 'none' | 'active' | 'stopping' | 'stopped' | 'resuming' | 'failed';

export interface BudgetView {
  environment: BudgetEnvironmentKey;
  account_id: string;
  budget_name: string;
  configured: true;
  time_unit: string | null;
  fx: { aud_per_usd: number; source: string };
  limit: BudgetMoney;
  base_limit: BudgetMoney;
  additional_approved: BudgetMoney;
  actual: BudgetMoney;
  /** null when AWS has no forecast yet (early in the month) -- never shown as 0. */
  forecast: BudgetMoney | null;
  /** false when the live limit differs from what the portal recorded (changed outside the portal). */
  limit_matches_record: boolean;
  alerts: Array<{
    notification_type: string; comparison: string; threshold: number; threshold_type: string;
    subscribers: Array<{ type: string; address: string }>;
  }>;
  subscribers: Array<{ type: string; address: string }>;
  action_state: BudgetActionState;
  actions: Array<{
    action_id: string; status: string; type: string; approval_model: string;
    notification_type: string; threshold: number | null; threshold_type: string | null;
  }>;
  approvals: Array<{
    approval_id: string; author: string; reason: string; amount: BudgetMoney; status: string;
    created_at: string; expires_at: string;
  }>;
  updated_by: string | null;
  updated_at: string | null;
}

/** An account with no managed budget (the payer): a statement, not an error. */
export interface BudgetNotConfigured {
  environment: BudgetEnvironmentKey;
  account_id: string;
  budget_name: null;
  configured: false;
  note: string;
}

export const budgetIsConfigured = (budget: BudgetView | BudgetNotConfigured): budget is BudgetView => budget.configured;

/** The fresh authenticator step every change needs. Never stored, never logged. */
export interface BudgetMfa { password: string; code: string }

export interface BudgetLimitChange { changed: 'limit'; environment: BudgetEnvironmentKey; limit: BudgetMoney }
export interface BudgetApprovalChange {
  changed: 'approval'; environment: BudgetEnvironmentKey; approval_id: string; expires_at: string; limit: BudgetMoney;
}
export interface BudgetResumeChange {
  changed: 'resume'; environment: BudgetEnvironmentKey; reversed_actions: string[]; still_over_limit: boolean;
}

export const budgetsApi = {
  get: async (environment: BudgetEnvironmentKey): Promise<{ budget: BudgetView | BudgetNotConfigured }> => {
    const response = await apiClient.get('/budgets', { params: { environment } });
    return response.data;
  },
  setLimit: async (payload: {
    environment: BudgetEnvironmentKey; limit_aud: number; reason?: string; mfa: BudgetMfa;
  }): Promise<BudgetLimitChange> => {
    const response = await apiClient.post('/budgets/limit', payload);
    return response.data;
  },
  approveAdditional: async (environment: BudgetEnvironmentKey, payload: {
    amount_aud: number; days: number; reason: string; mfa: BudgetMfa;
  }): Promise<BudgetApprovalChange> => {
    const response = await apiClient.post(`/budgets/${environment}/approve-additional`, payload);
    return response.data;
  },
  resume: async (environment: BudgetEnvironmentKey, payload: {
    reason?: string; mfa: BudgetMfa;
  }): Promise<BudgetResumeChange> => {
    const response = await apiClient.post(`/budgets/${environment}/resume`, payload);
    return response.data;
  },
};

// =============================================================================
// Metering API (Story #5346 Stream 7a/7b — A11)
// =============================================================================

export interface MeteringSourceEvent {
  event_id: string;
  portal_id: string;
  environment: string;
  role: string;
  metric_key: string;
  quantity: number;
  unit: string;
  occurred_at: string;
}

export interface MeteringRatedLine extends MeteringSourceEvent {
  unit_price: number | null;
  unit_price_currency_unit?: string;
  rated_amount: number | null;
  rate_status: 'rated' | 'no_matching_rate';
}

export interface MeteringReconcileResponse {
  portal_id: string;
  environment: string;
  actual_event_count: number;
  expected_event_count: number | null;
  delta: number | null;
  correction?: { error?: string; reason?: string; status?: string };
}

/** P26 (Story #5654) — one role's real aggregation over the metering
 *  lineage table, restoring what scopeDecisions.tsx recorded as missing
 *  ("no per-role meter exists"). `rated_total` is genuinely 0 when nothing
 *  traced for that role has a matching rate — never a fabricated non-zero. */
export interface MeteringRoleUsage {
  role: string;
  event_count: number;
  rated_event_count: number;
  unrated_event_count: number;
  quantity_by_metric: Record<string, number>;
  rated_total: number;
  portals: string[];
  first_seen: string | null;
  last_seen: string | null;
}

export interface MeteringUsageByRoleResponse {
  period_days: number;
  portal_id: string | null;
  roles: MeteringRoleUsage[];
  event_count_total: number;
  source: string;
  freshness: string;
  confidence: number;
}

export const meteringApi = {
  inspect: async (sourceEvent: MeteringSourceEvent, publishedMeteredRates: Array<{ key: string; label: string; price: number; unit: string }>): Promise<{ rated_line: MeteringRatedLine; source: string; freshness: string; confidence: number }> => {
    const response = await apiClient.post('/metering/inspect', { source_event: sourceEvent, published_metered_rates: publishedMeteredRates });
    return response.data;
  },
  reconcilePeriod: async (portalId: string, environment: string, expectedEventCount?: number, proposeCorrection?: boolean): Promise<MeteringReconcileResponse> => {
    const response = await apiClient.post('/metering/reconcile-period', {
      portal_id: portalId, environment, expected_event_count: expectedEventCount, propose_correction: proposeCorrection,
    });
    return response.data;
  },
  openInvestigation: async (reason: 'missing_events' | 'duplicate_events', portalId: string, environment: string, filterWindow: { start_date: string; end_date: string }) => {
    const response = await apiClient.post('/metering/open-investigation', { reason, portal_id: portalId, environment, filter_window: filterWindow });
    return response.data;
  },
  exportBillable: async (periodClosed: boolean, completenessConfirmed: boolean, authorisedBy: string, dlpClassification: string) => {
    const response = await apiClient.post('/metering/export-billable', {
      period_closed: periodClosed, completeness_confirmed: completenessConfirmed,
      authorised_by: authorisedBy, dlp_classification: dlpClassification,
    });
    return response.data;
  },
  getUsageByRole: async (periodDays = 30, portalId?: string): Promise<MeteringUsageByRoleResponse> => {
    const response = await apiClient.get('/metering/usage-by-role', {
      params: { period_days: periodDays, ...(portalId ? { portal_id: portalId } : {}) },
    });
    return response.data;
  },
};

export const portalConfigDraftApi = {
  get: async (portalId: string, environment: string): Promise<PortalConfigDraftGetResponse> => {
    const response = await apiClient.get(`/portal-config-draft/${portalId}/${environment}`);
    return response.data;
  },
  save: async (portalId: string, environment: string, body: { reason: string } & Record<string, unknown>): Promise<{ draft: PortalConfigDraftEntry }> => {
    const response = await apiClient.post(`/portal-config-draft/${portalId}/${environment}/save`, body);
    return response.data;
  },
  validate: async (portalId: string, environment: string, body: Record<string, unknown>): Promise<PortalConfigDraftValidateResponse> => {
    const response = await apiClient.post(`/portal-config-draft/${portalId}/${environment}/validate`, body);
    return response.data;
  },
  createChangeset: async (portalId: string, environment: string, body: { version?: number }): Promise<PortalConfigDraftChangesetResponse> => {
    const response = await apiClient.post(`/portal-config-draft/${portalId}/${environment}/changeset`, body);
    return response.data;
  },
};

export const portalSettingsApi = {
  list: async (): Promise<{ portals: PortalSettings[]; count: number }> => {
    const response = await apiClient.get('/portal-settings');
    return response.data;
  },
  get: async (portalId: string): Promise<{ settings: PortalSettings }> => {
    const response = await apiClient.get(`/portal-settings/${portalId}`);
    return response.data;
  },
  update: async (portalId: string, settings: Partial<PortalSettings>): Promise<{ settings: PortalSettings }> => {
    const response = await apiClient.put(`/portal-settings/${portalId}`, settings);
    return response.data;
  },
};

// =============================================================================
// Pricing Configuration API (per-portal versioned pricing schema)
// =============================================================================

export interface PricingTier {
  id: string;
  name: string;
  price: number | string;
  per: string;
  gst: 'inclusive' | 'exclusive';
  inc: string[];
  pub: boolean;
  feat: boolean;
}

export interface PricingSeatClass {
  class: string;
  price: number | string;
}

export interface PricingMeteredItem {
  key: string;
  label: string;
  price: number | string;
  unit: string;
}

export interface PricingAddon {
  key: string;
  label: string;
  price: number | string;
  unit: string;
}

export interface PricingSectorRoutes {
  education_discount_pct: string;
  government_route: string;
  founding_coupon: string;
}

export interface PricingConfig {
  portal_label: string;
  currency: string;
  gst_mode: string;
  cadence: string;
  tiers: PricingTier[];
  seat_classes: PricingSeatClass[];
  metered: PricingMeteredItem[];
  addons: PricingAddon[];
  sector_routes: PricingSectorRoutes;
}

export interface PricingPortalSummary {
  portal_id: string;
  portal_label?: string;
  color?: string;
  status?: 'draft' | 'published';
  version?: number;
}

export interface PricingGetResponse {
  portal_id: string;
  draft?: PricingConfig | null;
  published?: PricingConfig | null;
  draft_version?: number;
  published_version?: number;
  status?: 'draft' | 'published';
}

export interface PricingVersion {
  version: number;
  status: 'draft' | 'published' | 'archived';
  author?: string;
  note?: string;
  created_at?: string;
}

export interface PricingSyncResult {
  mode: 'test' | 'live';
  created: number;
  updated: number;
  skipped: number;
  details?: Array<{ name: string; action: string; stripe_id?: string }>;
  message?: string;
}

export const pricingApi = {
  list: async (): Promise<{ portals: PricingPortalSummary[]; count?: number }> => {
    const response = await apiClient.get('/pricing');
    return response.data;
  },
  get: async (portalId: string): Promise<PricingGetResponse> => {
    const response = await apiClient.get(`/pricing/${portalId}`);
    return response.data;
  },
  saveDraft: async (portalId: string, config: PricingConfig): Promise<PricingGetResponse> => {
    const response = await apiClient.put(`/pricing/${portalId}/draft`, config);
    return response.data;
  },
  publish: async (portalId: string): Promise<PricingGetResponse> => {
    const response = await apiClient.post(`/pricing/${portalId}/publish`);
    return response.data;
  },
  versions: async (portalId: string): Promise<{ versions: PricingVersion[] }> => {
    const response = await apiClient.get(`/pricing/${portalId}/versions`);
    return response.data;
  },
  restore: async (portalId: string, version: number): Promise<PricingGetResponse> => {
    const response = await apiClient.post(`/pricing/${portalId}/restore`, { version });
    return response.data;
  },
  syncStripe: async (portalId: string): Promise<PricingSyncResult> => {
    const response = await apiClient.post(`/pricing/${portalId}/sync-stripe`);
    return response.data;
  },
  calculateExamples: async (portalId: string, usage: Record<string, unknown>): Promise<PricingCalculateExamplesResponse> => {
    const response = await apiClient.post(`/pricing/${portalId}/calculate-examples`, { usage });
    return response.data;
  },
};

export interface PricingTariffResult {
  available: boolean;
  reason?: string;
  currency?: string;
  total: number | null;
  tax_amount?: number;
  breakdown: Array<{ dimension: string; label?: string; amount: number | null }>;
}

export interface PricingCalculateExamplesResponse {
  portal_id: string;
  usage: Record<string, unknown>;
  before: PricingTariffResult;
  after: PricingTariffResult;
  delta: number | null;
}

// =============================================================================
// =============================================================================

import { createOrgApi } from '@secureembed/shared-crm';

export const orgApi = createOrgApi({ apiClient });

// ---------------------------------------------------------------------------
// Customers (C02 Organisations, C03 Contacts) -- Story #5484 (Launch W7)
// Stream 1, Task #5540. The canonical /api/customers writer/projection.
// It reconciles with (never duplicates) the real org-crud/org-contacts
// backend that `orgApi` above already calls directly -- see
// lambda/dashboard/customers/handler.py's module docstring. Every write
// here returns the platform's write_response envelope (durable commandId,
// planHash, verified) instead of orgApi's raw pass-through response.
//
// These field shapes mirror lambda/organizations-api/{org-crud,org-
// contacts}/handler.py's real DynamoDB items exactly (the wire shape
// this Lambda proxies unchanged) -- duplicated locally rather than
// imported from @secureembed/shared-crm's types module, matching this
// file's own existing convention (see Organizations.tsx's local
// `interface Organization`): that package's barrel does not currently
// re-export its types/index.ts at all, only createOrgApi's untyped `any`.
// ---------------------------------------------------------------------------
export interface CustomerOrganisation {
  org_id: string;
  name: string;
  description?: string;
  website?: string;
  location_country?: string;
  location_city?: string;
  categories?: string[];
  capabilities?: string[];
  facility_type?: string;
  contact_info?: Record<string, unknown>;
  funding?: string | Record<string, unknown>;
  source_type?: 'partner' | 'quantum' | 'dashboard' | 'user';
  owner_id?: string;
  visibility?: 'public' | 'private';
  status?: string;
  created_at?: string;
  updated_at?: string;
}

export interface CustomerContact {
  contact_id: string;
  owner_id: string;
  first_name: string;
  last_name?: string;
  email?: string;
  phone?: string;
  title?: string;
  role?: string;
  notes?: string;
  created_at?: string;
  updated_at?: string;
}

export interface CustomersReadResponse<T> {
  data: T;
  scope: string;
  source: string;
  observedAt?: string;
  freshness?: string;
  status: EnvironmentRegistryState | 'stale' | 'unauthorised';
  schemaVersion?: number;
  correlationId?: string;
}

export interface CustomersWriteResponse {
  commandId: string;
  target: Record<string, string>;
  planHash: string;
  stage: string;
  resumableRoute: string;
  providerRequestId: string;
  verified: boolean;
  receiptUrl?: string | null;
  organisation?: CustomerOrganisation;
  contact?: CustomerContact;
  contactRelinkPending?: boolean;
  updatedFields?: string[];
}

export interface CustomersWriteError {
  error: string;
  reason?: unknown;
}

// Story #5484 (Launch W7) Stream 3, Task #5542 -- C05 (Customer Documents).
// Deliberately has NO s3_key, download_url, or embedding_* field: the
// backend (lambda/dashboard/customers/documents.py's _project_document_summary)
// strips them before this ever reaches the SPA -- ADR-004 (no admin/operator
// raw-document download path) is enforced server-side, not by omission here.
export interface CustomerDocumentClassification {
  category: string;
  dlpFlags: string[];
}

export interface CustomerDocument {
  document_id: string;
  owner_id?: string;
  target_type?: string;
  target_id?: string;
  filename?: string;
  content_type?: string;
  file_size?: number;
  security_scan_status?: 'scanning' | 'clean' | 'blocked';
  classification_status?: 'pending' | 'classified';
  classification?: CustomerDocumentClassification | null;
  version?: number;
  created_at?: string;
  updated_at?: string;
}

// Story #5484 (W7) Stream 4, Task #5543 -- C06 Customer Sharing. Mirrors
// lambda/organizations-api/org-shares/handler.py's real DynamoDB item shape.
export interface CustomerShare {
  share_id: string;
  owner_id: string;
  resource_type: 'organisation' | 'contact';
  resource_id: string;
  shared_with_email: string;
  shared_with_id?: string;
  share_mode: 'one_off' | 'ongoing';
  include_comments?: boolean;
  include_attachments?: boolean;
  status: 'active' | 'revoked';
  created_at?: string;
  revoked_at?: string;
  updated_at?: string;
}

// ---------------------------------------------------------------------------
// C04 -- Interactions (Story #5484 W7 Stream 2, Task #5541). Field names
// mirror lambda/dashboard/customers/handler.py's log_interaction/
// update_interaction payload shape exactly (camelCase on the wire, same
// convention as the rest of this file's write commands).
// ---------------------------------------------------------------------------
export type InteractionChannel = 'email' | 'call' | 'meeting' | 'note' | 'other';
export type InteractionTargetType = 'organisation' | 'contact';

export interface CustomerInteraction {
  id: string;
  owner_id: string;
  target_type: InteractionTargetType;
  target_id: string;
  interaction_type: InteractionChannel;
  summary: string;
  details?: string;
  outcome?: string;
  commitments?: string;
  occurred_at: string;
  created_at?: string;
  updated_at?: string;
}

// ---------------------------------------------------------------------------
// C01 -- Customer home (Story #5484 W7 Stream 2, Task #5541). Each metric
// column carries its own state so an unbuilt sibling stream (Documents,
// Sharing, Support/Access) renders as an honest UnavailableReason, never
// a fabricated 0 -- see get_customer_home's own docstring.
// ---------------------------------------------------------------------------
export interface CustomerHomeMetric {
  value: number | null;
  state: 'known' | 'unavailable';
  reason?: string;
}

export interface CustomerHomeRow {
  orgId: string;
  name: string;
  owner: string;
  openTickets: CustomerHomeMetric;
  expiringShares: CustomerHomeMetric;
  documentAlerts: CustomerHomeMetric;
}

export const customersApi = {
  listOrganisations: async (filters?: Record<string, string>): Promise<CustomersReadResponse<CustomerOrganisation[]>> => {
    const response = await apiClient.get<CustomersReadResponse<CustomerOrganisation[]>>('/customers', {
      params: { entity: 'organisations', ...filters },
    });
    return response.data;
  },
  getOrganisation: async (orgId: string): Promise<CustomersReadResponse<CustomerOrganisation | null>> => {
    const response = await apiClient.get<CustomersReadResponse<CustomerOrganisation | null>>('/customers', {
      params: { entity: 'organisations', action: 'get', orgId },
    });
    return response.data;
  },
  createOrganisation: async (payload: Partial<CustomerOrganisation>): Promise<CustomersWriteResponse> => {
    const response = await apiClient.post<CustomersWriteResponse>('/customers', {
      commandType: 'create_organisation',
      payload,
    });
    return response.data;
  },
  updateOrganisation: async (orgId: string, fields: Partial<CustomerOrganisation>): Promise<CustomersWriteResponse> => {
    const response = await apiClient.post<CustomersWriteResponse>('/customers', {
      commandType: 'update_organisation',
      payload: { orgId, ...fields },
    });
    return response.data;
  },
  archiveOrganisation: async (orgId: string): Promise<CustomersWriteResponse> => {
    const response = await apiClient.post<CustomersWriteResponse>('/customers', {
      commandType: 'archive_organisation',
      payload: { orgId },
    });
    return response.data;
  },
  mergeOrganisations: async (sourceOrgId: string, targetOrgId: string): Promise<CustomersWriteResponse> => {
    const response = await apiClient.post<CustomersWriteResponse>('/customers', {
      commandType: 'merge_organisation',
      payload: { sourceOrgId, targetOrgId },
    });
    return response.data;
  },
  listContacts: async (filters?: Record<string, string>): Promise<CustomersReadResponse<CustomerContact[]>> => {
    const response = await apiClient.get<CustomersReadResponse<CustomerContact[]>>('/customers', {
      params: { entity: 'contacts', ...filters },
    });
    return response.data;
  },
  getContact: async (contactId: string): Promise<CustomersReadResponse<CustomerContact | null>> => {
    const response = await apiClient.get<CustomersReadResponse<CustomerContact | null>>('/customers', {
      params: { entity: 'contacts', action: 'get', contactId },
    });
    return response.data;
  },
  updateContact: async (contactId: string, fields: Partial<CustomerContact>): Promise<CustomersWriteResponse> => {
    const response = await apiClient.post<CustomersWriteResponse>('/customers', {
      commandType: 'update_contact',
      payload: { contactId, ...fields },
    });
    return response.data;
  },
  // C05 -- read-only (see documents.py's module docstring): no create/
  // update/archive/download commandType exists for documents.
  listDocuments: async (filters?: Record<string, string>): Promise<CustomersReadResponse<CustomerDocument[]>> => {
    const response = await apiClient.get<CustomersReadResponse<CustomerDocument[]>>('/customers', {
      params: { entity: 'documents', ...filters },
    });
    return response.data;
  },
  getDocument: async (documentId: string): Promise<CustomersReadResponse<CustomerDocument | null>> => {
    const response = await apiClient.get<CustomersReadResponse<CustomerDocument | null>>('/customers', {
      params: { entity: 'documents', action: 'get', documentId },
    });
    return response.data;
  },

  // Story #5484 (W7) Stream 4, Task #5543 -- C06 Customer Sharing.
  // APPROVAL-GATED: issueShare/revokeShare both require an approvalToken
  // obtained via sharingApproval.ts's two-step request+approve flow
  // (decision #5284) -- see that file's module docstring.
  listShares: async (direction: 'sent' | 'received'): Promise<CustomersReadResponse<CustomerShare[]>> => {
    const response = await apiClient.get<CustomersReadResponse<CustomerShare[]>>('/customers', {
      params: { entity: 'shares', direction },
    });
    return response.data;
  },
  issueShare: async (payload: {
    resourceType: 'organisation' | 'contact';
    resourceId: string;
    email: string;
    shareMode?: 'one_off' | 'ongoing';
    includeComments?: boolean;
    includeAttachments?: boolean;
    approvalToken: string;
  }): Promise<CustomersWriteResponse> => {
    const response = await apiClient.post<CustomersWriteResponse>('/customers', {
      commandType: 'issue_share',
      payload,
    });
    return response.data;
  },
  revokeShare: async (shareId: string, approvalToken: string): Promise<CustomersWriteResponse> => {
    const response = await apiClient.post<CustomersWriteResponse>('/customers', {
      commandType: 'revoke_share',
      payload: { shareId, approvalToken },
    });
    return response.data;
  },

  // -- C04 Interactions --------------------------------------------------
  listInteractions: async (
    targetType: InteractionTargetType,
    targetId: string
  ): Promise<CustomersReadResponse<CustomerInteraction[]>> => {
    const response = await apiClient.get<CustomersReadResponse<CustomerInteraction[]>>('/customers', {
      params: { entity: 'interactions', targetType, targetId },
    });
    return response.data;
  },
  logInteraction: async (payload: {
    targetType: InteractionTargetType;
    targetId: string;
    channel: InteractionChannel;
    summary: string;
    details?: string;
    outcome?: string;
    commitments?: string;
    occurredAt?: string;
  }): Promise<CustomersWriteResponse> => {
    const response = await apiClient.post<CustomersWriteResponse>('/customers', {
      commandType: 'log_interaction',
      payload,
    });
    return response.data;
  },
  updateInteraction: async (
    interactionId: string,
    fields: Partial<Pick<CustomerInteraction, 'summary' | 'details' | 'outcome' | 'commitments'>> & { occurredAt?: string }
  ): Promise<CustomersWriteResponse> => {
    const response = await apiClient.post<CustomersWriteResponse>('/customers', {
      commandType: 'update_interaction',
      payload: { interactionId, ...fields },
    });
    return response.data;
  },

  // -- C01 Customer home ---------------------------------------------------
  getCustomerHome: async (): Promise<CustomersReadResponse<CustomerHomeRow[]>> => {
    const response = await apiClient.get<CustomersReadResponse<CustomerHomeRow[]>>('/customers', {
      params: { entity: 'customer_home' },
    });
    return response.data;
  },
};

// ---------------------------------------------------------------------------
// Knowledge — the OKF bundle, its decision records, and the first-party source
// the concepts describe. ADR-013: served ONLY from the authenticated hub API,
// out of a private CMK-encrypted bucket, never from this SPA's own origin (which
// answers 200 unauthenticated on every path).
//
// The `path` sent to getFile is a repository path, and the Lambda looks it up in
// the pack manifest. It is never used to build an S3 key, so an unknown path is
// a 404 by the same route as a typo.
// ---------------------------------------------------------------------------
export interface KnowledgeConcept {
  path: string;
  zone: string;
  name: string;
  bytes: number;
  sha256: string;
}

/** The bundle's own freshness measure, from scripts/okf-divergence.py. */
export interface KnowledgeDivergence {
  /** null means it could not be computed — never render that as 0. */
  score: number | null;
  components?: Record<string, number>;
  note?: string;
}

/** A diagram, its render, and whether that render still matches its source. */
export interface KnowledgeDiagram {
  path: string;
  root: string;
  format: string;
  render: string;
  render_state: 'rendered' | 'stale' | 'source-only' | 'too-large' | 'missing' | string;
  sources: string[];
}

/** K06 — an authored .docx user guide under documentation/, indexed by
 *  scripts/build-knowledge-pack.py. `text_path` is a normal manifest file
 *  (readable via knowledgeApi.getFile) when the guide was indexed; an
 *  indexing failure carries `error` and no text_path instead. */
export interface KnowledgeGuide {
  docx_path: string;
  title: string;
  portal: string;
  text_path?: string;
  bytes?: number;
  sha256?: string;
  headings?: string[];
  paragraph_count?: number;
  error?: string;
}

export interface KnowledgeOverview {
  commit: string;
  built_at?: string;
  roots: string[];
  counts: {
    files: number;
    concepts: number;
    diagrams?: number;
    diagrams_rendered?: number;
    diagrams_stale?: number;
    guides?: number;
    guides_indexed?: number;
  };
  divergence?: KnowledgeDivergence;
  diagrams?: KnowledgeDiagram[];
  concepts: KnowledgeConcept[];
  guides?: KnowledgeGuide[];
  version?: string;
}

export interface KnowledgeFile {
  path: string;
  /** Text, or base64 when `binary` is true (a diagram render). */
  content: string;
  language: string;
  bytes: number;
  sha256: string;
  commit: string;
  binary?: boolean;
  media_type?: string;
}

export const knowledgeApi = {
  getOverview: async (): Promise<KnowledgeOverview> => {
    const response = await apiClient.get('/knowledge');
    return response.data;
  },
  listConcepts: async (): Promise<{ commit: string; concepts: KnowledgeConcept[] }> => {
    const response = await apiClient.get('/knowledge/concepts');
    return response.data;
  },
  getFile: async (path: string): Promise<KnowledgeFile> => {
    const response = await apiClient.get('/knowledge/file', { params: { path } });
    return response.data;
  },
};

/** The real Cognito auth_time claim off the stored ID token -- decision
 *  #5284's step-up check needs WHEN the caller actually last authenticated,
 *  which only the token itself carries; a client-side Date.now() would let
 *  a long-cached session claim to be freshly authenticated, defeating the
 *  point of step-up entirely. Returns null if no token is stored or it
 *  cannot be parsed -- the approval call then fails loud server-side
 *  (dashboard_approval.approve() rejects a missing fresh_auth_time) rather
 *  than this function inventing a value.  */
export function getFreshAuthTimeSeconds(): number | null {
  try {
    const token = localStorage.getItem(STORAGE_KEYS.ID_TOKEN) || localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
    if (!token) return null;
    const payloadB64 = token.split('.')[1];
    if (!payloadB64) return null;
    const json = atob(payloadB64.replace(/-/g, '+').replace(/_/g, '/'));
    const claims = JSON.parse(json);
    return typeof claims.auth_time === 'number' ? claims.auth_time : null;
  } catch {
    return null;
  }
}

/** K07 (Story #5654) — the documentation ChangeSet republish route.
 *  Building/staging a candidate pack happens out of band
 *  (scripts/build-knowledge-pack.py --stage); this client only drives the
 *  governed promotion of an already-staged candidate to be the live pack —
 *  same request/approve/revoke/release shape as knowledgeExportsApi (K08). */
export interface KnowledgeChangesetCandidate {
  commit: string;
  manifest: string;
  built_at_commit_date?: string;
  divergence?: KnowledgeDivergence;
  counts?: Record<string, number>;
  staged_at: string;
}

export interface KnowledgeChangesetGateResult {
  all_passed: boolean;
  gates: Record<string, { passed: boolean } & Record<string, unknown>>;
}

export const knowledgeChangesetApi = {
  listCandidates: async (): Promise<{ current_commit: string; candidates: KnowledgeChangesetCandidate[] }> => {
    const response = await apiClient.get('/knowledge/changeset');
    return response.data;
  },
  preview: async (commit: string): Promise<{ preview: Record<string, unknown>; gate_result: KnowledgeChangesetGateResult }> => {
    const response = await apiClient.post('/knowledge/changeset', { commandType: 'preview', commit });
    return response.data;
  },
  requestApproval: async (commit: string): Promise<{ request: { request_id: string; payload_digest: string; expires_at: number } }> => {
    const response = await apiClient.post('/knowledge/changeset', { commandType: 'request', commit });
    return response.data;
  },
  approve: async (commit: string, requestId: string, freshAuthTime: number): Promise<{ receipt: Record<string, unknown> }> => {
    const response = await apiClient.post('/knowledge/changeset', {
      commandType: 'approve', commit, request_id: requestId, fresh_auth_time: freshAuthTime,
    });
    return response.data;
  },
  revoke: async (requestId: string): Promise<{ revoked: Record<string, unknown> }> => {
    const response = await apiClient.post('/knowledge/changeset', { commandType: 'revoke', request_id: requestId });
    return response.data;
  },
  release: async (commit: string, requestId: string): Promise<{ released: Record<string, unknown>; verification: Record<string, unknown> }> => {
    const response = await apiClient.post('/knowledge/changeset', { commandType: 'release', commit, request_id: requestId });
    return response.data;
  },
};

// Security Lambda API (Story #5484 Streams 7-8, screens A01, A12-A15). Calls
// the NEW lambda/dashboard/security/ Lambda. Approval-gated writes (A13
// publish, A14 key lifecycle, A15 break-glass) take an approvalToken sent
// as X-Approval-Token, never in the body (same convention as identityApi).
const SECURITY_APPROVAL_HEADER = 'X-Approval-Token';

export const securityApi = {
  // --- A01 Administration overview ---
  getOverview: async (): Promise<any> => {
    const response = await apiClient.get('/administration/security/overview');
    return response.data;
  },

  // --- A12 Security posture ---
  getPosture: async (): Promise<any> => {
    const response = await apiClient.get('/administration/security/posture');
    return response.data;
  },

  // --- A13 Security policies ---
  listPolicyVersions: async (policyId: string): Promise<any> => {
    const response = await apiClient.get(`/administration/security/policies/${encodeURIComponent(policyId)}`);
    return response.data;
  },
  simulatePolicy: async (policyId: string, document: Record<string, unknown>): Promise<any> => {
    const response = await apiClient.post(
      `/administration/security/policies/${encodeURIComponent(policyId)}/simulate`, { document });
    return response.data;
  },
  publishPolicy: async (policyId: string, document: Record<string, unknown>, requestId: string,
                         approvalToken: string): Promise<any> => {
    const response = await apiClient.post(
      `/administration/security/policies/${encodeURIComponent(policyId)}/publish`,
      { document, request_id: requestId },
      { headers: { [SECURITY_APPROVAL_HEADER]: approvalToken } });
    return response.data;
  },

  // --- Single-operator approval machinery (decision #5284) ---
  requestApproval: async (controlId: string, payload: Record<string, unknown>): Promise<any> => {
    const response = await apiClient.post('/administration/security/approvals/request',
      { control_id: controlId, payload });
    return response.data;
  },
  approveRequest: async (requestId: string, controlId: string, payload: Record<string, unknown>): Promise<any> => {
    const response = await apiClient.post(
      `/administration/security/approvals/${encodeURIComponent(requestId)}/approve`,
      { control_id: controlId, payload });
    return response.data;
  },
  revokeRequest: async (requestId: string): Promise<any> => {
    const response = await apiClient.post(
      `/administration/security/approvals/${encodeURIComponent(requestId)}/revoke`, {});
    return response.data;
  },

  // --- A14 Encryption keys (projection over lambda/cmk-api) ---
  listKeys: async (tenantId: string): Promise<any> => {
    const response = await apiClient.get('/administration/security/keys', { params: { tenant_id: tenantId } });
    return response.data;
  },
  previewKeyAction: async (tenantId: string, action: 'rotate' | 'grant' | 'revoke' | 'delete'): Promise<any> => {
    const response = await apiClient.post('/administration/security/keys/preview', { tenant_id: tenantId, action });
    return response.data;
  },
  executeKeyAction: async (tenantId: string, action: string, approvalToken: string,
                            params: Record<string, unknown> = {}): Promise<any> => {
    const response = await apiClient.post('/administration/security/keys/execute',
      { tenant_id: tenantId, action, params },
      { headers: { [SECURITY_APPROVAL_HEADER]: approvalToken } });
    return response.data;
  },

  // --- A15 Break-glass ---
  listBreakGlassGrants: async (): Promise<any> => {
    const response = await apiClient.get('/administration/security/break-glass');
    return response.data;
  },
  requestBreakGlass: async (incidentId: string, justification: string, scope: string,
                             expiryMinutes: 15 | 30 | 60): Promise<any> => {
    const response = await apiClient.post('/administration/security/break-glass/request',
      { incident_id: incidentId, justification, scope, expiry_minutes: expiryMinutes });
    return response.data;
  },
  activateBreakGlass: async (grantId: string, approvalToken: string): Promise<any> => {
    const response = await apiClient.post(
      `/administration/security/break-glass/${encodeURIComponent(grantId)}/activate`, {},
      { headers: { [SECURITY_APPROVAL_HEADER]: approvalToken } });
    return response.data;
  },
  revokeBreakGlass: async (grantId: string): Promise<any> => {
    const response = await apiClient.post(
      `/administration/security/break-glass/${encodeURIComponent(grantId)}/revoke`, {});
    return response.data;
  },
};

// =============================================================================
// Platform Settings API (A16-A19) -- Story #5484 (Launch W7) Stream 9
// =============================================================================

export interface RegistryBinding {
  entity_id: string;
  portal: string;
  environment: string;
  account: string;
  region: string;
  provider: string;
  pool: string;
  service: string;
  resource: string;
  owner: string;
  created_at: string;
  updated_at: string;
  updated_by: string;
}

export interface EnvironmentTemplateVersion {
  template_id: string;
  version: number;
  name: string;
  description: string;
  plan: Record<string, unknown>;
  created_at: string;
  created_by: string;
}

export interface FeatureFlag {
  entity_id: string;
  key: string;
  portal: string;
  environment: string;
  cohort: string;
  owner: string;
  purpose: string;
  default: boolean;
  enabled: boolean;
  expiry: string;
  removal_plan: string;
  telemetry_key: string;
  created_at: string;
  updated_at: string;
  updated_by: string;
}

export interface ConfidenceGateConfig {
  entity_type: string;
  entity_id: string;
  min_content_chars: number;
  updated_at: string | null;
  updated_by: string | null;
}

export interface ManagedWriter {
  writer_id: string;
  domain: string;
  canonical_function: string;
  adapters: string[];
  legacy_paths: string[];
  status: 'registered' | 'pending-registration';
}

export const platformSettingsApi = {
  listRegistries: async (): Promise<{ registries: RegistryBinding[] }> => {
    const response = await apiClient.get('/platform-settings', { params: { action: 'list_registries' } });
    return response.data?.data || response.data;
  },
  createRegistry: async (body: Omit<RegistryBinding, 'entity_id' | 'created_at' | 'updated_at' | 'updated_by'>): Promise<RegistryBinding> => {
    const response = await apiClient.post('/platform-settings', { action: 'create_registry', ...body });
    return response.data?.data || response.data;
  },
  listTemplates: async (): Promise<{ templates: EnvironmentTemplateVersion[] }> => {
    const response = await apiClient.get('/platform-settings', { params: { action: 'list_templates' } });
    return response.data?.data || response.data;
  },
  listTemplateVersions: async (templateId: string): Promise<{ template_id: string; versions: EnvironmentTemplateVersion[] }> => {
    const response = await apiClient.get('/platform-settings', { params: { action: 'list_templates', templateId } });
    return response.data?.data || response.data;
  },
  createTemplateVersion: async (body: { template_id: string; name: string; description?: string; plan: Record<string, unknown> }): Promise<EnvironmentTemplateVersion> => {
    const response = await apiClient.post('/platform-settings', { action: 'create_template_version', ...body });
    return response.data?.data || response.data;
  },
  listFlags: async (): Promise<{ flags: FeatureFlag[] }> => {
    const response = await apiClient.get('/platform-settings', { params: { action: 'list_flags' } });
    return response.data?.data || response.data;
  },
  createFlag: async (body: { key: string; owner: string; purpose: string; default: boolean; expiry: string; removal_plan: string; portal?: string; environment?: string; cohort?: string }): Promise<FeatureFlag> => {
    const response = await apiClient.post('/platform-settings', { action: 'create_flag', ...body });
    return response.data?.data || response.data;
  },
  updateFlag: async (key: string, enabled: boolean): Promise<FeatureFlag> => {
    const response = await apiClient.post('/platform-settings', { action: 'update_flag', key, enabled });
    return response.data?.data || response.data;
  },
  getConfidenceGateConfig: async (): Promise<ConfidenceGateConfig> => {
    const response = await apiClient.get('/platform-settings', { params: { action: 'get_confidence_gate_config' } });
    return response.data?.data || response.data;
  },
  updateConfidenceGateConfig: async (minContentChars: number): Promise<ConfidenceGateConfig> => {
    const response = await apiClient.post('/platform-settings', { action: 'update_confidence_gate_config', min_content_chars: minContentChars });
    return response.data?.data || response.data;
  },
  listWriters: async (): Promise<{ writers: ManagedWriter[]; zero_duplicate_writers: boolean }> => {
    const response = await apiClient.get('/platform-settings', { params: { action: 'list_writers' } });
    return response.data?.data || response.data;
  },
};

// =============================================================================
// Integrations API (A22) -- Story #5484 (Launch W7) Stream 10
// =============================================================================

export interface Integration {
  integration_id: string;
  type: 'webhook' | 'slack' | 'pagerduty' | 'email-relay' | 'sns-topic';
  purpose: string;
  scope: string;
  credential_ref: string;
  owner: string;
  expiry: string;
  failure_routing: 'alert-owner' | 'disable-on-failure' | 'retry-then-alert-owner';
  webhook_url?: string;
  enabled: boolean;
  delivery_test_status: 'untested' | 'pass' | 'fail' | 'manual-verification-required';
  delivery_test_detail?: string;
  plan_digest: string;
  approved_approval_id?: string;
  created_at: string;
  created_by: string;
}

export const integrationsApi = {
  list: async (): Promise<{ integrations: Integration[] }> => {
    const response = await apiClient.get('/integrations', { params: { action: 'list' } });
    return response.data?.data || response.data;
  },
  create: async (body: Pick<Integration, 'type' | 'purpose' | 'scope' | 'credential_ref' | 'owner' | 'expiry' | 'failure_routing' | 'webhook_url'>): Promise<Integration> => {
    const response = await apiClient.post('/integrations', { action: 'create_integration', ...body });
    return response.data?.data || response.data;
  },
  testDelivery: async (integrationId: string): Promise<{ integration_id: string; delivery_test_status: string; delivery_test_detail: string }> => {
    const response = await apiClient.post('/integrations', { action: 'test_delivery', integrationId });
    return response.data?.data || response.data;
  },
  requestApproval: async (integrationId: string): Promise<{ request_id: string; payload_digest: string; expires_at: number }> => {
    const response = await apiClient.post('/integrations', { action: 'request_integration_approval', integrationId });
    return response.data?.data || response.data;
  },
  approve: async (integrationId: string, approvalId: string, freshAuthTime?: number): Promise<any> => {
    const response = await apiClient.post('/integrations', { action: 'approve_integration', integrationId, approvalId, fresh_auth_time: freshAuthTime });
    return response.data?.data || response.data;
  },
  enable: async (integrationId: string, approvalId: string): Promise<Integration> => {
    const response = await apiClient.post('/integrations', { action: 'enable_integration', integrationId, approvalId });
    return response.data?.data || response.data;
  },
  revokeApproval: async (approvalId: string): Promise<any> => {
    const response = await apiClient.post('/integrations', { action: 'revoke_integration_approval', approvalId });
    return response.data?.data || response.data;
  },
};

// K08 (Task #5555): Knowledge > Exports (decision #5284 approval-gated).
export interface KnowledgeExportsOverview {
  exportable_scopes: string[];
  commit: string;
  release_enabled: boolean;
}

export interface KnowledgeExportGateResult {
  all_passed: boolean;
  gates: Record<string, { passed: boolean } & Record<string, unknown>>;
}

export interface KnowledgeExportPreview {
  scope: string;
  commit: string;
  file_count: number;
  files: string[];
}

export const knowledgeExportsApi = {
  getOverview: async (): Promise<KnowledgeExportsOverview> => {
    const response = await apiClient.get('/knowledge/exports');
    return response.data;
  },
  preview: async (scope: string): Promise<{ preview: KnowledgeExportPreview; gate_result: KnowledgeExportGateResult }> => {
    const response = await apiClient.post('/knowledge/exports', { commandType: 'preview', scope });
    return response.data;
  },
  requestApproval: async (scope: string): Promise<{ request: { request_id: string; payload_digest: string; expires_at: number }; preview: KnowledgeExportPreview }> => {
    const response = await apiClient.post('/knowledge/exports', { commandType: 'request', scope });
    return response.data;
  },
  approve: async (requestId: string, scope: string, freshAuthTime: number): Promise<{ receipt: Record<string, unknown> }> => {
    const response = await apiClient.post('/knowledge/exports', {
      commandType: 'approve', request_id: requestId, scope, fresh_auth_time: freshAuthTime,
    });
    return response.data;
  },
  revoke: async (requestId: string): Promise<{ revoked: Record<string, unknown> }> => {
    const response = await apiClient.post('/knowledge/exports', { commandType: 'revoke', request_id: requestId });
    return response.data;
  },
  release: async (requestId: string, scope: string): Promise<{ error?: string; unavailable_reason?: string; approval_verified?: boolean }> => {
    // Always refused (409 export pending approval) in this deploy -- see
    // lambda/dashboard/knowledge/exports.py's own docstring. This call
    // resolves only when the backend responds at all (the 409 body itself);
    // apiClient's interceptor still rejects on the non-2xx status, so a
    // caller sees this shape only via the error branch of its own try/catch.
    const response = await apiClient.post('/knowledge/exports', { commandType: 'release', request_id: requestId, scope });
    return response.data;
  },
};

// K09 (Task #5555): Knowledge > Contextual Help records.
export interface HelpRecordSummary {
  stable_id: string;
  updated_at: string;
}

export interface HelpRecordSections {
  on_this_page: string[];
  guided_tasks: string[];
  troubleshoot: string[];
  reference: string;
  owner: string;
}

export interface HelpRecord {
  stable_id: string;
  route: string;
  tab?: string | null;
  control?: string | null;
  title: string;
  sections: HelpRecordSections;
  published: boolean;
  archived: boolean;
  created_at: number;
  updated_at: number;
  version_id?: string;
}

export const knowledgeHelpRecordsApi = {
  list: async (): Promise<{ records: HelpRecordSummary[] }> => {
    const response = await apiClient.get('/knowledge/help-records');
    return response.data;
  },
  get: async (stableId: string): Promise<{ record: HelpRecord }> => {
    const response = await apiClient.get('/knowledge/help-records', { params: { stable_id: stableId } });
    return response.data;
  },
  test: async (stableId: string): Promise<{ resolved: boolean; valid?: boolean; missing_or_empty_sections?: string[]; mutated_state?: boolean; reason?: string }> => {
    const response = await apiClient.post('/knowledge/help-records', { commandType: 'test', stable_id: stableId });
    return response.data;
  },
  publish: async (stableId: string): Promise<{ record: HelpRecord }> => {
    const response = await apiClient.post('/knowledge/help-records', { commandType: 'publish', stable_id: stableId });
    return response.data;
  },
  recover: async (stableId: string, versionId: string): Promise<{ record: HelpRecord }> => {
    const response = await apiClient.post('/knowledge/help-records', { commandType: 'recover', stable_id: stableId, version_id: versionId });
    return response.data;
  },
};
